/**
 * VirtualStaging - AI-powered property photo staging and video reel generation
 * Allows Ultra/Enterprise users to upload property photos, generate AI-staged images,
 * and create video reels for marketing purposes.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Upload, X, Trash2, Download, Loader2, AlertCircle, Crown,
    ArrowRight, Image as ImageIcon, Video, Film, Sparkles, Home,
    Sofa, Bed, UtensilsCrossed, Bath, Monitor, TreePine,
    CheckCircle, ChevronDown, RefreshCw, Play, Pause,
    Maximize2, ZoomIn, Music, Settings, Eye, Layers, Clock,
    Mail, Package, FileArchive, AlertTriangle, XCircle,
    MessageSquare, Send, GitBranch, MoreVertical, ArrowLeft,
    History, Check, Wand2, Search, Star, SlidersHorizontal,
    LayoutPanelTop, LayoutGrid, Zap, ScanSearch, List,
    Link2, Clipboard, Building2, MapPin, Plus, Sunrise, Sunset,
    CloudSun, Moon, Cloud, CloudMoon, Camera, ExternalLink, ChevronRight, Info, Pencil,
    // Room type icons
    ToyBrick, Baby, Dumbbell, Gamepad2, Sun, DoorOpen,
    Presentation, Wine, Clapperboard, BookOpen, Footprints, Car,
    ArrowDownToLine, ArrowUpToLine, Palette, Armchair,
    // Gallery publishing
    Globe, Share2, Copy, QrCode, Facebook, Twitter, Linkedin,
    Instagram, User, Phone, AtSign, ImagePlus, Type, Layout,
    // Analytics
    BarChart3, TrendingUp, Users, MousePointerClick
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import PropTypes from 'prop-types';
import JSZip from 'jszip';
import {
    ROOM_TYPES,
    STAGING_STYLES,
    JOB_TYPES,
    ASPECT_RATIOS,
    VIDEO_STYLES,
    MUSIC_STYLES,
    CREDIT_COSTS,
    RESOLUTION_OPTIONS,
    DEFAULT_RESOLUTION,
    SESSION_STATUS,
    MESSAGE_ROLES,
    OUTDOOR_MODES,
    hasVirtualStagingAccess,
    uploadPhoto,
    fetchPhotos,
    deletePhoto,
    startStagingJob,
    startVideoJob,
    startVideoJobFromSession,
    submitBatchVideoJob,
    pollJobUntilComplete,
    downloadImage,
    downloadVideo,
    uploadPhotosForBatch,
    submitBatchStagingJob,
    fetchBatchJobs,
    fetchBatchJobStatus,
    downloadBatchResults,
    cancelBatchJob,
    // Session-based APIs
    createStagingSession,
    listStagingSessions,
    getStagingSession,
    sendRefinement,
    getVersionHistory,
    completeStagingSession,
    deleteStagingSession,
    // Video management
    listMyVideos,
    deleteVideo,
    saveVideo,
    uploadVideoFile,
    // Smart Batch APIs
    analyzePhotosForSmartBatch,
    submitSmartBatch,
    pollSmartBatchUntilComplete,
    // fetchSmartBatchStatus - no longer needed, backend sends email when complete
    // Batch Video APIs
    fetchBatchVideoStatus,
    submitProEditingRequest,
    // Property Management APIs
    createProperty,
    listProperties,
    getProperty,
    updateProperty,
    deleteProperty,
    addPhotosToProperty,
    removePhotoFromProperty,
    processProperty,
    downloadSmartBatchResults,
    // Video Management
    refreshVideoUrl,
    // Video Variations
    fetchVariationPresets,
    createVideoVariations,
    downloadVariationsZip,
    // Config
    fetchStagingConfig,
    fetchSmartBatchConfig,
    publishGallery,
    updatePropertyInfo
} from '../services/VirtualStagingService';
import {
    fetchCreditBalance,
    getCreditStatusColor
} from '../services/ChatUsageService';
import { getFeatureFlags } from '../utils/subscriptionHelpers';
import VideoAgent from './VideoAgent';
import GalleryAnalytics from './GalleryAnalytics';
import { PROPERTY_GALLERY_TEMPLATES, getPropertyGalleryTemplate } from "../config/propertyGalleryTemplates";
import PropertyFlyerBuilderModal from "./PropertyFlyerBuilderModal";
import PropertyPostcardBuilderModal from "./PropertyPostcardBuilderModal";
import SocialPostModal from "./SocialPostModal";
import MailingListPickerModal from "./DirectMail/MailingListPickerModal";
import CreateCampaignModal from "./DirectMail/CreateCampaignModal";
import flowCoverPhoto from '../../Gallery/Flow Cover Photo.png';

// Room type icon mapping
const ROOM_ICONS = {
    living_room: Sofa,
    bedroom: Bed,
    kitchen: UtensilsCrossed,
    dining_room: Sofa,
    bathroom: Bath,
    office: Monitor,
    outdoor: TreePine,
    other: Home
};

/**
 * Safe format credits - handles null/undefined
 */
const safeFormatCredits = (credits) => {
    if (credits === null || credits === undefined) return '...';
    if (credits >= 1000) {
        return `${(credits / 1000).toFixed(1)}k`;
    }
    return credits.toLocaleString();
};

/**
 * Decode over-encoded S3 URLs (e.g., %252540 → %2540 → %40 → @)
 */
const decodeS3Url = (url) => {
    if (!url || typeof url !== 'string') return url;
    let decoded = url;
    let prev = '';
    while (decoded !== prev && decoded.includes('%25')) {
        prev = decoded;
        try {
            decoded = decodeURIComponent(decoded);
        } catch (e) {
            break;
        }
    }
    return decoded;
};

/**
 * Generate a URL-friendly slug from a property address
 * @param {string} address - Property address (e.g., "1031 Miller Ave, San Jose, CA 95129")
 * @returns {string} - URL slug (e.g., "1031-miller-ave-san-jose-ca-95129")
 */
const generateSlugFromAddress = (address) => {
    if (!address) return null;
    return address
        .toLowerCase()
        .replace(/[,\.#]/g, '')           // Remove commas, periods, hash
        .replace(/[^a-z0-9\s-]/g, '')     // Remove special characters
        .replace(/\s+/g, '-')              // Replace spaces with hyphens
        .replace(/-+/g, '-')               // Collapse multiple hyphens
        .replace(/^-|-$/g, '');            // Trim leading/trailing hyphens
};

/**
 * Generate gallery URL using slug or batch ID
 * @param {string} slug - SEO-friendly slug
 * @param {string} batchId - Batch ID fallback
 * @param {string} propertyAddress - Property address for slug generation
 * @returns {string} - Full gallery URL
 */
const generateGalleryUrl = (slug, batchId, propertyAddress) => {
    const baseUrl = 'https://cloudmortgage.io/flow';
    const urlSlug = slug || generateSlugFromAddress(propertyAddress) || batchId;
    return `${baseUrl}/${urlSlug}`;
};

const DEFAULT_PRO_EDIT_REQUEST = {
    selectedPackages: ['simple'],
    instructions: '',
    turnaround: '24h',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    additionalNotes: ''
};

const DEFAULT_PRO_EDIT_PAYMENT = {
    cardNumber: '',
    expiry: '',
    cvv: '',
    cardholderName: '',
    billingZip: ''
};

const getDigitsOnly = (value) => value.replace(/\D/g, '');

const formatCardNumber = (value) => {
    const digits = getDigitsOnly(value).slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

const formatExpiry = (value) => {
    const digits = getDigitsOnly(value).slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const maskCardNumber = (value) => {
    const digits = getDigitsOnly(value);
    const last4 = digits.slice(-4);
    return last4 ? `**** **** **** ${last4}` : '';
};

const getCardBrand = (value) => {
    const digits = getDigitsOnly(value);
    if (/^4/.test(digits)) return 'Visa';
    if (/^5[1-5]/.test(digits)) return 'Mastercard';
    if (/^3[47]/.test(digits)) return 'American Express';
    if (/^6/.test(digits)) return 'Discover';
    return 'Card';
};

/**
 * Format style name from snake_case to Title Case
 * e.g., "parisian_classic" -> "Parisian Classic"
 */
const formatStyleName = (style) => {
    if (!style) return 'Unknown';
    return style
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const STYLE_CATEGORIES = [
    { id: 'all', name: 'All Styles' },
    { id: 'modern', name: 'Modern Edit' },
    { id: 'warm', name: 'Warm & Inviting' },
    { id: 'classic', name: 'Classic Luxury' },
    { id: 'bold', name: 'Statement' },
    { id: 'market', name: 'Buyer Focused' },
    { id: 'regional', name: 'Regional' }
];

const STYLE_CATEGORY_MAP = {
    modern: 'modern',
    scandinavian: 'modern',
    contemporary: 'modern',
    industrial: 'bold',
    transitional: 'classic',
    coastal: 'warm',
    farmhouse: 'warm',
    mid_century: 'bold',
    bohemian: 'bold',
    japandi: 'modern',
    traditional: 'classic',
    minimal_luxury: 'classic',
    luxury: 'classic',
    family_friendly: 'market',
    urban_professional: 'market',
    luxury_listing: 'market',
    california_casual: 'regional',
    parisian_classic: 'regional',
    tokyo_minimal: 'regional',
    kyoto_zen: 'regional',
    milan_modern: 'regional',
    copenhagen_light: 'regional',
    london_townhouse: 'regional',
    mediterranean_villa: 'regional',
    ibiza_organic: 'regional',
    moroccan_modern: 'regional'
};

const STYLE_BADGES = {
    modern: 'Popular',
    scandinavian: 'Bright',
    contemporary: 'Versatile',
    industrial: 'Bold',
    transitional: 'Balanced',
    coastal: 'Airy',
    farmhouse: 'Cozy',
    mid_century: 'Designer Pick',
    bohemian: 'Editorial',
    japandi: 'Calm',
    traditional: 'Timeless',
    minimal_luxury: 'Premium',
    luxury: 'Elegant',
    family_friendly: 'MLS Ready',
    urban_professional: 'City Ready',
    luxury_listing: 'High Impact',
    california_casual: 'Sunlit',
    parisian_classic: 'Elegant',
    tokyo_minimal: 'Precise',
    kyoto_zen: 'Serene',
    milan_modern: 'Fashion Forward',
    copenhagen_light: 'Nordic',
    london_townhouse: 'Tailored',
    mediterranean_villa: 'Resort',
    ibiza_organic: 'Organic',
    moroccan_modern: 'Textural'
};

// ==========================================
// INSPIRATION DATA
// ==========================================

const INSPIRATION_CATEGORIES = [
    { id: 'all', name: 'All', icon: '✨' },
    { id: 'resorts', name: 'Luxury Resorts', icon: '🏨' },
    { id: 'designers', name: 'Top Designers', icon: '🎨' },
    { id: 'hotels', name: 'Iconic Hotels', icon: '🏛️' },
    { id: 'brands', name: 'Design Houses', icon: '💎' },
    { id: 'destinations', name: 'Destinations', icon: '🌍' },
    { id: 'effects', name: 'Photo Effects', icon: '📸' },
    { id: 'renovation', name: 'Renovation', icon: '🔨' },
    { id: 'vibes', name: 'Visual Vibes', icon: '🎭' }
];

const INSPIRATIONS = [
    // Luxury Resorts
    {
        id: 'aman_resorts',
        name: 'Aman Resorts',
        category: 'resorts',
        description: 'Minimalist zen luxury with natural materials',
        image: 'from-amber-900 via-stone-800 to-stone-900',
        promptKeywords: 'Aman Resorts style, minimalist zen luxury, natural materials like wood stone and linen, muted earth tones, seamless indoor-outdoor living, Japanese-inspired tranquility, warm ambient lighting, bespoke furniture, uncluttered spaces with intentional emptiness',
        badge: 'Zen Luxury'
    },
    {
        id: 'four_seasons',
        name: 'Four Seasons',
        category: 'resorts',
        description: 'Timeless elegance with local character',
        image: 'from-amber-700 via-yellow-600 to-amber-800',
        promptKeywords: 'Four Seasons Hotels style, timeless elegance, sophisticated neutral palette with gold accents, plush upholstery, marble and brass details, fresh floral arrangements, luxurious textiles, warm inviting ambiance, impeccable attention to detail',
        badge: 'Timeless'
    },
    {
        id: 'one_and_only',
        name: 'One&Only',
        category: 'resorts',
        description: 'Ultra-luxury with dramatic flair',
        image: 'from-teal-800 via-emerald-700 to-cyan-900',
        promptKeywords: 'One&Only Resorts style, ultra-luxury dramatic interiors, bold jewel tones, oversized statement furniture, exotic materials, dramatic lighting, high ceilings, resort elegance with local cultural influences, glamorous yet relaxed atmosphere',
        badge: 'Ultra Luxury'
    },
    {
        id: 'six_senses',
        name: 'Six Senses',
        category: 'resorts',
        description: 'Sustainable luxury with organic textures',
        image: 'from-green-800 via-emerald-700 to-teal-800',
        promptKeywords: 'Six Senses style, sustainable luxury, organic natural textures, reclaimed wood, handwoven textiles, earthy color palette, biophilic design, living plants, wellness-inspired spaces, eco-conscious elegance',
        badge: 'Eco Luxury'
    },
    {
        id: 'rosewood',
        name: 'Rosewood Hotels',
        category: 'resorts',
        description: 'Residential-style refined luxury',
        image: 'from-rose-900 via-pink-800 to-rose-950',
        promptKeywords: 'Rosewood Hotels style, residential luxury feel, rich jewel tones with rosewood accents, curated art collections, layered textures, intimate sophisticated spaces, warm wood tones, velvet and silk fabrics, cultured elegance',
        badge: 'Refined'
    },
    {
        id: 'como',
        name: 'COMO Hotels',
        category: 'resorts',
        description: 'Asian-inspired contemporary minimalism',
        image: 'from-slate-700 via-zinc-600 to-slate-800',
        promptKeywords: 'COMO Hotels style, Asian-inspired minimalism, clean contemporary lines, neutral monochromatic palette, teak and terrazzo, floor-to-ceiling windows, meditation-inspired calm, sleek modern furniture, uncluttered refined spaces',
        badge: 'Serene'
    },

    // Top Interior Designers
    {
        id: 'kelly_wearstler',
        name: 'Kelly Wearstler',
        category: 'designers',
        description: 'Bold maximalist with sculptural elements',
        image: 'from-purple-800 via-fuchsia-700 to-violet-900',
        promptKeywords: 'Kelly Wearstler style, bold maximalist interiors, sculptural furniture, mixed materials and textures, vintage meets contemporary, statement lighting, rich saturated colors, artistic layering, gallery-worthy spaces, glamorous Hollywood Regency influence',
        badge: 'Maximalist'
    },
    {
        id: 'axel_vervoordt',
        name: 'Axel Vervoordt',
        category: 'designers',
        description: 'Wabi-sabi aesthetic with antiques',
        image: 'from-stone-600 via-zinc-500 to-stone-700',
        promptKeywords: 'Axel Vervoordt style, wabi-sabi aesthetic, aged patinas and weathered surfaces, antique furniture, muted earthy tones, natural light emphasis, textured plaster walls, timeless serenity, perfectly imperfect beauty, museum-quality art placement',
        badge: 'Wabi-Sabi'
    },
    {
        id: 'vincent_van_duysen',
        name: 'Vincent Van Duysen',
        category: 'designers',
        description: 'Belgian minimalism with warmth',
        image: 'from-neutral-600 via-stone-500 to-neutral-700',
        promptKeywords: 'Vincent Van Duysen style, Belgian minimalism, warm tactile materials, oak and limestone, monastic simplicity, architectural purity, soft neutral palette, natural light, clean lines with organic touches, sophisticated restraint',
        badge: 'Belgian'
    },
    {
        id: 'pierre_yovanovitch',
        name: 'Pierre Yovanovitch',
        category: 'designers',
        description: 'Playful French sophistication',
        image: 'from-blue-700 via-indigo-600 to-blue-800',
        promptKeywords: 'Pierre Yovanovitch style, playful French sophistication, curved organic furniture, bold color blocking, custom bespoke pieces, whimsical yet refined, contemporary with classic French bones, sculptural forms, joyful luxury',
        badge: 'Playful'
    },
    {
        id: 'john_pawson',
        name: 'John Pawson',
        category: 'designers',
        description: 'Pure architectural minimalism',
        image: 'from-gray-300 via-white to-gray-400',
        promptKeywords: 'John Pawson style, pure architectural minimalism, monastic simplicity, all-white spaces, perfect proportions, natural light as material, hidden storage, seamless surfaces, spiritual calm, absence of ornament',
        badge: 'Pure'
    },
    {
        id: 'studio_ko',
        name: 'Studio KO',
        category: 'designers',
        description: 'Moroccan-inspired desert modernism',
        image: 'from-orange-800 via-amber-700 to-orange-900',
        promptKeywords: 'Studio KO style, Moroccan-inspired modernism, desert palette, tadelakt walls, handcrafted details, indoor-outdoor living, earth tones, geometric patterns, Yves Saint Laurent Marrakech influence, warm minimalism',
        badge: 'Desert'
    },
    {
        id: 'ken_fulk',
        name: 'Ken Fulk',
        category: 'designers',
        description: 'Theatrical storytelling luxury',
        image: 'from-emerald-800 via-green-700 to-emerald-900',
        promptKeywords: 'Ken Fulk style, theatrical storytelling interiors, rich saturated colors, vintage glamour, collected antiques, pattern mixing, sumptuous textures, maximalist layering, old-world meets rock-and-roll, dramatic personality',
        badge: 'Theatrical'
    },

    // Iconic Hotels
    {
        id: 'claridges',
        name: "Claridge's London",
        category: 'hotels',
        description: 'Art Deco grandeur with British elegance',
        image: 'from-slate-800 via-zinc-700 to-slate-900',
        promptKeywords: "Claridge's London style, Art Deco grandeur, black and white marble, chrome and mirror accents, geometric patterns, British elegance, statement chandeliers, plush seating, timeless glamour, impeccable service atmosphere",
        badge: 'Art Deco'
    },
    {
        id: 'ritz_paris',
        name: 'Ritz Paris',
        category: 'hotels',
        description: 'French palatial luxury',
        image: 'from-yellow-600 via-amber-500 to-yellow-700',
        promptKeywords: 'Ritz Paris style, French palatial luxury, Louis XVI furniture, gilded details, silk damask fabrics, crystal chandeliers, Aubusson rugs, powder blue and gold palette, Coco Chanel elegance, ultimate Parisian grandeur',
        badge: 'Palatial'
    },
    {
        id: 'gritti_palace',
        name: 'Gritti Palace Venice',
        category: 'hotels',
        description: 'Venetian palazzo opulence',
        image: 'from-red-900 via-rose-800 to-red-950',
        promptKeywords: 'Gritti Palace style, Venetian palazzo opulence, Murano glass chandeliers, silk brocade, terrazzo floors, antique Venetian furniture, rich reds and golds, canal views, Renaissance grandeur, romantic Italian luxury',
        badge: 'Venetian'
    },
    {
        id: 'ace_hotel',
        name: 'Ace Hotel',
        category: 'hotels',
        description: 'Industrial-chic creative spaces',
        image: 'from-zinc-700 via-neutral-600 to-zinc-800',
        promptKeywords: 'Ace Hotel style, industrial-chic interiors, exposed brick and concrete, vintage furniture, local art installations, creative eclectic mix, warm Edison lighting, vinyl records aesthetic, hipster luxury, community-minded design',
        badge: 'Creative'
    },
    {
        id: 'soho_house',
        name: 'Soho House',
        category: 'hotels',
        description: 'British members club aesthetic',
        image: 'from-green-900 via-emerald-800 to-green-950',
        promptKeywords: 'Soho House style, British members club aesthetic, eclectic vintage furniture, rich jewel tones forest green, worn leather, reclaimed materials, bohemian luxury, art-filled walls, cozy yet sophisticated, creative community vibe',
        badge: 'Members Club'
    },

    // Design Houses / Brands
    {
        id: 'rh',
        name: 'RH (Restoration Hardware)',
        category: 'brands',
        description: 'Grand scale neutral luxury',
        image: 'from-stone-500 via-neutral-400 to-stone-600',
        promptKeywords: 'RH Restoration Hardware style, grand scale furniture, oversized comfortable pieces, neutral greige palette, Belgian linen, weathered oak, crystal orb chandeliers, gallery-inspired spaces, modern luxury with vintage soul',
        badge: 'Grand'
    },
    {
        id: 'boffi',
        name: 'Boffi',
        category: 'brands',
        description: 'Italian precision minimalism',
        image: 'from-gray-600 via-slate-500 to-gray-700',
        promptKeywords: 'Boffi style, Italian precision minimalism, sleek kitchen and bath, handleless cabinetry, stone and stainless steel, architectural purity, museum-quality finishes, seamless integration, ultra-modern Italian design',
        badge: 'Precision'
    },
    {
        id: 'minotti',
        name: 'Minotti',
        category: 'brands',
        description: 'Italian contemporary comfort',
        image: 'from-neutral-700 via-stone-600 to-neutral-800',
        promptKeywords: 'Minotti style, Italian contemporary luxury, deep comfortable sofas, rich leather and cashmere, warm neutrals with accent colors, sophisticated modern living, impeccable tailoring, understated elegance',
        badge: 'Italian'
    },
    {
        id: 'fendi_casa',
        name: 'Fendi Casa',
        category: 'brands',
        description: 'Fashion-forward Italian glamour',
        image: 'from-amber-800 via-yellow-700 to-amber-900',
        promptKeywords: 'Fendi Casa style, fashion-forward Italian glamour, bold FF logo patterns, rich materials, fur and leather accents, black and gold palette, runway-inspired interiors, luxury fashion house aesthetic',
        badge: 'Fashion'
    },
    {
        id: 'armani_casa',
        name: 'Armani Casa',
        category: 'brands',
        description: 'Sleek sophisticated restraint',
        image: 'from-zinc-800 via-gray-700 to-zinc-900',
        promptKeywords: 'Armani Casa style, sleek sophisticated restraint, muted color palette, lacquered finishes, clean geometric lines, subtle luxury, Japanese-Italian fusion, impeccable proportions, quiet confidence',
        badge: 'Sleek'
    },

    // Destinations
    {
        id: 'hamptons',
        name: 'The Hamptons',
        category: 'destinations',
        description: 'Coastal American elegance',
        image: 'from-blue-400 via-sky-300 to-blue-500',
        promptKeywords: 'Hamptons style, coastal American elegance, white and navy palette, shiplap walls, natural light, wicker and rattan, blue hydrangeas, beach house sophistication, preppy classic, breezy linen fabrics',
        badge: 'Coastal'
    },
    {
        id: 'aspen',
        name: 'Aspen Lodge',
        category: 'destinations',
        description: 'Mountain luxury retreat',
        image: 'from-amber-900 via-orange-800 to-amber-950',
        promptKeywords: 'Aspen lodge style, mountain luxury retreat, reclaimed timber beams, stone fireplaces, cowhide and sheepskin, warm cabin aesthetic, après-ski elegance, rustic refined, cozy mountain sanctuary',
        badge: 'Mountain'
    },
    {
        id: 'palm_beach',
        name: 'Palm Beach',
        category: 'destinations',
        description: 'Tropical resort glamour',
        image: 'from-pink-500 via-rose-400 to-pink-600',
        promptKeywords: 'Palm Beach style, tropical resort glamour, pink and green palette, bamboo furniture, bold prints, lacquered finishes, Dorothy Draper influence, glamorous Floridian, sunny optimistic interiors',
        badge: 'Tropical'
    },
    {
        id: 'santorini',
        name: 'Santorini',
        category: 'destinations',
        description: 'Greek island whitewashed beauty',
        image: 'from-blue-600 via-cyan-500 to-blue-700',
        promptKeywords: 'Santorini style, Greek island aesthetic, whitewashed walls, blue accents, curved organic architecture, Mediterranean light, minimalist cave-like spaces, terracotta touches, Aegean sea views, simple elegant beauty',
        badge: 'Mediterranean'
    },
    {
        id: 'bali',
        name: 'Bali Villa',
        category: 'destinations',
        description: 'Tropical zen paradise',
        image: 'from-green-700 via-emerald-600 to-green-800',
        promptKeywords: 'Bali villa style, tropical zen paradise, teak wood furniture, thatched roofs, open-air living, infinity pools, lush greenery, Balinese carved details, natural stone, spiritual resort atmosphere',
        badge: 'Tropical Zen'
    },
    {
        id: 'amalfi',
        name: 'Amalfi Coast',
        category: 'destinations',
        description: 'Italian coastal romance',
        image: 'from-yellow-500 via-amber-400 to-yellow-600',
        promptKeywords: 'Amalfi Coast style, Italian coastal romance, hand-painted tiles, lemon motifs, terracotta and white, wrought iron details, Mediterranean terraces, dolce vita lifestyle, romantic Italian summer aesthetic',
        badge: 'Italian Coast'
    },

    // Additional Designers (Popular/Mainstream)
    {
        id: 'joanna_gaines',
        name: 'Joanna Gaines',
        category: 'designers',
        description: 'Modern Farmhouse with rustic comfort',
        image: 'from-amber-600 via-yellow-500 to-amber-700',
        promptKeywords: 'Joanna Gaines Magnolia style, modern farmhouse, shiplap walls, reclaimed wood beams, neutral color palette, cozy textiles, black metal accents, rustic-meets-modern, family-friendly comfort, greenery in galvanized buckets',
        badge: 'Farmhouse'
    },
    {
        id: 'studio_mcgee',
        name: 'Studio McGee',
        category: 'designers',
        description: 'Light, airy, and sophisticated transitional style',
        image: 'from-slate-400 via-gray-300 to-slate-500',
        promptKeywords: 'Studio McGee style, Shea McGee design, light and airy transitional, neutral tones with blue accents, layered textiles, woven baskets, brass hardware, modern traditional mix, clean lines, timeless sophistication',
        badge: 'Transitional'
    },
    {
        id: 'emily_henderson',
        name: 'Emily Henderson',
        category: 'designers',
        description: 'Stylized, eclectic, and approachable family-friendly design',
        image: 'from-teal-500 via-cyan-400 to-teal-600',
        promptKeywords: 'Emily Henderson style, stylized and approachable, mixed-and-matched furniture, mid-century influence with modern touches, layered textures, vintage accents, functional family-friendly design, curated but lived-in feel',
        badge: 'Eclectic'
    },
    {
        id: 'bobby_berk',
        name: 'Bobby Berk',
        category: 'designers',
        description: 'Clean, modern, and highly functional urban luxury',
        image: 'from-zinc-700 via-gray-600 to-zinc-800',
        promptKeywords: 'Bobby Berk style, clean and modern functionality, minimalist but warm, geometric shapes, neutral palette with bold black accents, organized and efficient spaces, urban contemporary luxury, sleek and sophisticated',
        badge: 'Urban Modern'
    },
    {
        id: 'nate_berkus',
        name: 'Nate Berkus',
        category: 'designers',
        description: 'Travel-inspired sophisticated eclecticism',
        image: 'from-amber-700 via-orange-600 to-amber-800',
        promptKeywords: 'Nate Berkus style, travel-inspired eclectic, sophisticated neutral base, vintage finds, textured fabrics, layered rugs, black and white photography, mixed wood tones, masculine but refined, globally-sourced decor',
        badge: 'Collected'
    },
    {
        id: 'justina_blakeney',
        name: 'Justina Blakeney',
        category: 'designers',
        description: 'Vibrant boho-maximalist with Jungalow vibes',
        image: 'from-green-600 via-emerald-500 to-green-700',
        promptKeywords: 'Justina Blakeney Jungalow style, boho-maximalist, vibrant colors, abundant indoor plants, rattan and bamboo furniture, globally-inspired patterns, layered textiles, playful textures, organic shapes, jungle-inspired sanctuary',
        badge: 'Jungalow'
    },

    // Additional Brands (new)
    {
        id: 'west_elm',
        name: 'West Elm',
        category: 'brands',
        description: 'Mid-century modern with urban contemporary flair',
        image: 'from-amber-500 via-yellow-400 to-amber-600',
        promptKeywords: 'West Elm style, mid-century modern influence, approachable contemporary, walnut wood finishes, geometric patterns, pops of color (mustard, teal, terracotta), slim-legged furniture, brass accents, urban loft feel',
        badge: 'Mid-Century'
    },
    {
        id: 'cb2',
        name: 'CB2',
        category: 'brands',
        description: 'Modern, edgy, and urban sophisticated style',
        image: 'from-slate-800 via-zinc-700 to-slate-900',
        promptKeywords: 'CB2 style, modern and edgy, urban sophisticated, high-contrast materials (marble, black metal, velvet), sleek minimalist silhouettes, bold statement pieces, contemporary city apartment vibe',
        badge: 'Urban Edge'
    },
    {
        id: 'pottery_barn',
        name: 'Pottery Barn',
        category: 'brands',
        description: 'Classic American traditional and cozy comfort',
        image: 'from-amber-600 via-orange-500 to-amber-700',
        promptKeywords: 'Pottery Barn style, classic American traditional, cozy and family-oriented, slipcovered sofas, warm wood tones, rustic textures, timeless decor, seasonal accents, comfortable and inviting, coastal influence',
        badge: 'Classic'
    },
    {
        id: 'serena_and_lily',
        name: 'Serena & Lily',
        category: 'brands',
        description: 'Upscale coastal and breezy California luxury',
        image: 'from-blue-400 via-cyan-300 to-blue-500',
        promptKeywords: 'Serena and Lily style, upscale coastal, breezy California vibe, rattan and wicker textures, blue and white palette, nautical patterns, light and airy, casual luxury, woven wallpapers, seaside sophisticated',
        badge: 'Coastal'
    },
    {
        id: 'arhaus',
        name: 'Arhaus',
        category: 'brands',
        description: 'Artisanal, handcrafted, and globally-inspired luxury',
        image: 'from-stone-600 via-amber-500 to-stone-700',
        promptKeywords: 'Arhaus style, natural and artisanal, globally-inspired handcrafted furniture, rich textures, reclaimed wood, neutral earth tones, sophisticated rustic-meets-luxury, heirloom-quality feel',
        badge: 'Artisanal'
    },

    // Photography Effects
    {
        id: 'architectural_pro',
        name: 'Architectural Pro',
        category: 'effects',
        description: 'High-end magazine quality photography finish',
        image: 'from-gray-600 via-slate-500 to-gray-700',
        promptKeywords: 'Professional architectural photography style, wide-angle lens, corrected verticals, high dynamic range (HDR), perfectly even exposure, crisp sharp edges, minimal distortion, magazine-quality finish',
        badge: 'Pro Shot'
    },
    {
        id: 'bright_and_airy',
        name: 'Bright & Airy',
        category: 'effects',
        description: 'High-key lighting with a fresh, ethereal mood',
        image: 'from-white via-gray-100 to-white',
        promptKeywords: 'Bright and airy photography, high-key lighting, soft diffused shadows, overexposed window light, glowing highlights, clean white tones, ethereal atmosphere, fresh and uplifting mood',
        badge: 'Light'
    },
    {
        id: 'moody_cinematic',
        name: 'Moody Cinematic',
        category: 'effects',
        description: 'Dramatic low-key lighting for an intimate feel',
        image: 'from-slate-900 via-zinc-800 to-slate-950',
        promptKeywords: 'Moody cinematic lighting, low-key atmosphere, dramatic shadows, warm localized glow from lamps, deep textures, rich color grading, intimate feel, mystery and sophistication',
        badge: 'Cinematic'
    },
    {
        id: 'wes_anderson',
        name: 'Wes Anderson',
        category: 'effects',
        description: 'Whimsical, symmetrical, and pastel-toned aesthetic',
        image: 'from-pink-300 via-rose-200 to-pink-400',
        promptKeywords: 'Wes Anderson style, perfectly symmetrical composition, pastel color palette (pink, mint, yellow), quirky and meticulous detail, flat frontal camera angle, nostalgic and whimsical atmosphere, highly stylized',
        badge: 'Symmetry'
    },
    {
        id: 'golden_hour',
        name: 'Golden Hour',
        category: 'effects',
        description: 'Warm, magical sunlight for an inviting glow',
        image: 'from-amber-400 via-orange-300 to-amber-500',
        promptKeywords: 'Golden hour lighting, warm low-angle sunlight, long soft shadows, amber glow, sun-drenched surfaces, cozy and inviting atmosphere, magical afternoon light',
        badge: 'Warm Glow'
    },
    {
        id: 'vintage_film',
        name: 'Vintage Film',
        category: 'effects',
        description: 'Nostalgic analog film aesthetic with subtle grain',
        image: 'from-amber-700 via-yellow-600 to-amber-800',
        promptKeywords: 'Vintage 35mm film photography, subtle grain, warm nostalgic tones, slight light leaks, soft highlights, timeless and authentic feel, analog aesthetic',
        badge: 'Film'
    },

    // Virtual Renovation
    {
        id: 'white_oak_floors',
        name: 'White Oak Floors',
        category: 'renovation',
        description: 'Trendy wide-plank white oak flooring update',
        image: 'from-amber-200 via-yellow-100 to-amber-300',
        promptKeywords: 'Modern virtual renovation, replace flooring with wide-plank white oak hardwood, light natural wood finish, matte texture, clean and contemporary foundation, seamless installation',
        badge: 'Flooring'
    },
    {
        id: 'limewash_walls',
        name: 'Limewash Walls',
        category: 'renovation',
        description: 'Soft, organic textured limewash wall finish',
        image: 'from-stone-300 via-zinc-200 to-stone-400',
        promptKeywords: 'Architectural wall finish, apply soft textured limewash paint, subtle mottled appearance, matte chalky finish, neutral stone or sand tones, organic and tactile atmosphere',
        badge: 'Walls'
    },
    {
        id: 'exposed_brick',
        name: 'Exposed Brick',
        category: 'renovation',
        description: 'Authentic exposed brick feature wall',
        image: 'from-red-700 via-orange-600 to-red-800',
        promptKeywords: 'Industrial virtual renovation, create an exposed red brick feature wall, authentic weathered texture, rustic charm, high-contrast architectural detail, urban loft character',
        badge: 'Industrial'
    },

    // Visual Vibes
    {
        id: 'scandi_industrial',
        name: 'Scandi-Industrial',
        category: 'vibes',
        description: 'Fusion of clean Scandi and raw Industrial',
        image: 'from-slate-500 via-zinc-400 to-slate-600',
        promptKeywords: 'Scandi-Industrial fusion style, clean minimalist silhouettes mixed with raw industrial materials, light wood and black steel, cozy hygge textiles and concrete surfaces, functional but edgy',
        badge: 'Fusion'
    },
    {
        id: 'desert_modern',
        name: 'Desert Modern',
        category: 'vibes',
        description: 'Warm, organic, and Southwestern-inspired modernism',
        image: 'from-orange-600 via-amber-500 to-orange-700',
        promptKeywords: 'Desert Modern aesthetic, organic Southwestern influence, warm earthy tones (terracotta, clay, ochre), natural wood and cactus silk, desert-inspired greenery, sun-baked textures, serene and grounded',
        badge: 'Southwest'
    },
    {
        id: 'biophilic_design',
        name: 'Biophilic Sanctuary',
        category: 'vibes',
        description: 'Nature-integrated restorative design',
        image: 'from-green-500 via-emerald-400 to-green-600',
        promptKeywords: 'Biophilic interior design, abundant indoor plants and living walls, natural light optimization, organic shapes, sustainable materials, wood and stone elements, deep connection to nature, restorative atmosphere',
        badge: 'Nature'
    }
];

const ROOM_SUGGESTED_STYLES = {
    living_room: ['milan_modern', 'parisian_classic', 'minimal_luxury'],
    bedroom: ['kyoto_zen', 'japandi', 'copenhagen_light'],
    kitchen: ['contemporary', 'tokyo_minimal', 'california_casual'],
    dining_room: ['parisian_classic', 'luxury_listing', 'milan_modern'],
    bathroom: ['kyoto_zen', 'modern', 'mediterranean_villa'],
    office: ['urban_professional', 'tokyo_minimal', 'london_townhouse'],
    outdoor: ['mediterranean_villa', 'ibiza_organic', 'california_casual'],
    other: ['milan_modern', 'parisian_classic', 'luxury_listing']
};

const QUICK_REFINEMENTS = [
    'Make it brighter and more airy',
    'Add greenery and natural textures',
    'Use warmer wood tones',
    'Create a luxury listing look',
    'Stage it for a young family',
    'Keep the furniture minimal'
];

/**
 * UpgradePrompt - Shown when user doesn't have access
 */
const UpgradePrompt = ({ onUpgrade }) => (
    <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 p-8 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="max-w-md text-center relative z-10">
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-24 h-24 rounded-3xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-emerald-500/30"
            >
                <Home className="w-12 h-12 text-white" />
            </motion.div>

            <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-3xl font-bold text-white mb-4"
            >
                Virtual Staging
            </motion.h2>

            <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-slate-300 mb-8 text-lg"
            >
                Transform empty properties into beautifully staged homes with AI-powered virtual staging.
            </motion.p>

            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 text-left border border-white/10"
            >
                <p className="text-sm font-medium text-white/90 mb-4">Unlock staging powers:</p>
                <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        AI Virtual Furniture Staging
                    </li>
                    <li className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        Multiple Design Styles
                    </li>
                    <li className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        Before/After Comparisons
                    </li>
                    <li className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        Video Reel Generation
                    </li>
                </ul>
            </motion.div>

            <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                onClick={onUpgrade}
                className="w-full px-8 py-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white font-semibold rounded-2xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all flex items-center justify-center gap-3 text-lg"
            >
                <Crown className="w-5 h-5" />
                Upgrade to Ultra
                <ArrowRight className="w-5 h-5" />
            </motion.button>

            <p className="text-xs text-slate-400 mt-4">
                Available on Ultra and Enterprise plans
            </p>
        </div>
    </div>
);

UpgradePrompt.propTypes = {
    onUpgrade: PropTypes.func.isRequired
};

/**
 * PhotoCard - Individual photo thumbnail with actions
 */
const PhotoCard = ({ photo, onSelect, onDelete, isSelected, isDeleting }) => {
    const RoomIcon = ROOM_ICONS[photo.roomType] || Home;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            whileHover={{ scale: 1.02 }}
            className={`relative group aspect-[4/3] rounded-xl overflow-hidden cursor-pointer transition-all ${
                isSelected
                    ? 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
                    : 'hover:shadow-lg'
            }`}
            onClick={() => onSelect(photo)}
        >
            <img
                src={photo.thumbnailUrl || photo.originalUrl}
                alt={photo.name || 'Property photo'}
                className="w-full h-full object-cover"
            />

            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

            {/* Room type badge */}
            <div className="absolute top-2 left-2 px-2 py-1 rounded-lg bg-black/50 backdrop-blur-sm text-white text-xs flex items-center gap-1">
                <RoomIcon className="w-3 h-3" />
                <span>{ROOM_TYPES.find(r => r.id === photo.roomType)?.name || 'Room'}</span>
            </div>

            {/* Status badge if staged */}
            {photo.stagedUrl && (
                <div className="absolute top-2 right-2 px-2 py-1 rounded-lg bg-emerald-500/90 text-white text-xs flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Staged
                </div>
            )}

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent px-3 py-2">
                <p className="truncate text-xs font-medium text-white">
                    {photo.name || photo.customRoomName || ROOM_TYPES.find((room) => room.id === photo.roomType)?.name || 'Property photo'}
                </p>
            </div>

            {/* Delete button */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(photo.id);
                }}
                disabled={isDeleting}
                className="absolute bottom-2 right-2 p-1.5 rounded-lg bg-red-500/80 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
            >
                {isDeleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Trash2 className="w-4 h-4" />
                )}
            </button>
        </motion.div>
    );
};

PhotoCard.propTypes = {
    photo: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    isSelected: PropTypes.bool,
    isDeleting: PropTypes.bool
};

/**
 * PhotoUploader - Drag/drop upload zone with room type selection
 */
/**
 * RoomTypeSelector - Reusable room type selector with custom room name for "Other"
 */
const RoomTypeSelector = ({ selectedRoomType, onRoomTypeChange, customRoomName, onCustomRoomNameChange, disabled = false, compact = false }) => {
    return (
        <div className="space-y-3">
            <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-2'} gap-2`}>
                {ROOM_TYPES.map((room) => {
                    const Icon = ROOM_ICONS[room.id] || Home;
                    const isSelected = selectedRoomType === room.id;
                    const suggestedStyles = ROOM_SUGGESTED_STYLES[room.id] || [];
                    return (
                        <button
                            key={room.id}
                            onClick={() => onRoomTypeChange(room.id)}
                            disabled={disabled}
                            className={`rounded-2xl border text-left transition-all disabled:opacity-50 ${
                                compact ? 'p-3' : 'p-3.5'
                            } ${
                                isSelected
                                    ? 'border-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30'
                                    : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200 hover:border-emerald-300 dark:hover:border-emerald-600 hover:bg-emerald-50/70 dark:hover:bg-emerald-900/10'
                            }`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className={`rounded-xl ${isSelected ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700'} p-2`}>
                                    <Icon className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${isSelected ? 'text-white' : 'text-emerald-500'}`} />
                                </div>
                                {!compact && suggestedStyles.length > 0 && (
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        isSelected
                                            ? 'bg-white/20 text-white'
                                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                    }`}>
                                        AI-ready
                                    </span>
                                )}
                            </div>
                            <div className="mt-3">
                                <div className={`font-medium ${compact ? 'text-sm' : 'text-sm'}`}>{room.name}</div>
                                {!compact && suggestedStyles.length > 0 && (
                                    <div className={`mt-1 text-xs ${isSelected ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                                        Best with {suggestedStyles.slice(0, 2).map((styleId) => {
                                            const style = STAGING_STYLES.find((item) => item.id === styleId);
                                            return style?.name;
                                        }).filter(Boolean).join(' + ')}
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Custom room name input for "Other" */}
            {selectedRoomType === 'other' && (
                <div>
                    <input
                        type="text"
                        value={customRoomName || ''}
                        onChange={(e) => onCustomRoomNameChange?.(e.target.value)}
                        placeholder="Enter room name (e.g., Nursery, Game Room, Gym)"
                        disabled={disabled}
                        maxLength={50}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                    />
                    <p className="text-xs text-slate-400 mt-1">This will be used in the AI prompt</p>
                </div>
            )}
        </div>
    );
};

RoomTypeSelector.propTypes = {
    selectedRoomType: PropTypes.string.isRequired,
    onRoomTypeChange: PropTypes.func.isRequired,
    customRoomName: PropTypes.string,
    onCustomRoomNameChange: PropTypes.func,
    disabled: PropTypes.bool,
    compact: PropTypes.bool
};

const PhotoUploader = ({ onUpload, isUploading, uploadProgress, selectedRoomType, onRoomTypeChange, customRoomName, onCustomRoomNameChange }) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setIsDragActive(true);
        } else if (e.type === 'dragleave') {
            setIsDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            onUpload(e.dataTransfer.files[0], selectedRoomType, customRoomName);
        }
    }, [onUpload, selectedRoomType, customRoomName]);

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            onUpload(e.target.files[0], selectedRoomType, customRoomName);
        }
    };

    return (
        <div className="space-y-4">
            {/* Room type selector */}
            <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Room Type
                </label>
                <RoomTypeSelector
                    selectedRoomType={selectedRoomType}
                    onRoomTypeChange={onRoomTypeChange}
                    customRoomName={customRoomName}
                    onCustomRoomNameChange={onCustomRoomNameChange}
                    disabled={isUploading}
                    compact={false}
                />
            </div>

            {/* Upload zone */}
            <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    isDragActive
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400 dark:hover:border-emerald-600 bg-slate-50 dark:bg-slate-800/30'
                } ${isUploading ? 'pointer-events-none' : ''}`}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                />

                {isUploading ? (
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Uploading... {Math.round(uploadProgress)}%
                        </p>
                        <div className="w-48 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${uploadProgress}%` }}
                                className="h-full bg-emerald-500 rounded-full"
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <Upload className={`w-10 h-10 mx-auto mb-3 ${
                            isDragActive ? 'text-emerald-500' : 'text-slate-400'
                        }`} />
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            {isDragActive ? 'Drop photo here' : 'Click or drag photo to upload'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            JPEG, PNG, or WebP (max 20MB)
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

PhotoUploader.propTypes = {
    onUpload: PropTypes.func.isRequired,
    isUploading: PropTypes.bool,
    uploadProgress: PropTypes.number,
    selectedRoomType: PropTypes.string.isRequired,
    onRoomTypeChange: PropTypes.func.isRequired,
    customRoomName: PropTypes.string,
    onCustomRoomNameChange: PropTypes.func
};

const StyleSelector = ({ selectedStyle, onSelectStyle, selectedInspiration, onSelectInspiration, disabled, recommendedStyleIds = [], inspirations = null, inspirationCategories = null }) => {
    const [activeTab, setActiveTab] = useState('styles'); // 'styles' or 'inspiration'
    const [activeCategory, setActiveCategory] = useState('all');
    const [activeInspirationCategory, setActiveInspirationCategory] = useState('all');

    // Use dynamic data if available, fallback to static constants
    const allInspirations = inspirations || INSPIRATIONS;
    const allCategories = inspirationCategories || INSPIRATION_CATEGORIES;

    const visibleStyles = STAGING_STYLES.filter((style) => {
        if (activeCategory === 'all') return true;
        return STYLE_CATEGORY_MAP[style.id] === activeCategory;
    });

    const visibleInspirations = allInspirations.filter((insp) => {
        if (activeInspirationCategory === 'all') return true;
        return insp.category === activeInspirationCategory;
    });

    const handleSelectStyle = (styleId) => {
        onSelectStyle(styleId);
        if (onSelectInspiration) onSelectInspiration(null); // Clear inspiration when selecting style
    };

    const handleSelectInspiration = (inspiration) => {
        if (onSelectInspiration) onSelectInspiration(inspiration);
        onSelectStyle(null); // Clear style when selecting inspiration
    };

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-3">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Design Style
                </label>
                <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    Curated looks
                </div>
            </div>

            {/* Main Tabs: Styles vs Inspiration */}
            <div className="flex gap-2 mb-4 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
                <button
                    type="button"
                    onClick={() => setActiveTab('styles')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        activeTab === 'styles'
                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                    <Palette className="w-4 h-4" />
                    Styles
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('inspiration')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        activeTab === 'inspiration'
                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                    <Sparkles className="w-4 h-4" />
                    Inspiration
                </button>
            </div>

            {activeTab === 'styles' ? (
                <>
                    {/* Style Categories */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {STYLE_CATEGORIES.map((category) => (
                            <button
                                key={category.id}
                                type="button"
                                onClick={() => setActiveCategory(category.id)}
                                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                                    activeCategory === category.id
                                        ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                                }`}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>

                    {/* Styles Grid */}
                    <div className="grid grid-cols-1 gap-3">
                        {visibleStyles.map((style) => {
                            const isSelected = selectedStyle === style.id && !selectedInspiration;
                            const isRecommended = recommendedStyleIds.includes(style.id);

                            return (
                                <button
                                    key={style.id}
                                    onClick={() => handleSelectStyle(style.id)}
                                    disabled={disabled}
                                    className={`relative overflow-hidden rounded-2xl border text-left transition-all ${
                                        isSelected
                                            ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600'
                                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className={`h-24 w-full bg-gradient-to-br ${style.color}`} />
                                    <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
                                        <span className="rounded-full bg-black/35 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm">
                                            {STYLE_BADGES[style.id] || 'Featured'}
                                        </span>
                                        {isRecommended && (
                                            <span className="rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-slate-900">
                                                Recommended
                                            </span>
                                        )}
                                    </div>
                                    <div className="bg-white/95 p-4 dark:bg-slate-900/95">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-semibold text-slate-800 dark:text-white">{style.name}</p>
                                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{style.description}</p>
                                            </div>
                                            {isSelected && (
                                                <div className="rounded-full bg-emerald-500 p-1 text-white">
                                                    <Check className="w-3.5 h-3.5" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            ) : (
                <>
                    {/* Inspiration Description */}
                    <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                        <div className="flex items-start gap-2">
                            <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-purple-900 dark:text-purple-100">Get Inspired</p>
                                <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
                                    Stage your room in the style of world-renowned resorts, hotels, and top interior designers
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Inspiration Categories */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {allCategories.map((category) => (
                            <button
                                key={category.id}
                                type="button"
                                onClick={() => setActiveInspirationCategory(category.id)}
                                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 ${
                                    activeInspirationCategory === category.id
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                                }`}
                            >
                                <span>{category.icon}</span>
                                {category.name}
                            </button>
                        ))}
                    </div>

                    {/* Inspirations Grid */}
                    <div className="grid grid-cols-1 gap-3">
                        {visibleInspirations.map((inspiration) => {
                            const isSelected = selectedInspiration?.id === inspiration.id;

                            return (
                                <button
                                    key={inspiration.id}
                                    onClick={() => handleSelectInspiration(inspiration)}
                                    disabled={disabled}
                                    className={`relative overflow-hidden rounded-2xl border text-left transition-all ${
                                        isSelected
                                            ? 'border-purple-500 ring-2 ring-purple-500/30'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600'
                                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className={`h-24 w-full bg-gradient-to-br ${inspiration.image || 'from-slate-400 to-slate-600'}`} />
                                    <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3">
                                        <span className="rounded-full bg-purple-600/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm">
                                            {inspiration.badge || inspiration.category}
                                        </span>
                                        <span className="rounded-full bg-black/40 px-2 py-1 text-[10px] font-medium text-white backdrop-blur-sm">
                                            {allCategories.find(c => c.id === inspiration.category)?.icon}
                                        </span>
                                    </div>
                                    <div className="bg-white/95 p-4 dark:bg-slate-900/95">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-semibold text-slate-800 dark:text-white">{inspiration.name}</p>
                                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{inspiration.description}</p>
                                            </div>
                                            {isSelected && (
                                                <div className="rounded-full bg-purple-500 p-1 text-white">
                                                    <Check className="w-3.5 h-3.5" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

StyleSelector.propTypes = {
    selectedStyle: PropTypes.string,
    onSelectStyle: PropTypes.func.isRequired,
    selectedInspiration: PropTypes.object,
    onSelectInspiration: PropTypes.func,
    disabled: PropTypes.bool,
    recommendedStyleIds: PropTypes.array
};

/**
 * StagingOptionsPanel - Job type radio buttons and style dropdown
 */
const StagingOptionsPanel = ({
    selectedJobType,
    setSelectedJobType,
    selectedStyle,
    setSelectedStyle,
    selectedInspiration,
    setSelectedInspiration,
    disabled,
    recommendedStyleIds,
    inspirations = null,
    inspirationCategories = null
}) => (
    <div className="space-y-6">
        {/* Job Type Selection */}
        <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Staging Type
            </label>
            <div className="space-y-2">
                {JOB_TYPES.map((job) => (
                    <label
                        key={job.id}
                        className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedJobType === job.id
                                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                                : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <input
                            type="radio"
                            name="jobType"
                            value={job.id}
                            checked={selectedJobType === job.id}
                            onChange={() => setSelectedJobType(job.id)}
                            disabled={disabled}
                            className="sr-only"
                        />
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-slate-800 dark:text-white">{job.name}</span>
                                <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                                    {job.credits} credits
                                </span>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{job.description}</p>
                        </div>
                        <div className={`ml-4 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedJobType === job.id
                                ? 'border-emerald-500 bg-emerald-500'
                                : 'border-slate-300 dark:border-slate-600'
                        }`}>
                            {selectedJobType === job.id && (
                                <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                        </div>
                    </label>
                ))}
            </div>
        </div>

        {/* Style Selection (only for staging jobs) */}
        {selectedJobType !== 'empty_room' && (
            <div>
                <StyleSelector
                    selectedStyle={selectedStyle}
                    onSelectStyle={setSelectedStyle}
                    selectedInspiration={selectedInspiration}
                    onSelectInspiration={setSelectedInspiration}
                    disabled={disabled}
                    recommendedStyleIds={recommendedStyleIds}
                    inspirations={inspirations}
                    inspirationCategories={inspirationCategories}
                />
            </div>
        )}
    </div>
);

StagingOptionsPanel.propTypes = {
    selectedJobType: PropTypes.string.isRequired,
    setSelectedJobType: PropTypes.func.isRequired,
    selectedStyle: PropTypes.string,
    setSelectedStyle: PropTypes.func.isRequired,
    selectedInspiration: PropTypes.object,
    setSelectedInspiration: PropTypes.func,
    disabled: PropTypes.bool,
    recommendedStyleIds: PropTypes.array
};

/**
 * GenerationProgress - Instagram-style smooth progress indicator
 */
const GenerationProgress = ({ progress, message, type = 'staging', thumbnail }) => {
    // Progress steps for visual feedback
    const steps = type === 'video'
        ? ['Preparing', 'Rendering', 'Encoding', 'Finalizing']
        : ['Analyzing', 'Designing', 'Rendering', 'Enhancing'];

    const currentStep = Math.min(Math.floor(progress / 25), 3);

    return (
        <motion.div
            key="generation-progress"
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-50"
        >
            <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden min-w-[340px]">
                {/* Shimmer overlay for processing feel */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <motion.div
                        animate={{ x: ['0%', '200%'] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12"
                    />
                </div>

                {/* Top progress bar - Instagram style with smooth animations */}
                <div className="h-1.5 bg-slate-200 dark:bg-slate-700">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{
                            type: "tween",
                            duration: progress === 100 ? 0.3 : 0.15,
                            ease: progress === 100 ? "easeOut" : "linear"
                        }}
                        className="h-full bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 relative"
                    >
                        {/* Animated glow on progress bar - pulses faster as progress increases */}
                        <motion.div
                            animate={{ opacity: [0.4, 1, 0.4], x: ['-100%', '0%', '-100%'] }}
                            transition={{ duration: progress > 80 ? 0.8 : 1.2, repeat: Infinity }}
                            className="absolute right-0 top-0 w-12 h-full bg-gradient-to-l from-white/70 to-transparent"
                        />
                    </motion.div>
                </div>

                <div className="p-5">
                    <div className="flex items-center gap-4">
                        {/* Thumbnail or icon with pulse */}
                        <div className="relative flex-shrink-0">
                            {thumbnail ? (
                                <motion.div
                                    animate={{ scale: [1, 1.02, 1] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                    className="w-14 h-14 rounded-xl overflow-hidden ring-2 ring-emerald-500/30"
                                >
                                    <img src={thumbnail} alt="" className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                                </motion.div>
                            ) : (
                                <motion.div
                                    animate={{ scale: [1, 1.05, 1] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                    className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/25"
                                >
                                    {type === 'video' ? (
                                        <Video className="w-7 h-7 text-white" />
                                    ) : (
                                        <ImageIcon className="w-7 h-7 text-white" />
                                    )}
                                </motion.div>
                            )}
                            {/* Spinning sparkle indicator */}
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg"
                            >
                                <Sparkles className="w-3 h-3 text-white" />
                            </motion.div>
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="font-semibold text-slate-800 dark:text-white truncate">
                                    {type === 'video' ? 'Creating Video' : 'AI Staging'}
                                </h3>
                                <motion.span
                                    key={progress}
                                    initial={{ scale: 1.2, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tabular-nums"
                                >
                                    {progress}%
                                </motion.span>
                            </div>
                            <motion.p
                                key={message}
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-sm text-slate-500 dark:text-slate-400 truncate"
                            >
                                {message || 'Processing...'}
                            </motion.p>
                        </div>
                    </div>

                    {/* Step indicators with smooth animated progress */}
                    <div className="flex items-center gap-2 mt-4">
                        {steps.map((step, idx) => {
                            // Calculate partial progress within current step (0-100%)
                            const stepStart = idx * 25;
                            const stepProgress = idx < currentStep
                                ? 100
                                : idx === currentStep
                                    ? Math.min(100, Math.max(0, ((progress - stepStart) / 25) * 100))
                                    : 0;
                            const isActive = idx === currentStep;
                            const isComplete = idx < currentStep;

                            return (
                                <div key={step} className="flex-1">
                                    <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-600 overflow-hidden relative">
                                        {/* Background shimmer for active step */}
                                        {isActive && (
                                            <motion.div
                                                animate={{ x: ['-100%', '200%'] }}
                                                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                                                className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-300/30 to-transparent"
                                            />
                                        )}
                                        {/* Main progress bar with smooth fill */}
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${stepProgress}%` }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 50,
                                                damping: 15,
                                                duration: 0.8
                                            }}
                                            className={`h-full rounded-full relative ${
                                                isComplete
                                                    ? 'bg-emerald-500'
                                                    : 'bg-gradient-to-r from-emerald-500 to-teal-400'
                                            }`}
                                        >
                                            {/* Glowing tip on active progress */}
                                            {isActive && stepProgress > 0 && stepProgress < 100 && (
                                                <motion.div
                                                    animate={{ opacity: [0.5, 1, 0.5] }}
                                                    transition={{ duration: 0.8, repeat: Infinity }}
                                                    className="absolute right-0 top-0 w-3 h-full bg-gradient-to-l from-white/80 to-transparent rounded-full"
                                                />
                                            )}
                                        </motion.div>
                                    </div>
                                    <p className={`text-[10px] mt-1.5 text-center font-medium transition-colors ${
                                        isActive
                                            ? 'text-emerald-500 dark:text-emerald-400'
                                            : isComplete
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-slate-400 dark:text-slate-500'
                                    }`}>
                                        {step}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

GenerationProgress.propTypes = {
    progress: PropTypes.number.isRequired,
    message: PropTypes.string,
    type: PropTypes.oneOf(['staging', 'video']),
    thumbnail: PropTypes.string
};

/**
 * BeforeAfterSlider - Draggable comparison slider for before/after images
 */
const BeforeAfterSlider = ({ beforeUrl, afterUrl, className = '' }) => {
    const containerRef = useRef(null);
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);

    const handleMove = useCallback((clientX) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
        setSliderPosition(percentage);
    }, []);

    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;
        handleMove(e.clientX);
    }, [isDragging, handleMove]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!isDragging) return;
        handleMove(e.touches[0].clientX);
    }, [isDragging, handleMove]);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('touchend', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

    return (
        <div
            ref={containerRef}
            className={`relative overflow-hidden rounded-2xl shadow-2xl cursor-ew-resize select-none ${className}`}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
        >
            {/* After (Staged) Image - Full size background */}
            <img
                src={afterUrl}
                alt="Staged"
                className="w-full h-full object-contain block"
                draggable={false}
            />

            {/* Before (Original) Image - Clipped */}
            <div
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
                <img
                    src={beforeUrl}
                    alt="Original"
                    className="w-full h-full object-contain"
                    draggable={false}
                />
            </div>

            {/* Slider Handle */}
            <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
                {/* Handle Circle */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-emerald-500">
                    <div className="flex items-center gap-0.5">
                        <ChevronDown className="w-4 h-4 text-emerald-600 -rotate-90" />
                        <ChevronDown className="w-4 h-4 text-emerald-600 rotate-90" />
                    </div>
                </div>
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/60 text-white text-sm rounded-lg backdrop-blur-sm">
                Before
            </div>
            <div className="absolute top-4 right-4 px-3 py-1.5 bg-emerald-500/90 text-white text-sm rounded-lg backdrop-blur-sm flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" />
                After
            </div>
        </div>
    );
};

BeforeAfterSlider.propTypes = {
    beforeUrl: PropTypes.string.isRequired,
    afterUrl: PropTypes.string.isRequired,
    className: PropTypes.string
};

/**
 * ResolutionSelector - Radio group for selecting output resolution
 */
const ResolutionSelector = ({ value, onChange, userCredits, baseCredits }) => {
    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Output Resolution
            </label>
            <div className="space-y-2">
                {RESOLUTION_OPTIONS.map((option) => {
                    const totalCost = baseCredits + option.extraCredits;
                    const canAfford = userCredits >= totalCost;
                    const isSelected = value === option.value;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => canAfford && onChange(option.value)}
                            disabled={!canAfford}
                            className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                                isSelected
                                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                                    : canAfford
                                        ? 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800'
                                        : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 opacity-50 cursor-not-allowed'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                    isSelected
                                        ? 'border-emerald-500 bg-emerald-500'
                                        : 'border-slate-300 dark:border-slate-600'
                                }`}>
                                    {isSelected && (
                                        <div className="w-2 h-2 rounded-full bg-white" />
                                    )}
                                </div>
                                <div className="text-left">
                                    <div className="flex items-center gap-2">
                                        <span className={`font-medium ${
                                            isSelected ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-800 dark:text-white'
                                        }`}>
                                            {option.label}
                                        </span>
                                        {option.badge && (
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                                option.badge === 'Recommended'
                                                    ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                                                    : 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400'
                                            }`}>
                                                {option.badge}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                        {option.description}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                {option.extraCredits === 0 ? (
                                    <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                        Included
                                    </span>
                                ) : (
                                    <span className={`text-sm font-medium ${
                                        isSelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-300'
                                    }`}>
                                        +{option.extraCredits} cr
                                    </span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

ResolutionSelector.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    userCredits: PropTypes.number,
    baseCredits: PropTypes.number
};

/**
 * CreditSummary - Shows total credit cost breakdown
 */
const CreditSummary = ({ baseCredits, resolution, label = 'Base staging' }) => {
    const resOption = RESOLUTION_OPTIONS.find(r => r.value === resolution);
    const extraCredits = resOption?.extraCredits || 0;
    const total = baseCredits + extraCredits;

    return (
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">{label}</span>
                <span className="text-slate-800 dark:text-white">{baseCredits} cr</span>
            </div>
            {extraCredits > 0 && (
                <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">
                        Resolution upgrade ({resolution.toUpperCase()})
                    </span>
                    <span className="text-emerald-600 dark:text-emerald-400">+{extraCredits} cr</span>
                </div>
            )}
            <div className="flex justify-between text-sm font-semibold pt-2 border-t border-slate-200 dark:border-slate-700">
                <span className="text-slate-800 dark:text-white">Total</span>
                <span className="text-emerald-600 dark:text-emerald-400">{total} credits</span>
            </div>
        </div>
    );
};

CreditSummary.propTypes = {
    baseCredits: PropTypes.number.isRequired,
    resolution: PropTypes.string.isRequired,
    label: PropTypes.string
};

/**
 * StagingWizard - Step-by-step staging configuration
 */
const StagingWizard = ({
    selectedPhoto,
    selectedStyle,
    setSelectedStyle,
    selectedInspiration,
    setSelectedInspiration,
    selectedResolution,
    setSelectedResolution,
    customPrompt,
    setCustomPrompt,
    credits,
    isProcessing,
    onRoomTypeChange,
    onStartStaging,
    recommendedStyleIds,
    inspirations = null, // Dynamic inspirations from API (falls back to INSPIRATIONS constant)
    inspirationCategories = null, // Dynamic categories from API (falls back to INSPIRATION_CATEGORIES constant)
    // Indoor/Outdoor support
    photoCategory = 'indoor',
    setPhotoCategory,
    outdoorMode = 'twilight',
    setOutdoorMode
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [styleTab, setStyleTab] = useState('styles'); // 'styles' or 'inspiration'
    const [inspirationCategory, setInspirationCategory] = useState('all');
    const roomType = selectedPhoto?.roomType || 'living_room';
    const customRoomName = selectedPhoto?.customRoomName || '';

    // All resolutions unlocked for all staging users
    const availableResolutions = RESOLUTION_OPTIONS;

    const totalCredits = CREDIT_COSTS.full_staging + (RESOLUTION_OPTIONS.find(r => r.value === selectedResolution)?.extraCredits || 0);
    const canAfford = credits >= totalCredits;

    const handleRoomSelect = (newType) => {
        onRoomTypeChange(newType, customRoomName);
        setCurrentStep(2);
    };

    const handleStyleSelect = (styleId) => {
        setSelectedStyle(styleId);
        if (setSelectedInspiration) setSelectedInspiration(null); // Clear inspiration when selecting style
        setCurrentStep(3);
    };

    const handleInspirationSelect = (inspiration) => {
        if (setSelectedInspiration) setSelectedInspiration(inspiration);
        setSelectedStyle(null); // Clear style when selecting inspiration
        setCurrentStep(3);
    };

    const handleResolutionSelect = (res) => {
        setSelectedResolution(res);
        setCurrentStep(4);
    };

    // Use dynamic inspirations if available, fallback to static INSPIRATIONS
    const allInspirations = inspirations || INSPIRATIONS;
    const allCategories = inspirationCategories || INSPIRATION_CATEGORIES;

    const visibleInspirations = allInspirations.filter((insp) => {
        if (inspirationCategory === 'all') return true;
        return insp.category === inspirationCategory;
    });

    const getRoomIcon = (roomId) => {
        const icons = {
            // Primary rooms
            living_room: <Sofa className="w-5 h-5" />,
            bedroom: <Bed className="w-5 h-5" />,
            kitchen: <UtensilsCrossed className="w-5 h-5" />,
            dining_room: <Armchair className="w-5 h-5" />,
            bathroom: <Bath className="w-5 h-5" />,
            office: <Monitor className="w-5 h-5" />,
            outdoor: <TreePine className="w-5 h-5" />,
            // Secondary rooms
            kids_room: <ToyBrick className="w-5 h-5" />,
            nursery: <Baby className="w-5 h-5" />,
            open_living_dining: <LayoutPanelTop className="w-5 h-5" />,
            home_gym: <Dumbbell className="w-5 h-5" />,
            game_room: <Gamepad2 className="w-5 h-5" />,
            laundry_room: <Sparkles className="w-5 h-5" />,
            studio: <Palette className="w-5 h-5" />,
            sunroom: <Sun className="w-5 h-5" />,
            hallway: <DoorOpen className="w-5 h-5" />,
            // Specialty rooms
            conference_room: <Presentation className="w-5 h-5" />,
            spa_room: <Sparkles className="w-5 h-5" />,
            wine_cellar: <Wine className="w-5 h-5" />,
            home_theater: <Clapperboard className="w-5 h-5" />,
            library: <BookOpen className="w-5 h-5" />,
            mudroom: <Footprints className="w-5 h-5" />,
            garage: <Car className="w-5 h-5" />,
            basement: <ArrowDownToLine className="w-5 h-5" />,
            attic: <ArrowUpToLine className="w-5 h-5" />,
            // Other
            other: <Home className="w-5 h-5" />
        };
        return icons[roomId] || <Home className="w-5 h-5" />;
    };

    const selectedRoomName = ROOM_TYPES.find(r => r.id === roomType)?.name || 'Room';
    const selectedStyleName = selectedInspiration
        ? `${selectedInspiration.name} (Inspired)`
        : STAGING_STYLES.find(s => s.id === selectedStyle)?.name || 'Style';
    const selectedResName = RESOLUTION_OPTIONS.find(r => r.value === selectedResolution)?.label || 'Resolution';

    const isOutdoor = photoCategory === 'outdoor';
    const selectedOutdoorModeName = OUTDOOR_MODES.find(m => m.value === outdoorMode)?.label || 'Twilight';

    return (
        <div className="space-y-4">
            {/* Indoor/Outdoor Toggle */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Enhancement Type</p>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            if (setPhotoCategory) setPhotoCategory('indoor');
                            setCurrentStep(1);
                        }}
                        disabled={isProcessing}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                            !isOutdoor
                                ? 'bg-emerald-500 text-white shadow-md'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                    >
                        <Home className="w-4 h-4" />
                        Indoor Staging
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (setPhotoCategory) setPhotoCategory('outdoor');
                            setCurrentStep(1);
                        }}
                        disabled={isProcessing}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                            isOutdoor
                                ? 'bg-amber-500 text-white shadow-md'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                    >
                        <TreePine className="w-4 h-4" />
                        Outdoor / Sky
                    </button>
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {isOutdoor
                        ? 'Enhance exterior photos with dramatic sky replacements'
                        : 'Transform empty rooms with AI-powered virtual staging'
                    }
                </p>
            </div>

            {/* Step 1: Room Type (Indoor) or Sky Enhancement (Outdoor) */}
            <div className={`rounded-2xl border transition-all ${currentStep === 1 ? (isOutdoor ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-900/10' : 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10') : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}>
                <button
                    onClick={() => setCurrentStep(1)}
                    className="w-full flex items-center justify-between p-4"
                >
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep > 1 ? (isOutdoor ? 'bg-amber-500' : 'bg-emerald-500') + ' text-white' : currentStep === 1 ? (isOutdoor ? 'bg-amber-500' : 'bg-emerald-500') + ' text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                            {currentStep > 1 ? <Check className="w-4 h-4" /> : '1'}
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-medium text-slate-800 dark:text-white">
                                {isOutdoor ? 'Sky Enhancement' : 'Room Type'}
                            </p>
                            {currentStep > 1 && (
                                <p className={`text-xs mt-0.5 ${isOutdoor ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                    {isOutdoor ? selectedOutdoorModeName : selectedRoomName}
                                </p>
                            )}
                        </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${currentStep === 1 ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                    {currentStep === 1 && (
                        <motion.div
                            key="step-1-content"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            {isOutdoor ? (
                                /* Outdoor Mode Selector in Step 1 */
                                <div className="px-4 pb-4 space-y-2">
                                    {OUTDOOR_MODES.map((mode) => {
                                        const isSelected = outdoorMode === mode.value;
                                        return (
                                            <button
                                                key={mode.value}
                                                type="button"
                                                onClick={() => {
                                                    if (setOutdoorMode) setOutdoorMode(mode.value);
                                                    setCurrentStep(3); // Skip Step 2 (Design Style) for outdoor - go to Resolution
                                                }}
                                                disabled={isProcessing}
                                                className={`w-full flex items-center justify-between p-3.5 rounded-xl text-left transition-all ${
                                                    isSelected
                                                        ? 'ring-2 ring-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-md'
                                                        : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                        mode.value === 'twilight' ? 'bg-gradient-to-br from-indigo-600 to-purple-800' :
                                                        mode.value === 'sunset' ? 'bg-gradient-to-br from-orange-500 to-red-600' :
                                                        mode.value === 'sunrise' ? 'bg-gradient-to-br from-pink-400 to-orange-400' :
                                                        mode.value === 'blue_hour' ? 'bg-gradient-to-br from-blue-600 to-indigo-800' :
                                                        mode.value === 'dramatic' ? 'bg-gradient-to-br from-slate-600 to-slate-900' :
                                                        'bg-gradient-to-br from-amber-400 to-orange-500'
                                                    }`}>
                                                        {mode.value === 'twilight' && <Moon className="w-5 h-5 text-white" />}
                                                        {mode.value === 'sunset' && <Sunset className="w-5 h-5 text-white" />}
                                                        {mode.value === 'sunrise' && <Sunrise className="w-5 h-5 text-white" />}
                                                        {mode.value === 'blue_hour' && <CloudMoon className="w-5 h-5 text-white" />}
                                                        {mode.value === 'dramatic' && <CloudSun className="w-5 h-5 text-white" />}
                                                        {mode.value === 'golden_hour' && <Sun className="w-5 h-5 text-white" />}
                                                    </div>
                                                    <div>
                                                        <p className={`text-sm font-medium ${isSelected ? 'text-amber-700 dark:text-amber-300' : 'text-slate-700 dark:text-slate-300'}`}>
                                                            {mode.label}
                                                        </p>
                                                        {mode.description && (
                                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{mode.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {isSelected && <Check className="w-5 h-5 text-amber-500" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                /* Indoor Room Type Selector */
                                <div className="px-4 pb-4 grid grid-cols-2 gap-2.5">
                                    {ROOM_TYPES.map((room) => (
                                        <button
                                            key={room.id}
                                            onClick={() => handleRoomSelect(room.id)}
                                            disabled={isProcessing}
                                            className={`flex items-center gap-2.5 p-3.5 rounded-xl text-left transition-all ${
                                                roomType === room.id
                                                    ? 'bg-emerald-500 text-white shadow-md'
                                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                            }`}
                                        >
                                            {getRoomIcon(room.id)}
                                            <span className="text-sm font-medium">{room.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Step 2: Design Style (Indoor Only - Outdoor skips this step) */}
            {!isOutdoor && (
            <div className={`rounded-2xl border transition-all ${currentStep === 2 ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'} ${currentStep < 2 ? 'opacity-50' : ''}`}>
                <button
                    onClick={() => currentStep >= 2 && setCurrentStep(2)}
                    disabled={currentStep < 2}
                    className="w-full flex items-center justify-between p-4"
                >
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep > 2 ? 'bg-emerald-500 text-white' : currentStep === 2 ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                            {currentStep > 2 ? <Check className="w-4 h-4" /> : '2'}
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-medium text-slate-800 dark:text-white">Design Style</p>
                            {currentStep > 2 && (
                                <p className="text-xs mt-0.5 text-emerald-600 dark:text-emerald-400">{selectedStyleName}</p>
                            )}
                        </div>
                    </div>
                    {currentStep >= 2 && <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${currentStep === 2 ? 'rotate-180' : ''}`} />}
                </button>
                <AnimatePresence>
                    {currentStep === 2 && (
                        <motion.div
                            key="step-2-content"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="px-4 pb-4">
                                {/* Indoor Style Selector */}
                                {/* Tabs: Styles vs Inspiration */}
                                <div className="flex gap-1 mb-4 p-1 bg-slate-100 dark:bg-slate-700 rounded-xl">
                                    <button
                                        type="button"
                                        onClick={() => setStyleTab('styles')}
                                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                            styleTab === 'styles'
                                                ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                                                : 'text-slate-600 dark:text-slate-400'
                                        }`}
                                    >
                                        <Palette className="w-3.5 h-3.5" />
                                        Styles
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setStyleTab('inspiration')}
                                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                            styleTab === 'inspiration'
                                                ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                                                : 'text-slate-600 dark:text-slate-400'
                                        }`}
                                    >
                                        <Sparkles className="w-3.5 h-3.5" />
                                        Inspiration
                                    </button>
                                </div>

                                {styleTab === 'styles' ? (
                                    <div className="grid grid-cols-2 gap-2.5">
                                        {STAGING_STYLES.map((style) => {
                                            const isRecommended = recommendedStyleIds?.includes(style.id);
                                            const isSelected = selectedStyle === style.id && !selectedInspiration;
                                            return (
                                                <button
                                                    key={style.id}
                                                    onClick={() => handleStyleSelect(style.id)}
                                                    disabled={isProcessing}
                                                    className={`relative p-3.5 rounded-xl text-left transition-all ${
                                                        isSelected
                                                            ? 'ring-2 ring-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 shadow-md'
                                                            : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                                                    }`}
                                                >
                                                    <div className={`w-full h-5 rounded-lg bg-gradient-to-r ${style.color} mb-2.5`} />
                                                    <p className="text-xs font-medium text-slate-800 dark:text-white">{style.name}</p>
                                                    {isRecommended && (
                                                        <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] rounded-full">
                                                            <Star className="w-2.5 h-2.5 inline" />
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div>
                                        {/* Inspiration info banner */}
                                        <div className="mb-3 p-2.5 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                            <div className="flex items-start gap-2">
                                                <Sparkles className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                                                <p className="text-[11px] text-purple-700 dark:text-purple-300">
                                                    Stage in the style of world-renowned resorts & top designers
                                                </p>
                                            </div>
                                        </div>

                                        {/* Inspiration Categories */}
                                        <div className="flex flex-wrap gap-1.5 mb-3">
                                            {allCategories.map((cat) => (
                                                <button
                                                    key={cat.id}
                                                    type="button"
                                                    onClick={() => setInspirationCategory(cat.id)}
                                                    className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-all flex items-center gap-1 ${
                                                        inspirationCategory === cat.id
                                                            ? 'bg-purple-600 text-white'
                                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                                    }`}
                                                >
                                                    <span>{cat.icon}</span>
                                                    {cat.name}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Inspirations Grid */}
                                        <div className="grid grid-cols-2 gap-2.5 max-h-64 overflow-y-auto">
                                            {visibleInspirations.map((insp) => {
                                                const isSelected = selectedInspiration?.id === insp.id;
                                                return (
                                                    <button
                                                        key={insp.id}
                                                        onClick={() => handleInspirationSelect(insp)}
                                                        disabled={isProcessing}
                                                        className={`relative p-3 rounded-xl text-left transition-all ${
                                                            isSelected
                                                                ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                                                                : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                                                        }`}
                                                    >
                                                        <div className={`w-full h-8 rounded-lg bg-gradient-to-r ${insp.image} mb-2`} />
                                                        <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{insp.name}</p>
                                                        <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{insp.description}</p>
                                                        <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-purple-500/80 text-white text-[9px] rounded-full">
                                                            {insp.badge}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            )}

            {/* Step 3: Resolution (shows as Step 2 for Outdoor since Design Style is skipped) */}
            <div className={`rounded-2xl border transition-all ${currentStep === 3 ? (isOutdoor ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-900/10' : 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10') : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'} ${currentStep < 3 ? 'opacity-50' : ''}`}>
                <button
                    onClick={() => currentStep >= 3 && setCurrentStep(3)}
                    disabled={currentStep < 3}
                    className="w-full flex items-center justify-between p-4"
                >
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep > 3 ? (isOutdoor ? 'bg-amber-500' : 'bg-emerald-500') + ' text-white' : currentStep === 3 ? (isOutdoor ? 'bg-amber-500' : 'bg-emerald-500') + ' text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                            {currentStep > 3 ? <Check className="w-4 h-4" /> : (isOutdoor ? '2' : '3')}
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-medium text-slate-800 dark:text-white">Resolution</p>
                            {currentStep > 3 && <p className={`text-xs mt-0.5 ${isOutdoor ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{selectedResName}</p>}
                        </div>
                    </div>
                    {currentStep >= 3 && <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${currentStep === 3 ? 'rotate-180' : ''}`} />}
                </button>
                <AnimatePresence>
                    {currentStep === 3 && (
                        <motion.div
                            key="step-3-content"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="px-4 pb-4 space-y-2.5">
                                {availableResolutions.map((option) => {
                                    const optionTotal = CREDIT_COSTS.full_staging + option.extraCredits;
                                    const canAffordOption = credits >= optionTotal;
                                    return (
                                        <button
                                            key={option.value}
                                            onClick={() => canAffordOption && handleResolutionSelect(option.value)}
                                            disabled={!canAffordOption || isProcessing}
                                            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                                                selectedResolution === option.value
                                                    ? 'bg-emerald-500 text-white shadow-md'
                                                    : canAffordOption
                                                        ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                                        : 'bg-slate-100 dark:bg-slate-700 text-slate-400 opacity-50 cursor-not-allowed'
                                            }`}
                                        >
                                            <div className="text-left">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium">{option.label}</span>
                                                    {option.badge && (
                                                        <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${
                                                            selectedResolution === option.value
                                                                ? 'bg-white/20 text-white'
                                                                : option.badge === 'Recommended'
                                                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                                                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400'
                                                        }`}>
                                                            {option.badge}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className={`text-xs mt-1 ${selectedResolution === option.value ? 'text-white/70' : 'text-slate-500'}`}>
                                                    {option.description}
                                                </p>
                                            </div>
                                            <span className={`text-sm font-medium ${selectedResolution === option.value ? 'text-white' : ''}`}>
                                                {option.extraCredits === 0 ? 'Included' : `+${option.extraCredits} cr`}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Step 4: Review & Start */}
            <AnimatePresence>
                {currentStep >= 4 && (
                    <motion.div
                        key="step-4-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="space-y-4"
                    >
                        {/* Custom Instructions (Optional) */}
                        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-3">
                                Custom Instructions (Optional)
                            </label>
                            <textarea
                                value={customPrompt}
                                onChange={(e) => setCustomPrompt(e.target.value)}
                                disabled={isProcessing}
                                placeholder="e.g., add plants, use warm tones..."
                                maxLength={500}
                                rows={2}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                            />
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {['Add plants', 'Warm lighting', 'Minimalist', 'Cozy feel'].map((prompt) => (
                                    <button
                                        key={prompt}
                                        onClick={() => setCustomPrompt(prompt)}
                                        className="px-2.5 py-1 text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full hover:bg-emerald-100 hover:text-emerald-700 transition-colors"
                                    >
                                        {prompt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Credit Summary */}
                        <div className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 p-4 space-y-3">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-600 dark:text-slate-400">Base staging</span>
                                <span className="text-slate-800 dark:text-white">{CREDIT_COSTS.full_staging} cr</span>
                            </div>
                            {RESOLUTION_OPTIONS.find(r => r.value === selectedResolution)?.extraCredits > 0 && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-600 dark:text-slate-400">Resolution ({selectedResolution.toUpperCase()})</span>
                                    <span className="text-emerald-600">+{RESOLUTION_OPTIONS.find(r => r.value === selectedResolution)?.extraCredits} cr</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm font-semibold pt-3 border-t border-slate-200 dark:border-slate-700">
                                <span className="text-slate-800 dark:text-white">Total</span>
                                <span className="text-emerald-600">{totalCredits} credits</span>
                            </div>
                        </div>

                        {/* Start Button */}
                        <button
                            onClick={onStartStaging}
                            disabled={isProcessing || !canAfford}
                            className="w-full px-5 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-4 h-4" />
                                    Start Staging
                                </>
                            )}
                        </button>
                        <p className="text-xs text-slate-400 text-center mt-2">
                            You can refine the result in the session
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

StagingWizard.propTypes = {
    selectedPhoto: PropTypes.object,
    selectedStyle: PropTypes.string,
    setSelectedStyle: PropTypes.func.isRequired,
    selectedInspiration: PropTypes.object,
    setSelectedInspiration: PropTypes.func,
    selectedResolution: PropTypes.string.isRequired,
    setSelectedResolution: PropTypes.func.isRequired,
    customPrompt: PropTypes.string,
    setCustomPrompt: PropTypes.func.isRequired,
    credits: PropTypes.number,
    isProcessing: PropTypes.bool,
    onRoomTypeChange: PropTypes.func.isRequired,
    onStartStaging: PropTypes.func.isRequired,
    recommendedStyleIds: PropTypes.array,
    // Indoor/Outdoor support
    photoCategory: PropTypes.oneOf(['indoor', 'outdoor']),
    setPhotoCategory: PropTypes.func,
    outdoorMode: PropTypes.string,
    setOutdoorMode: PropTypes.func
};

/**
 * ResultPreviewModal - Before/after comparison with download (Full screen)
 */
const ResultPreviewModal = ({ photo, onClose, onDownload }) => {
    if (!photo) return null;

    return (
        <motion.div
            key="result-preview-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex flex-col"
            onClick={onClose}
        >
            {/* Header with close button */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-4">
                <h2 className="text-white text-lg font-semibold">Before / After Comparison</h2>
                <button
                    onClick={onClose}
                    className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>

            {/* Main image container - takes most of the space */}
            <div
                className="flex-1 flex items-center justify-center px-6 pb-4 min-h-0"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="relative w-full h-full max-w-[95vw] max-h-[75vh] bg-slate-900 rounded-2xl overflow-hidden">
                    {photo.stagedUrl ? (
                        <BeforeAfterSlider
                            beforeUrl={photo.originalUrl}
                            afterUrl={photo.stagedUrl}
                            className="w-full h-full"
                        />
                    ) : (
                        <img
                            src={photo.originalUrl}
                            alt="Original photo"
                            className="w-full h-full object-contain"
                        />
                    )}
                </div>
            </div>

            {/* Footer with hint and buttons */}
            <div
                className="flex-shrink-0 px-6 pb-6"
                onClick={(e) => e.stopPropagation()}
            >
                {photo.stagedUrl && (
                    <p className="text-center text-sm text-slate-400 mb-4">
                        Drag the slider left/right to compare before and after
                    </p>
                )}

                {/* Action buttons */}
                <div className="flex justify-center gap-4">
                    {photo.stagedUrl && (
                        <button
                            onClick={() => onDownload(photo.stagedUrlHd || photo.stagedUrl, `staged-${photo.id}${photo.stagedUrlHd ? '-HD' : ''}.${photo.stagedUrlHd ? 'png' : 'jpg'}`)}
                            className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-all shadow-lg shadow-emerald-500/30"
                        >
                            <Download className="w-5 h-5" />
                            Download Staged
                            {photo.stagedUrlHd && <span className="ml-1 px-1.5 py-0.5 bg-amber-400 text-amber-900 text-xs font-bold rounded">HD</span>}
                        </button>
                    )}
                    <button
                        onClick={() => onDownload(photo.originalUrl, `original-${photo.id}.jpg`)}
                        className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-all"
                    >
                        <Download className="w-5 h-5" />
                        Download Original
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

ResultPreviewModal.propTypes = {
    photo: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired
};

/**
 * VideoOptionsPanel - Aspect ratio, style, music selection
 */
const VideoOptionsPanel = ({
    aspectRatio,
    setAspectRatio,
    videoStyle,
    setVideoStyle,
    musicStyle,
    setMusicStyle,
    disabled = false
}) => {
    const [styleTab, setStyleTab] = useState('cinematic'); // 'cinematic' or '3d'

    const filteredStyles = VIDEO_STYLES.filter(style => 
        styleTab === '3d' ? style.is3D : !style.is3D
    );

    return (
        <div className="space-y-3">
            {/* Aspect Ratio */}
            <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Aspect Ratio
                </label>
                <div className="flex gap-1.5">
                    {ASPECT_RATIOS.map((ratio) => (
                        <button
                            key={ratio.id}
                            onClick={() => setAspectRatio(ratio.id)}
                            disabled={disabled}
                            className={`flex-1 p-1.5 rounded-lg text-center transition-all ${
                                aspectRatio === ratio.id
                                    ? 'bg-emerald-500 text-white shadow-md'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <span className="text-[10px] font-medium">{ratio.id}</span>
                            <p className="text-[9px] opacity-80">{ratio.name}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Video Style Tabs */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                        Video Style
                    </label>
                    <div className="flex bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg">
                        <button
                            onClick={() => setStyleTab('cinematic')}
                            className={`px-2 py-1 text-[10px] font-semibold rounded transition-all ${
                                styleTab === 'cinematic'
                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            Cinematic
                        </button>
                        <button
                            onClick={() => setStyleTab('3d')}
                            className={`px-2 py-1 text-[10px] font-semibold rounded transition-all flex items-center gap-1 ${
                                styleTab === '3d'
                                    ? 'bg-emerald-500 text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            3D
                            <span className={`px-1 py-0.5 rounded text-[8px] ${styleTab === '3d' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-600'}`}>NEW</span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-1.5 max-h-[120px] overflow-y-auto pr-1">
                    {filteredStyles.map((style) => (
                        <button
                            key={style.id}
                            onClick={() => setVideoStyle(style.id)}
                            disabled={disabled}
                            className={`p-1.5 rounded-lg text-left transition-all relative ${
                                videoStyle === style.id
                                    ? 'bg-emerald-500 text-white shadow-md ring-1 ring-emerald-400'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {(style.isNew || style.is3D) && (
                                <div className="absolute -top-1 -right-1">
                                    <span className="px-1 py-0.5 text-[8px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full">
                                        {style.is3D ? '3D' : 'NEW'}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center gap-1">
                                <span className="text-sm">{style.icon}</span>
                                <span className="text-[10px] font-medium truncate">{style.name}</span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Music Style */}
            <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Background Music
                </label>
                <div className="grid grid-cols-3 gap-1.5 max-h-[80px] overflow-y-auto pr-1">
                    {MUSIC_STYLES.map((music) => (
                        <button
                            key={music.id}
                            onClick={() => setMusicStyle(music.id)}
                            disabled={disabled}
                            className={`flex items-center gap-1.5 p-1.5 rounded-lg border transition-all text-left ${
                                musicStyle === music.id
                                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                                    : 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 hover:border-slate-300'
                            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <div className={`p-1 rounded ${musicStyle === music.id ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                                <Music className="w-3 h-3" />
                            </div>
                            <span className={`text-[10px] font-medium truncate ${musicStyle === music.id ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300'}`}>
                                {music.name}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Credit cost */}
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-between">
                    <span className="text-xs text-amber-800 dark:text-amber-200">Video reel cost:</span>
                    <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{CREDIT_COSTS.video_reel} credits</span>
                </div>
            </div>
        </div>
    );
};

VideoOptionsPanel.propTypes = {
    aspectRatio: PropTypes.string.isRequired,
    setAspectRatio: PropTypes.func.isRequired,
    videoStyle: PropTypes.string.isRequired,
    setVideoStyle: PropTypes.func.isRequired,
    musicStyle: PropTypes.string.isRequired,
    setMusicStyle: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};

/**
 * CreditsDisplay - Header credits badge with animation
 */
const CreditsDisplay = ({ credits, lastDeduction }) => {
    const creditColor = credits !== null ? getCreditStatusColor(credits) : 'emerald';

    return (
        <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            creditColor === 'red'
                ? 'bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30'
                : creditColor === 'amber'
                    ? 'bg-gradient-to-r from-amber-500/10 to-yellow-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30'
                    : 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
        }`}>
            <Sparkles className="w-4 h-4" />
            <span>{safeFormatCredits(credits)} credits</span>
            <AnimatePresence>
                {lastDeduction && (
                    <motion.span
                        key="credit-deduction"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        className="text-red-500 font-bold"
                    >
                        -{lastDeduction}
                    </motion.span>
                )}
            </AnimatePresence>
        </div>
    );
};

CreditsDisplay.propTypes = {
    credits: PropTypes.number,
    lastDeduction: PropTypes.number
};

/**
 * InsufficientCreditsModal
 */
const InsufficientCreditsModal = ({ onClose, onBuyCredits }) => (
    <motion.div
        key="insufficient-credits-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
    >
        <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500">
                    <Sparkles className="w-8 h-8 text-white" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                        Need More Credits
                    </h3>
                    <p className="text-sm text-slate-500">Purchase credits to continue staging</p>
                </div>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
                You don't have enough credits for this operation. Get more credits to continue creating stunning staged photos.
            </p>
            <div className="flex gap-3">
                <button
                    onClick={onClose}
                    className="flex-1 px-5 py-3 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all font-medium"
                >
                    Maybe Later
                </button>
                <button
                    onClick={() => {
                        onClose();
                        onBuyCredits?.();
                    }}
                    className="flex-1 px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all font-medium"
                >
                    Buy Credits
                </button>
            </div>
        </motion.div>
    </motion.div>
);

InsufficientCreditsModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onBuyCredits: PropTypes.func
};

// ============================================================
// BATCH STAGING COMPONENTS
// ============================================================

/**
 * BatchPhotoCard - Photo card with room type dropdown for batch mode
 */
const BatchPhotoCard = ({ photo, onRoomTypeChange, onRemove }) => {
    const [previewUrl, setPreviewUrl] = useState(null);

    // Create preview URL from file
    useEffect(() => {
        if (photo.file && !photo.thumbnailUrl) {
            const url = URL.createObjectURL(photo.file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [photo.file, photo.thumbnailUrl]);

    const RoomIcon = ROOM_ICONS[photo.roomType] || Home;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden group"
        >
            {/* Photo thumbnail */}
            <div className="aspect-[4/3] bg-slate-100 dark:bg-slate-900 overflow-hidden">
                <img
                    src={photo.thumbnailUrl || previewUrl || photo.originalUrl}
                    alt={photo.name || 'Property photo'}
                    className="w-full h-full object-cover"
                />
            </div>

            {/* Remove button */}
            <button
                onClick={() => onRemove(photo.id)}
                className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all"
            >
                <X className="w-4 h-4" />
            </button>

            {/* Room type selector */}
            <div className="p-3">
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                    Room Type
                </label>
                <div className="relative">
                    <select
                        value={photo.roomType}
                        onChange={(e) => onRoomTypeChange(photo.id, e.target.value)}
                        className="w-full pl-8 pr-8 py-2 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-800 dark:text-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                        {ROOM_TYPES.map((room) => (
                            <option key={room.id} value={room.id}>
                                {room.name}
                            </option>
                        ))}
                    </select>
                    <RoomIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 pointer-events-none" />
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
            </div>
        </motion.div>
    );
};

BatchPhotoCard.propTypes = {
    photo: PropTypes.object.isRequired,
    onRoomTypeChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired
};

/**
 * SmartBatchPhotoCard - Photo card with AI detection results
 */
const SmartBatchPhotoCard = ({ photo, onUpdate, onRemove, index, onDragStart, onDragOver, onDrop, isDragging, isVideoSelected, videoSelectionIndex, onToggleVideo, enableVideoSelection }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const isIndoor = photo.category === 'indoor';

    // Format room type for display
    const formatRoomType = (type) => {
        return type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
    };

    const photoUrl = photo.previewUrl || photo.thumbnailUrl || photo.originalUrl;

    return (
        <>
            <motion.div
                key={`smart-batch-card-${photo.photoId}`}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                draggable
                onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    onDragStart?.(index);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setIsDragOver(true);
                    onDragOver?.(index);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setIsDragOver(false);
                    onDrop?.(index);
                }}
                onDragEnd={() => setIsDragOver(false)}
                className={`relative bg-white dark:bg-slate-800 rounded-xl border-2 overflow-hidden group cursor-grab active:cursor-grabbing transition-all ${
                    isDragOver
                        ? 'border-emerald-500 ring-2 ring-emerald-500/30 scale-105'
                        : isDragging
                            ? 'border-violet-500 opacity-50'
                            : 'border-slate-200 dark:border-slate-700'
                }`}
            >
                {/* Drag handle indicator */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex gap-0.5">
                        <div className="w-1 h-1 rounded-full bg-white/70" />
                        <div className="w-1 h-1 rounded-full bg-white/70" />
                        <div className="w-1 h-1 rounded-full bg-white/70" />
                    </div>
                </div>

                {/* Photo thumbnail - clickable to expand */}
                <div
                    className="aspect-[4/3] bg-slate-100 dark:bg-slate-900 overflow-hidden relative cursor-pointer"
                    onClick={() => setIsExpanded(true)}
                >
                    <img
                        src={photoUrl}
                        alt={photo.roomType || 'Property photo'}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />

                    {/* Expand hint on hover */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-slate-800/90 rounded-full p-2">
                            <Maximize2 className="w-5 h-5 text-slate-700 dark:text-slate-300" />
                        </div>
                    </div>

                    {/* Category badge */}
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-medium ${
                        isIndoor
                            ? 'bg-blue-500/90 text-white'
                            : 'bg-amber-500/90 text-white'
                    }`}>
                        {isIndoor ? (
                            <span className="flex items-center gap-1">
                                <Home className="w-3 h-3" />
                                Indoor
                            </span>
                        ) : (
                            <span className="flex items-center gap-1">
                                <TreePine className="w-3 h-3" />
                                Outdoor
                            </span>
                        )}
                    </div>
                </div>

                {/* Video selection badge */}
                {enableVideoSelection && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleVideo?.(photo.photoId, photo.roomType);
                        }}
                        className={`absolute bottom-2 right-2 p-1.5 rounded-lg transition-all z-10 ${
                            isVideoSelected
                                ? 'bg-violet-600 text-white'
                                : 'bg-black/50 hover:bg-violet-600 text-white opacity-0 group-hover:opacity-100'
                        }`}
                        title={isVideoSelected ? `Video #${videoSelectionIndex + 1}` : 'Add to video'}
                    >
                        {isVideoSelected ? (
                            <span className="flex items-center gap-1 text-xs font-bold px-1">
                                <Film className="w-3.5 h-3.5" />
                                {videoSelectionIndex + 1}
                            </span>
                        ) : (
                            <Film className="w-4 h-4" />
                        )}
                    </button>
                )}

                {/* Remove button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(photo.photoId);
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all z-10"
                >
                    <X className="w-4 h-4" />
                </button>

            {/* Detection info and controls */}
            <div className="p-3 space-y-2">
                {/* Room type */}
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-800 dark:text-white">
                        {formatRoomType(photo.roomType)}
                    </span>
                </div>

                {/* Room type override */}
                <div className="relative">
                    <select
                        value={photo.roomType}
                        onChange={(e) => onUpdate(photo.photoId, { roomType: e.target.value })}
                        className="w-full px-2 py-1.5 text-xs bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                        <optgroup label="Indoor Rooms">
                            {ROOM_TYPES.filter(r => r.category === 'primary' || r.category === 'secondary').map((room) => (
                                <option key={room.id} value={room.id}>{room.name}</option>
                            ))}
                        </optgroup>
                        <optgroup label="Outdoor">
                            <option value="exterior_front">Exterior Front</option>
                            <option value="exterior_back">Exterior Back</option>
                            <option value="pool">Pool</option>
                            <option value="patio">Patio</option>
                            <option value="backyard">Backyard</option>
                            <option value="porch">Porch</option>
                        </optgroup>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                </div>

                {/* Category toggle */}
                <div className="flex gap-1">
                    <button
                        onClick={() => onUpdate(photo.photoId, { category: 'indoor' })}
                        className={`flex-1 px-2 py-1 text-xs rounded-lg transition-all ${
                            isIndoor
                                ? 'bg-blue-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                    >
                        Stage
                    </button>
                    <button
                        onClick={() => onUpdate(photo.photoId, { category: 'outdoor' })}
                        className={`flex-1 px-2 py-1 text-xs rounded-lg transition-all ${
                            !isIndoor
                                ? 'bg-amber-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                    >
                        Sky
                    </button>
                </div>
            </div>
        </motion.div>

        {/* Expanded photo modal */}
        <AnimatePresence>
            {isExpanded && (
                <motion.div
                    key="expanded-photo-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
                    onClick={() => setIsExpanded(false)}
                >
                    {/* Close button */}
                    <button
                        onClick={() => setIsExpanded(false)}
                        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    {/* Room type badge */}
                    <div className="absolute top-4 left-4 flex items-center gap-3">
                        <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                            isIndoor
                                ? 'bg-blue-500 text-white'
                                : 'bg-amber-500 text-white'
                        }`}>
                            {isIndoor ? 'Indoor - Staging' : 'Outdoor - Sky Enhancement'}
                        </span>
                        <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm rounded-full">
                            {formatRoomType(photo.roomType)}
                        </span>
                    </div>

                    {/* Main image */}
                    <motion.img
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.9 }}
                        src={photoUrl}
                        alt={photo.roomType || 'Property photo'}
                        className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    />
                </motion.div>
            )}
        </AnimatePresence>
        </>
    );
};

SmartBatchPhotoCard.propTypes = {
    photo: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    index: PropTypes.number,
    onDragStart: PropTypes.func,
    onDragOver: PropTypes.func,
    onDrop: PropTypes.func,
    isDragging: PropTypes.bool
};

/**
 * OutdoorModeSelector - Selector for outdoor enhancement modes
 */
const OutdoorModeSelector = ({ value, onChange }) => {
    const modeIcons = {
        twilight: Moon,
        sunset: Sunset,
        sunrise: Sunrise,
        blue_hour: Moon,
        dramatic: CloudSun,
        golden_hour: Sun
    };

    return (
        <div className="grid grid-cols-2 gap-2">
            {OUTDOOR_MODES.map((mode) => {
                const Icon = modeIcons[mode.value] || Sun;
                return (
                    <button
                        key={mode.value}
                        onClick={() => onChange(mode.value)}
                        className={`p-3 rounded-xl text-left transition-all ${
                            value === mode.value
                                ? 'ring-2 ring-amber-500 bg-amber-50 dark:bg-amber-900/20'
                                : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Icon className={`w-4 h-4 ${value === mode.value ? 'text-amber-500' : 'text-slate-400'}`} />
                            <span className={`text-sm font-medium ${value === mode.value ? 'text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'}`}>
                                {mode.label}
                            </span>
                            {mode.recommended && (
                                <span className="px-1.5 py-0.5 bg-amber-500 text-white text-[10px] rounded">Best</span>
                            )}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{mode.description}</p>
                    </button>
                );
            })}
        </div>
    );
};

OutdoorModeSelector.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired
};

/**
 * PropertyCard - Card showing property in the list with inline editing
 */
const PropertyCard = ({ property, onView, onDownload, onDelete, isEditing, editingName, onStartEdit, onEditNameChange, onSaveEdit, onCancelEdit }) => {
    const getStatusBadge = () => {
        const statusConfig = {
            draft: { color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400', icon: Clock, label: 'Draft' },
            pending: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock, label: 'Pending' },
            processing: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2, label: 'Processing' },
            completed: { color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle, label: 'Completed' },
            partial: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: AlertTriangle, label: 'Partial' },
            failed: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle, label: 'Failed' }
        };
        const config = statusConfig[property.status] || statusConfig.draft;
        const Icon = config.icon;
        return (
            <span className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${config.color}`}>
                <Icon className={`w-3 h-3 ${property.status === 'processing' ? 'animate-spin' : ''}`} />
                {config.label}
            </span>
        );
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => !isEditing && onView(property.batchId)}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                        {isEditing ? (
                            <input
                                type="text"
                                value={editingName}
                                onChange={(e) => onEditNameChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') onSaveEdit();
                                    if (e.key === 'Escape') onCancelEdit();
                                }}
                                onBlur={onSaveEdit}
                                className="w-full px-2 py-1 text-sm font-semibold bg-white dark:bg-slate-700 border border-emerald-500 rounded focus:outline-none text-slate-800 dark:text-white"
                                autoFocus
                            />
                        ) : (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onStartEdit && onStartEdit();
                                }}
                                className="font-semibold text-slate-800 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors flex items-center gap-1 group text-left"
                            >
                                {property.propertyName || 'Untitled Property'}
                                <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                            </button>
                        )}
                        {property.propertyAddress && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3" />
                                {property.propertyAddress}
                            </p>
                        )}
                    </div>
                </div>
                {getStatusBadge()}
            </div>

            <div className="grid grid-cols-4 gap-3 text-sm mb-4">
                <div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Photos</p>
                    <p className="font-medium text-slate-800 dark:text-white">{property.totalPhotos || 0}</p>
                </div>
                <div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Indoor</p>
                    <p className="font-medium text-blue-600 dark:text-blue-400">{property.indoorCount || 0}</p>
                </div>
                <div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Outdoor</p>
                    <p className="font-medium text-amber-600 dark:text-amber-400">{property.outdoorCount || 0}</p>
                </div>
                <div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Done</p>
                    <p className="font-medium text-emerald-600 dark:text-emerald-400">{property.completedCount || 0}</p>
                </div>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>{formatDate(property.createdAt)}</span>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    {property.status === 'completed' && property.packageUrl && (
                        <button
                            onClick={() => onDownload(property.batchId)}
                            className="flex items-center gap-1 px-3 py-1.5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                        >
                            <Download className="w-3 h-3" />
                            Download
                        </button>
                    )}
                    {property.status === 'completed' && (
                        <a
                            href={generateGalleryUrl(property.slug, property.batchId, property.propertyAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title="View gallery"
                        >
                            <ExternalLink className="w-3 h-3" />
                            Gallery
                        </a>
                    )}
                    {property.status === 'draft' && (
                        <button
                            onClick={() => onDelete(property.batchId)}
                            className="flex items-center gap-1 px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                            <Trash2 className="w-3 h-3" />
                        </button>
                    )}
                    <button
                        onClick={() => onView(property.batchId)}
                        className="flex items-center gap-1 px-3 py-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <Eye className="w-3 h-3" />
                        View
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

PropertyCard.propTypes = {
    property: PropTypes.object.isRequired,
    onView: PropTypes.func.isRequired,
    onDownload: PropTypes.func,
    onDelete: PropTypes.func,
    isEditing: PropTypes.bool,
    editingName: PropTypes.string,
    onStartEdit: PropTypes.func,
    onEditNameChange: PropTypes.func,
    onSaveEdit: PropTypes.func,
    onCancelEdit: PropTypes.func
};

/**
 * BatchUploader - Multi-file upload zone for batch staging
 */
const BatchUploader = ({ onUpload, isUploading, uploadProgress, uploadTotal }) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setIsDragActive(true);
        } else if (e.type === 'dragleave') {
            setIsDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            onUpload(e.dataTransfer.files);
        }
    }, [onUpload]);

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            onUpload(e.target.files);
        }
    };

    return (
        <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragActive
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400 dark:hover:border-emerald-600 bg-slate-50 dark:bg-slate-800/30'
            } ${isUploading ? 'pointer-events-none' : ''}`}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleFileSelect}
                className="hidden"
            />

            {isUploading ? (
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Uploading {uploadProgress} of {uploadTotal} photos...
                    </p>
                    <div className="w-48 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(uploadProgress / uploadTotal) * 100}%` }}
                            className="h-full bg-emerald-500 rounded-full"
                        />
                    </div>
                </div>
            ) : (
                <>
                    <Layers className={`w-10 h-10 mx-auto mb-3 ${isDragActive ? 'text-emerald-500' : 'text-slate-400'}`} />
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {isDragActive ? 'Drop photos here' : 'Click or drag multiple photos to upload'}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        JPEG, PNG, or WebP (max 20MB each, up to 20 photos)
                    </p>
                </>
            )}
        </div>
    );
};

BatchUploader.propTypes = {
    onUpload: PropTypes.func.isRequired,
    isUploading: PropTypes.bool,
    uploadProgress: PropTypes.number,
    uploadTotal: PropTypes.number
};

/**
 * BatchSubmitConfirmation - Modal shown after successful batch submission
 */
const BatchSubmitConfirmation = ({ batchInfo, onClose, onViewJobs }) => {
    // Function to download QR code as PNG
    const downloadQrCode = () => {
        const svg = document.getElementById('gallery-qr-code');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = 400;
            canvas.height = 400;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, 400, 400);

            const link = document.createElement('a');
            link.download = `gallery-qr-${batchInfo.batchId}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    // Function to copy gallery URL
    const copyGalleryUrl = () => {
        if (batchInfo.galleryUrl) {
            navigator.clipboard.writeText(batchInfo.galleryUrl);
        }
    };

    return (
        <motion.div
            key="batch-submit-confirmation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md w-full shadow-2xl text-center"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    Batch Submitted!
                </h3>

                <p className="text-slate-600 dark:text-slate-400 mb-6">
                    Your {batchInfo.photoCount} photos are being staged. We'll email you when they're ready.
                </p>

                {/* Gallery URL and QR Code Section */}
                {batchInfo.galleryUrl && (
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 mb-6">
                        <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-3">
                            <Globe className="w-4 h-4" />
                            <span>Property Gallery</span>
                        </div>

                        {/* QR Code */}
                        <div className="flex flex-col items-center gap-3 mb-4">
                            <div className="bg-white p-3 rounded-xl shadow-sm">
                                <QRCodeSVG
                                    id="gallery-qr-code"
                                    value={batchInfo.galleryUrl}
                                    size={150}
                                    level="M"
                                    includeMargin={false}
                                />
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Scan to view gallery
                            </p>
                        </div>

                        {/* Gallery URL with Copy */}
                        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg p-2">
                            <input
                                type="text"
                                readOnly
                                value={batchInfo.galleryUrl}
                                className="flex-1 text-xs bg-transparent text-slate-600 dark:text-slate-300 outline-none truncate"
                            />
                            <button
                                onClick={copyGalleryUrl}
                                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                title="Copy URL"
                            >
                                <Copy className="w-4 h-4 text-slate-500" />
                            </button>
                        </div>

                        {/* Download QR Button */}
                        <button
                            onClick={downloadQrCode}
                            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Download QR Code
                        </button>
                    </div>
                )}

                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-6">
                    <Mail className="w-4 h-4" />
                    <span>You'll receive an email with your gallery link and QR code</span>
                </div>

                <div className="flex gap-3">
                    {batchInfo.galleryUrl && (
                        <a
                            href={batchInfo.galleryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 border border-emerald-500 text-emerald-600 dark:text-emerald-400 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all font-medium"
                        >
                            <ExternalLink className="w-4 h-4" />
                            View Gallery
                        </a>
                    )}
                    <button
                        onClick={onClose}
                        className="flex-1 px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all font-medium"
                    >
                        Done
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};

BatchSubmitConfirmation.propTypes = {
    batchInfo: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onViewJobs: PropTypes.func
};

/**
 * BatchJobCard - Individual batch job in history list
 */
const BatchJobCard = ({ job, onViewDetails, onDownload, onCancel }) => {
    const getStatusBadge = () => {
        switch (job.status) {
            case 'completed':
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Completed
                    </span>
                );
            case 'processing':
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-lg text-xs font-medium">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Processing
                    </span>
                );
            case 'queued':
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg text-xs font-medium">
                        <Clock className="w-3 h-3" />
                        Queued
                    </span>
                );
            case 'failed':
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-xs font-medium">
                        <XCircle className="w-3 h-3" />
                        Failed
                    </span>
                );
            case 'cancelled':
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-500 rounded-lg text-xs font-medium">
                        <X className="w-3 h-3" />
                        Cancelled
                    </span>
                );
            default:
                return (
                    <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg text-xs font-medium">
                        {job.status}
                    </span>
                );
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-shadow"
        >
            <div className="flex items-start justify-between mb-3">
                <div>
                    <h4 className="font-semibold text-slate-800 dark:text-white">
                        {job.propertyName || 'Untitled Batch'}
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                        {job.batchId}
                    </p>
                </div>
                {getStatusBadge()}
            </div>

            <div className="grid grid-cols-3 gap-3 text-sm mb-4">
                <div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Photos</p>
                    <p className="font-medium text-slate-800 dark:text-white">
                        {job.completedCount || 0}/{job.photoCount}
                    </p>
                </div>
                <div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Style</p>
                    <p className="font-medium text-slate-800 dark:text-white">
                        {formatStyleName(job.style)}
                    </p>
                </div>
                <div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Credits</p>
                    <p className="font-medium text-emerald-600 dark:text-emerald-400">
                        {job.totalCredits}
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>{formatDate(job.createdAt)}</span>
                <div className="flex gap-2">
                    {(job.status === 'queued' || job.status === 'processing') && (
                        <button
                            onClick={() => onCancel?.(job.batchId)}
                            className="px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                    )}
                    {job.status === 'completed' && (
                        <button
                            onClick={() => onDownload?.(job.batchId)}
                            className="flex items-center gap-1 px-3 py-1.5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                        >
                            <Download className="w-3 h-3" />
                            Download
                        </button>
                    )}
                    <button
                        onClick={() => onViewDetails?.(job.batchId)}
                        className="flex items-center gap-1 px-3 py-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <Eye className="w-3 h-3" />
                        View
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

BatchJobCard.propTypes = {
    job: PropTypes.object.isRequired,
    onViewDetails: PropTypes.func,
    onDownload: PropTypes.func,
    onCancel: PropTypes.func
};

/**
 * BatchJobDetails - Modal showing details of a specific batch job
 */
const BatchJobDetails = ({ job, onClose, onDownload }) => {
    if (!job) return null;

    return (
        <motion.div
            key="batch-job-details"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-3xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                            {job.propertyName || 'Batch Job Details'}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">{job.batchId}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Photo Grid */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {job.photos?.map((photo, idx) => (
                            <div key={photo.photoId || idx} className="relative rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-900">
                                {photo.status === 'completed' && photo.stagedUrl ? (
                                    <BeforeAfterSlider
                                        beforeUrl={photo.originalUrl}
                                        afterUrl={photo.stagedUrl}
                                        className="aspect-[4/3]"
                                    />
                                ) : (
                                    <div className="aspect-[4/3] relative">
                                        <img
                                            src={photo.thumbnailUrl || photo.originalUrl}
                                            alt={`Photo ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                        {photo.status === 'processing' && (
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                                <Loader2 className="w-8 h-8 text-white animate-spin" />
                                            </div>
                                        )}
                                        {photo.status === 'failed' && (
                                            <div className="absolute inset-0 bg-red-900/50 flex items-center justify-center">
                                                <AlertTriangle className="w-8 h-8 text-white" />
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg">
                                    {ROOM_TYPES.find(r => r.id === photo.roomType)?.name || photo.roomType}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                        {job.completedCount} of {job.photoCount} photos completed
                    </div>
                    {job.status === 'completed' && (
                        <button
                            onClick={() => onDownload?.(job.batchId)}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                        >
                            <FileArchive className="w-5 h-5" />
                            Download All (ZIP)
                        </button>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

BatchJobDetails.propTypes = {
    job: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onDownload: PropTypes.func
};

// ============================================================
// SESSION-BASED STAGING COMPONENTS (ChatGPT-like)
// ============================================================

/**
 * SessionCard - Card for session list
 */
const SessionCard = ({ session, onOpen, onDelete }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => onOpen(session.session_id)}
        >
            <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                        <div>
                            <h4 className="font-semibold text-slate-800 dark:text-white truncate">
                                {session.title || 'Untitled Session'}
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                {session.message_count || 0} messages · v{session.current_version || 1}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {session.status === 'completed' && (
                                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs rounded-full">
                                    Complete
                                </span>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(session.session_id);
                                }}
                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                        {formatDate(session.updated_at)}
                    </p>
                </div>
            </div>
        </motion.div>
    );
};

SessionCard.propTypes = {
    session: PropTypes.object.isRequired,
    onOpen: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

/**
 * SessionListItem - Compact list item for session list (ChatGPT-style)
 */
const SessionListItem = ({ session, onOpen, onDelete }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg cursor-pointer group transition-colors"
            onClick={() => onOpen(session.session_id)}
        >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-800 dark:text-white truncate text-sm">
                    {session.title || 'Untitled Session'}
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {session.message_count || 0} messages · v{session.current_version || 1} · {formatDate(session.updated_at)}
                </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                {session.status === 'completed' && (
                    <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs rounded-full">
                        Complete
                    </span>
                )}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(session.session_id);
                    }}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </motion.div>
    );
};

SessionListItem.propTypes = {
    session: PropTypes.object.isRequired,
    onOpen: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

/**
 * FullScreenGallery - Full screen lightbox for before/after comparison
 */
const FullScreenGallery = ({ beforeUrl, afterUrl, version, isHD, resolution, onClose, onDownload }) => {
    return (
        <motion.div
            key="full-screen-gallery"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[100] flex flex-col"
        >
            {/* Close button - top right */}
            <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-colors"
            >
                <X className="w-6 h-6" />
            </button>

            {/* Version & Resolution badge - top center */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                <span className="px-3 py-1.5 bg-black/60 backdrop-blur-sm text-white text-sm rounded-full font-medium">
                    v{version}
                </span>
                {(isHD || resolution) && (
                    <span className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm rounded-full font-bold">
                        {resolution ? resolution.toUpperCase() : '2K HD'}
                    </span>
                )}
            </div>

            {/* Main comparison area - fills the screen */}
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="w-full h-full max-w-[98vw] max-h-[90vh]">
                    <BeforeAfterSlider
                        beforeUrl={beforeUrl}
                        afterUrl={afterUrl}
                        className="w-full h-full rounded-2xl"
                    />
                </div>
            </div>

            {/* Bottom bar with hint and download */}
            <div className="flex-shrink-0 px-6 pb-6 flex items-center justify-between">
                <p className="text-white/60 text-sm">
                    Drag the slider to compare before and after
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={() => onDownload(afterUrl, `staged-v${version}.jpg`)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors"
                    >
                        <Download className="w-5 h-5" />
                        Download Staged
                    </button>
                    <button
                        onClick={() => onDownload(beforeUrl, `original.jpg`)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-colors"
                    >
                        <Download className="w-5 h-5" />
                        Download Original
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

FullScreenGallery.propTypes = {
    beforeUrl: PropTypes.string.isRequired,
    afterUrl: PropTypes.string.isRequired,
    version: PropTypes.number,
    isHD: PropTypes.bool,
    resolution: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired
};

/**
 * ChatMessage - Individual message in the chat with before/after comparison
 */
const ChatMessage = ({ message, onBranchFrom, originalPhotoUrl, onGenerateReel, selectedResolution }) => {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';
    const [showComparison, setShowComparison] = useState(false);
    const [showFullScreen, setShowFullScreen] = useState(false);

    return (
        <>
            <motion.div
                key={`chat-message-${message.message_id || message.version || 'msg'}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}
            >
                {isAssistant && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0 mt-1">
                        <Wand2 className="w-4 h-4 text-white" />
                    </div>
                )}

                <div className={`max-w-[85%] ${isUser ? 'order-first' : ''}`}>
                    {isUser ? (
                        <div className="flex flex-col items-end gap-1">
                            {message.branch_from && (
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                                    <GitBranch className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                                    <span className="text-xs text-amber-700 dark:text-amber-300">
                                        Branching from v{message.branch_from}
                                    </span>
                                </div>
                            )}
                            <div className="px-4 py-3 bg-emerald-500 text-white rounded-2xl rounded-br-md">
                                <p className="text-sm">{message.content}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {message.image_url && (
                                <div className="relative group" style={{ maxWidth: '500px' }}>
                                    {/* Clickable image area - opens fullscreen */}
                                    <div
                                        className="cursor-pointer"
                                        onClick={() => originalPhotoUrl && setShowFullScreen(true)}
                                    >
                                        {/* Toggle between single image and comparison */}
                                        {showComparison && originalPhotoUrl ? (
                                            <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700" style={{ maxHeight: '350px' }}>
                                                <BeforeAfterSlider
                                                    beforeUrl={originalPhotoUrl}
                                                    afterUrl={message.image_url}
                                                    className="max-h-[350px]"
                                                />
                                            </div>
                                        ) : (
                                            <img
                                                src={message.image_url}
                                                alt={`Version ${message.version}`}
                                                className="rounded-xl max-w-full max-h-80 object-contain border border-slate-200 dark:border-slate-700"
                                            />
                                        )}
                                    </div>

                                    {/* Click to expand hint */}
                                    {originalPhotoUrl && (
                                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <div className="px-4 py-2 bg-black/70 backdrop-blur-sm text-white text-sm rounded-full flex items-center gap-2">
                                                <Maximize2 className="w-4 h-4" />
                                                Click to expand
                                            </div>
                                        </div>
                                    )}

                                    {/* Version badge */}
                                    <div className="absolute top-2 left-2 flex gap-2 pointer-events-none">
                                        <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg font-medium">
                                            v{message.version}
                                        </span>
                                        {message.parent_version && (
                                            <span className="px-2 py-1 bg-black/40 backdrop-blur-sm text-white/80 text-xs rounded-lg">
                                                from v{message.parent_version}
                                            </span>
                                        )}
                                        {(message.image_url_hd || message.result_url_hd || selectedResolution) && (
                                            <span className="px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs rounded-lg font-bold">
                                                {selectedResolution ? selectedResolution.toUpperCase() : '2K HD'}
                                            </span>
                                        )}
                                    </div>

                                    {/* Action buttons */}
                                    <div className="absolute bottom-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {originalPhotoUrl && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setShowComparison(!showComparison); }}
                                                className={`p-2 backdrop-blur-sm text-white rounded-lg transition-colors ${
                                                    showComparison
                                                        ? 'bg-emerald-500 hover:bg-emerald-600'
                                                        : 'bg-black/60 hover:bg-black/80'
                                                }`}
                                                title={showComparison ? "Show staged only" : "Compare before/after"}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); downloadImage(message.image_url_hd || message.result_url_hd || message.image_url, `staging-v${message.version}${message.image_url_hd ? '-HD' : ''}.${message.image_url_hd ? 'png' : 'jpg'}`); }}
                                            className="p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white rounded-lg transition-colors flex items-center gap-1"
                                            title={message.image_url_hd ? "Download HD (2K)" : "Download"}
                                        >
                                            <Download className="w-4 h-4" />
                                            {message.image_url_hd && <span className="text-[10px] font-bold text-amber-300">HD</span>}
                                        </button>
                                        {onGenerateReel && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onGenerateReel(message.image_url, message.version); }}
                                                className="p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white rounded-lg transition-colors"
                                                title="Generate Video Reel"
                                            >
                                                <Video className="w-4 h-4" />
                                            </button>
                                        )}
                                        {onBranchFrom && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onBranchFrom(message.version); }}
                                                className="p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white rounded-lg transition-colors"
                                                title="Branch from this version"
                                            >
                                                <GitBranch className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Comparison hint */}
                                    {showComparison && (
                                        <div className="absolute bottom-2 left-2 pointer-events-none">
                                            <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg">
                                                Drag to compare
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {isUser && (
                    <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">You</span>
                    </div>
                )}
            </motion.div>

            {/* Full screen gallery */}
            <AnimatePresence>
                {showFullScreen && originalPhotoUrl && (
                    <FullScreenGallery
                        beforeUrl={originalPhotoUrl}
                        afterUrl={message.image_url_hd || message.image_url}
                        version={message.version}
                        isHD={!!(message.image_url_hd || message.result_url_hd)}
                        resolution={selectedResolution}
                        onClose={() => setShowFullScreen(false)}
                        onDownload={downloadImage}
                    />
                )}
            </AnimatePresence>
        </>
    );
};

ChatMessage.propTypes = {
    message: PropTypes.object.isRequired,
    onBranchFrom: PropTypes.func,
    originalPhotoUrl: PropTypes.string,
    onGenerateReel: PropTypes.func,
    selectedResolution: PropTypes.string
};

/**
 * VersionTimeline - Version history for branching
 */
const VersionTimeline = ({ versions, currentVersion, onSelectVersion, onClose }) => (
    <motion.div
        key="version-timeline"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
    >
        <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-emerald-500" />
                    <h3 className="font-semibold text-slate-800 dark:text-white">Version History</h3>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
                    <X className="w-5 h-5 text-slate-500" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    Click a version to branch from it and try different refinements
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {versions.map((v) => (
                        <button
                            key={v.version}
                            onClick={() => {
                                onSelectVersion(v.version);
                                onClose();
                            }}
                            className={`relative rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                                v.version === currentVersion
                                    ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                                    : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400'
                            }`}
                        >
                            <img
                                src={v.image_url}
                                alt={`Version ${v.version}`}
                                className="w-full aspect-[4/3] object-cover"
                            />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                <span className="text-white text-sm font-medium">v{v.version}</span>
                                {v.parent_version && (
                                    <span className="text-white/70 text-xs ml-1">from v{v.parent_version}</span>
                                )}
                            </div>
                            {v.version === currentVersion && (
                                <div className="absolute top-2 right-2">
                                    <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full">Current</span>
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </motion.div>
    </motion.div>
);

VersionTimeline.propTypes = {
    versions: PropTypes.array.isRequired,
    currentVersion: PropTypes.number,
    onSelectVersion: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

/**
 * ConfirmModal - Styled confirmation dialog to replace native browser confirm
 */
const ConfirmModal = ({ isOpen, title, message, confirmLabel, cancelLabel, onConfirm, onCancel, variant }) => {
    if (!isOpen) return null;

    const isDestructive = variant === 'destructive';

    return (
        <motion.div
            key="confirm-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
            onClick={onCancel}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header with icon */}
                <div className="p-6 pb-4">
                    <div className={`w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center ${
                        isDestructive
                            ? 'bg-red-100 dark:bg-red-900/30'
                            : 'bg-amber-100 dark:bg-amber-900/30'
                    }`}>
                        <AlertCircle className={`w-6 h-6 ${
                            isDestructive
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-amber-600 dark:text-amber-400'
                        }`} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white text-center">
                        {title || 'Confirm Action'}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 text-center">
                        {message}
                    </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 p-4 pt-2 border-t border-slate-100 dark:border-slate-700">
                    <button
                        onClick={onCancel}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl transition-colors"
                    >
                        {cancelLabel || 'Cancel'}
                    </button>
                    <button
                        onClick={onConfirm}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors ${
                            isDestructive
                                ? 'bg-red-500 hover:bg-red-600'
                                : 'bg-emerald-500 hover:bg-emerald-600'
                        }`}
                    >
                        {confirmLabel || 'Confirm'}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};

ConfirmModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    title: PropTypes.string,
    message: PropTypes.string.isRequired,
    confirmLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['default', 'destructive'])
};

/**
 * ThinkingAnimation - Animated dot grid loading state
 */
const ThinkingAnimation = ({ message = "Generating a more detailed image — hang tight." }) => {
    const rows = 14;
    const cols = 16;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-4 max-w-lg"
        >
            <div className="space-y-2">
                <p className="text-lg font-medium text-emerald-500">Thinking</p>
                <p className="text-sm text-slate-400">{message}</p>
            </div>

            <div className="relative aspect-square w-full max-w-md bg-slate-800/80 dark:bg-slate-800 rounded-3xl p-6 overflow-hidden">
                {/* Animated dot grid */}
                <div
                    className="grid gap-3 w-full h-full"
                    style={{
                        gridTemplateColumns: `repeat(${cols}, 1fr)`,
                        gridTemplateRows: `repeat(${rows}, 1fr)`
                    }}
                >
                    {Array.from({ length: rows * cols }).map((_, idx) => {
                        const row = Math.floor(idx / cols);
                        const col = idx % cols;
                        const delay = (row + col) * 0.05;

                        return (
                            <motion.div
                                key={idx}
                                className="w-1.5 h-1.5 rounded-full bg-slate-500/60"
                                animate={{
                                    opacity: [0.3, 0.8, 0.3],
                                    scale: [1, 1.2, 1],
                                }}
                                transition={{
                                    duration: 2,
                                    delay: delay % 1.5,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            />
                        );
                    })}
                </div>

                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5 pointer-events-none" />
            </div>
        </motion.div>
    );
};

/**
 * Helper to extract room type from photo object (handles various API field names)
 */
const getPhotoRoomType = (photo) => {
    const roomType = photo?.roomType || photo?.room_type || photo?.detectedRoomType ||
                     photo?.detected_room_type || photo?.type || photo?.name || photo?.label;
    return roomType ? roomType.replace(/_/g, ' ') : 'Unknown';
};

/**
 * Helper to extract category from photo object
 */
const getPhotoCategory = (photo) => {
    return photo?.category || photo?.detectedCategory || photo?.detected_category || 'outdoor';
};

/**
 * Main VirtualStaging Component
 */
// Allowlist for analytics access (beta feature)
const ANALYTICS_ALLOWED_EMAILS = new Set([
    'nguyenlp.tpm@gmail.com',
    'andrew.uta99@yahoo.com',
]);

const VirtualStaging = ({ subscription, userEmail, userMeta, onUpgradePlan, onRefreshCredits }) => {
    const hasAccess = hasVirtualStagingAccess(subscription);
    const featureFlags = getFeatureFlags(subscription);
    const isUltra = featureFlags.isUltra || featureFlags.isRpUltra;
    // Allow all RP tiers to generate video (rp_starter, rp_pro, rp_ultra, ultra)
    const canGenerateVideo = isUltra || featureFlags.isRpPro || featureFlags.isRpStarter;
    // Check if user has analytics access
    const hasAnalyticsAccess = ANALYTICS_ALLOWED_EMAILS.has(userEmail?.toLowerCase());

    // State
    const [activeWorkspace, setActiveWorkspace] = useState('create'); // 'create' | 'library' | 'sessions' | 'bulk'
    const [createMode, setCreateMode] = useState('staging'); // 'staging' | 'video'
    const [libraryView, setLibraryView] = useState('photos'); // 'photos' | 'videos'
    const [bulkView, setBulkView] = useState('quick'); // 'quick' | 'properties' | 'history'

    // Videos state
    const [myVideos, setMyVideos] = useState([]);
    const [isLoadingVideos, setIsLoadingVideos] = useState(false);
    const [deletingVideoId, setDeletingVideoId] = useState(null);
    const [showVideoUploadModal, setShowVideoUploadModal] = useState(false);
    const [isUploadingVideo, setIsUploadingVideo] = useState(false);
    const [videoUploadProgress, setVideoUploadProgress] = useState(0);
    const [videoUploadError, setVideoUploadError] = useState(null);

    // Pro Editing Request state
    const [selectedVideosForPro, setSelectedVideosForPro] = useState([]);
    const [showSendToProModal, setShowSendToProModal] = useState(false);
    const [proEditStep, setProEditStep] = useState('details');
    const [proEditRequest, setProEditRequest] = useState({ ...DEFAULT_PRO_EDIT_REQUEST });
    const [proEditPayment, setProEditPayment] = useState({ ...DEFAULT_PRO_EDIT_PAYMENT });
    const [isSubmittingProRequest, setIsSubmittingProRequest] = useState(false);

    // Video Variations state
    const [variationPresets, setVariationPresets] = useState([]);
    const [showVariationModal, setShowVariationModal] = useState(false);
    const [selectedVideoForVariation, setSelectedVideoForVariation] = useState(null);
    const [selectedVariations, setSelectedVariations] = useState([]);
    const [variationNamePrefix, setVariationNamePrefix] = useState('');
    const [isCreatingVariations, setIsCreatingVariations] = useState(false);
    const [variationError, setVariationError] = useState(null);

    // Pro Editing Pricing Packages (can select multiple)
    const PRO_EDITING_PACKAGES = [
        {
            id: 'simple',
            name: 'Simple Edit',
            price: 14.99,
            description: 'Music, subtitles & transitions',
            features: ['Background music', 'Subtitles/captions', 'Smooth transitions', 'Color correction'],
            required: true // Base package, always included
        },
        {
            id: 'voiceover',
            name: 'Voice Over',
            price: 24.99,
            description: 'Professional voice over narration',
            features: ['Script writing', 'Professional voice actor', 'Audio mixing'],
            popular: true
        },
        {
            id: 'ai_actor',
            name: 'AI Actor Intro',
            price: 29.99,
            description: 'AI-generated presenter intro',
            features: ['AI avatar presenter', 'Custom intro/outro', 'Branded overlays']
        }
    ];

    // Rush delivery pricing
    const RUSH_OPTIONS = [
        { id: '24h', label: '24 hours', price: 0, badge: 'Standard' },
        { id: '12h', label: '12 hours', price: 14.99, badge: 'Rush' },
        { id: '6h', label: '6 hours', price: 19.99, badge: 'Priority' }
    ];

    const [photos, setPhotos] = useState([]);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [previewPhoto, setPreviewPhoto] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDeleting, setIsDeleting] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processingMessage, setProcessingMessage] = useState('');
    const [processingType, setProcessingType] = useState('staging');
    const [previewMode, setPreviewMode] = useState('slider');
    const [error, setError] = useState(null);
    const [credits, setCredits] = useState(null);
    const [lastCreditDeduction, setLastCreditDeduction] = useState(null);
    const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
    const [photoSearch, setPhotoSearch] = useState('');
    const [photoFilter, setPhotoFilter] = useState('all');
    const [photoSort, setPhotoSort] = useState('recent');
    const [showPhotoTray, setShowPhotoTray] = useState(false);

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        variant: 'default',
        onConfirm: () => {},
    });

    // Staging options
    const [selectedJobType, setSelectedJobType] = useState('full_staging');
    const [selectedStyle, setSelectedStyle] = useState('modern');
    const [selectedInspiration, setSelectedInspiration] = useState(null); // For inspiration-based styling
    const [dynamicInspirations, setDynamicInspirations] = useState(null); // Fetched from API
    const [dynamicInspirationCategories, setDynamicInspirationCategories] = useState(null); // Fetched from API
    // All resolutions unlocked for all users - default to 1K (most economical)
    const [selectedResolution, setSelectedResolution] = useState(DEFAULT_RESOLUTION);
    const [customPrompt, setCustomPrompt] = useState('');

    // Single photo category (indoor staging vs outdoor enhancement)
    const [singlePhotoCategory, setSinglePhotoCategory] = useState('indoor'); // 'indoor' | 'outdoor'
    const [singleOutdoorMode, setSingleOutdoorMode] = useState('twilight'); // Default outdoor enhancement mode

    // Video options
    const [aspectRatio, setAspectRatio] = useState('16:9');
    const [videoStyle, setVideoStyle] = useState('cinematic');
    const [musicStyle, setMusicStyle] = useState('ambient_piano');

    // Upload room type state (separate from selected photo's room type)
    const [uploadRoomType, setUploadRoomType] = useState('living_room');
    const [customRoomName, setCustomRoomName] = useState('');

    // Batch staging state (legacy - kept for compatibility)
    const [batchPhotos, setBatchPhotos] = useState([]);
    const [batchJobType, setBatchJobType] = useState('full_staging');
    const [batchStyle, setBatchStyle] = useState('modern');
    const [batchPropertyName, setBatchPropertyName] = useState('');
    const [isBatchUploading, setIsBatchUploading] = useState(false);
    const [batchUploadProgress, setBatchUploadProgress] = useState(0);
    const [batchUploadTotal, setBatchUploadTotal] = useState(0);
    const [batchUploadedCount, setBatchUploadedCount] = useState(0); // Track actual uploaded file count
    const [isServerProcessing, setIsServerProcessing] = useState(false); // True when upload done but server still processing
    const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);
    const [batchSubmitResult, setBatchSubmitResult] = useState(null);

    // Batch jobs history state
    const [batchJobs, setBatchJobs] = useState([]);
    const [isLoadingJobs, setIsLoadingJobs] = useState(false);
    const [selectedBatchJob, setSelectedBatchJob] = useState(null);

    // Smart AI Agent state (batch processing workflow)
    const [smartBatchMode, setSmartBatchMode] = useState('quick'); // 'quick' | 'properties'
    const [smartBatchStep, setSmartBatchStep] = useState(1); // 1: Property Info, 2: Upload, 3: Review, 4: Submit
    const [smartBatchAnalysis, setSmartBatchAnalysis] = useState([]); // AI detection results
    const [draggedPhotoIndex, setDraggedPhotoIndex] = useState(null); // For drag-drop reordering
    const [smartBatchSummary, setSmartBatchSummary] = useState(null);
    const [smartBatchId, setSmartBatchId] = useState(null); // Batch ID from analyze response
    const [isAnalyzingPhotos, setIsAnalyzingPhotos] = useState(false);
    const [smartIndoorStyle, setSmartIndoorStyle] = useState('modern');
    const [smartOutdoorMode, setSmartOutdoorMode] = useState('twilight');
    const [smartPropertyName, setSmartPropertyName] = useState('');
    const [smartStyleTab, setSmartStyleTab] = useState('styles'); // 'styles' or 'inspiration'
    const [smartInspiration, setSmartInspiration] = useState(null);
    const [smartInspirationCategory, setSmartInspirationCategory] = useState('all');

    // Property Info for batch flow (Step 1)
    const [smartBatchPropertyInfo, setSmartBatchPropertyInfo] = useState({
        propertyAddress: '',
        listingPrice: '',
        bedrooms: '',
        bathrooms: '',
        squareFeet: '',
        propertyDescription: ''
    });

    // Batch result state (for email delivery and QR code)
    const [smartBatchResult, setSmartBatchResult] = useState(null); // { batchId, galleryUrl, estimatedCompletion }

    // Video generation selections (3 photos max for automated video creation)
    const [selectedVideoPhotos, setSelectedVideoPhotos] = useState([]); // Array of photoIds selected for video
    const [enableVideoGeneration, setEnableVideoGeneration] = useState(true); // Toggle for video generation

    // Smart batch pricing config (fetched from API)
    const [smartBatchConfig, setSmartBatchConfig] = useState({
        batchFee: 250,           // Flat fee per batch
        creditsPerPhoto: 75,     // Cost per photo
        maxPhotos: 50,           // Max photos per batch
        galleryPublishCost: 100  // Cost to publish gallery
    });

    const [propertyStyleTab, setPropertyStyleTab] = useState('styles'); // For property settings panel
    const [propertyInspiration, setPropertyInspiration] = useState(null);
    const [propertyInspirationCategory, setPropertyInspirationCategory] = useState('all');

    // Property Management state
    const [properties, setProperties] = useState([]);
    const [isLoadingProperties, setIsLoadingProperties] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [isCreatingProperty, setIsCreatingProperty] = useState(false);
    const [newPropertyName, setNewPropertyName] = useState('');
    const [newPropertyAddress, setNewPropertyAddress] = useState('');
    const [isAddingPhotos, setIsAddingPhotos] = useState(false);
    const [isProcessingProperty, setIsProcessingProperty] = useState(false);
    const [enlargedPropertyPhoto, setEnlargedPropertyPhoto] = useState(null);
    const [propertiesViewMode, setPropertiesViewMode] = useState('list'); // 'grid' | 'list'
    const [propertySearchTerm, setPropertySearchTerm] = useState('');
    const [propertySortOrder, setPropertySortOrder] = useState('newest'); // 'newest' | 'oldest' | 'name'
    const [editingPropertyId, setEditingPropertyId] = useState(null);
    const [editingPropertyName, setEditingPropertyName] = useState('');
    const [propertyContentTab, setPropertyContentTab] = useState('photos'); // 'photos' | 'videos'

    // Gallery Publishing state
    const [showGalleryEditor, setShowGalleryEditor] = useState(false);
    const [showFlyerBuilder, setShowFlyerBuilder] = useState(false);
    const [showPostcardBuilder, setShowPostcardBuilder] = useState(false);
    const [showSocialPost, setShowSocialPost] = useState(false);
    const [showMailingListPicker, setShowMailingListPicker] = useState(false);
    const [showCampaignModal, setShowCampaignModal] = useState(false);
    const [pendingPostcardDesign, setPendingPostcardDesign] = useState(null);
    const [selectedMailingList, setSelectedMailingList] = useState(null);
    const [gallerySettings, setGallerySettings] = useState({
        template: 'luxury-dark',
        propertyName: '',
        propertyAddress: '',
        listingPrice: '',
        propertyDescription: '',
        bedrooms: '',
        bathrooms: '',
        squareFeet: '',
        features: [],
        // Agent Branding
        agentName: '',
        agentTitle: 'Real Estate Agent',
        agentPhone: '',
        agentEmail: '',
        agentPhoto: null,
        brokerageName: '',
        brokerageLogo: null,
        accentColor: '#d6b27f',
        // Settings
        showPrice: true,
        showContactForm: true,
        showDownloadButton: false,
        customSlug: ''
    });
    const [isPublishingGallery, setIsPublishingGallery] = useState(false);
    const [publishedGalleryUrl, setPublishedGalleryUrl] = useState(null);
    const [galleryEditorStep, setGalleryEditorStep] = useState(1); // 1: Property, 2: Preview (Template & Branding pulled from Account Settings)

    // Initialize gallery settings with user's branding data from userMeta
    useEffect(() => {
        if (userMeta) {
            setGallerySettings(prev => ({
                ...prev,
                agentName: userMeta.agentName || prev.agentName,
                agentTitle: userMeta.agentTitle || prev.agentTitle,
                agentPhone: userMeta.agentPhone || prev.agentPhone,
                agentEmail: userMeta.agentEmail || userEmail || prev.agentEmail,
                agentPhoto: userMeta.agentPhoto || prev.agentPhoto,
                brokerageName: userMeta.brokerageName || prev.brokerageName,
                brokerageLogo: userMeta.brokerageLogo || prev.brokerageLogo,
                accentColor: userMeta.accentColor || prev.accentColor,
                template: userMeta.defaultTemplate || prev.template
            }));
        }
    }, [userMeta, userEmail]);

    // Sync gallery settings with selected property info from database
    useEffect(() => {
        if (selectedProperty) {
            console.log('[VirtualStaging] Syncing selectedProperty to gallerySettings:', selectedProperty);

            // Property info may be nested under 'property' key from API
            const propInfo = selectedProperty.property || selectedProperty.propertyInfo || {};

            // Handle various field name formats from database (check nested 'property' object first)
            const price = propInfo.price || propInfo.listingPrice ||
                         selectedProperty.listingPrice || selectedProperty.listing_price || selectedProperty.price || '';
            const sqft = propInfo.squareFeet || propInfo.sqft ||
                        selectedProperty.squareFeet || selectedProperty.square_feet || selectedProperty.sqft || '';
            const beds = propInfo.bedrooms || propInfo.beds ||
                        selectedProperty.bedrooms || selectedProperty.beds || '';
            const baths = propInfo.bathrooms || propInfo.baths ||
                         selectedProperty.bathrooms || selectedProperty.baths || '';
            const desc = propInfo.description || propInfo.propertyDescription ||
                        selectedProperty.propertyDescription || selectedProperty.property_description || selectedProperty.description || '';
            const addr = propInfo.address || propInfo.propertyAddress ||
                        selectedProperty.propertyAddress || selectedProperty.property_address || selectedProperty.address || '';
            const name = selectedProperty.propertyName || selectedProperty.property_name || selectedProperty.name || selectedProperty.title || '';

            console.log('[VirtualStaging] Extracted property info:', { price, beds, baths, sqft, desc, addr, name });

            setGallerySettings(prev => ({
                ...prev,
                propertyName: name || prev.propertyName,
                propertyAddress: addr || prev.propertyAddress,
                listingPrice: typeof price === 'number' ? price.toLocaleString() : price || prev.listingPrice,
                bedrooms: beds || prev.bedrooms,
                bathrooms: baths || prev.bathrooms,
                squareFeet: typeof sqft === 'number' ? sqft.toLocaleString() : sqft || prev.squareFeet,
                propertyDescription: desc || prev.propertyDescription
            }));
        }
    }, [selectedProperty]);

    const handleSelectGalleryTemplate = useCallback((templateId) => {
        const nextTemplate = getPropertyGalleryTemplate(templateId);
        setGallerySettings(prev => ({
            ...prev,
            template: templateId,
            accentColor: nextTemplate.accentColor
        }));
    }, []);

    // Batch photo editing state (chat refinement for processed batch photos)
    const [batchPhotoSession, setBatchPhotoSession] = useState(null);
    const [batchPhotoMessages, setBatchPhotoMessages] = useState([]);
    const [batchPhotoInput, setBatchPhotoInput] = useState('');
    const [isGeneratingBatchRefinement, setIsGeneratingBatchRefinement] = useState(false);
    const [batchPhotoVersions, setBatchPhotoVersions] = useState([]);
    const [showBatchVersionHistory, setShowBatchVersionHistory] = useState(false);

    // Session-based staging state (ChatGPT-like conversational refinement)
    const [sessions, setSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [sessionsViewMode, setSessionsViewMode] = useState('list'); // 'grid' | 'list'
    const [activeSession, setActiveSession] = useState(null);
    const [sessionMessages, setSessionMessages] = useState([]);
    const [refinementInput, setRefinementInput] = useState('');
    const [isGeneratingRefinement, setIsGeneratingRefinement] = useState(false);
    const [sessionVersions, setSessionVersions] = useState([]);
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [branchFromVersion, setBranchFromVersion] = useState(null);

    // Video reel options modal state
    const [showReelOptionsModal, setShowReelOptionsModal] = useState(false);
    const [pendingReelRequest, setPendingReelRequest] = useState(null); // { imageUrl, version }

    // Batch video generation state
    const [selectedPhotosForVideo, setSelectedPhotosForVideo] = useState([]);
    const [showBatchVideoModal, setShowBatchVideoModal] = useState(false);
    const [isGeneratingBatchVideo, setIsGeneratingBatchVideo] = useState(false);
    const [batchVideoAspectRatio, setBatchVideoAspectRatio] = useState('16:9');
    const [batchVideoStyle, setBatchVideoStyle] = useState('cinematic');
    const [batchVideoMusic, setBatchVideoMusic] = useState('ambient_piano');
    const [batchStyleTab, setBatchStyleTab] = useState('cinematic'); // 'cinematic' or '3d'

    // Download progress state
    const [isDownloadingZip, setIsDownloadingZip] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadMessage, setDownloadMessage] = useState('');

    // Success message state (for batch operations)
    const [successMessage, setSuccessMessage] = useState('');

    const recommendedStyleIds = ROOM_SUGGESTED_STYLES[selectedPhoto?.roomType || 'living_room'] || [];

    const filteredPhotos = useMemo(() => {
        const normalizedSearch = photoSearch.trim().toLowerCase();
        const filtered = photos.filter((photo) => {
            const roomName = ROOM_TYPES.find((room) => room.id === photo.roomType)?.name || photo.roomType || '';
            const matchesSearch = !normalizedSearch || [photo.name, roomName, photo.customRoomName]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(normalizedSearch));

            const matchesFilter =
                photoFilter === 'all' ||
                (photoFilter === 'staged' && Boolean(photo.stagedUrl)) ||
                (photoFilter === 'unstaged' && !photo.stagedUrl) ||
                (photoFilter === 'living' && photo.roomType === 'living_room') ||
                (photoFilter === 'bedroom' && photo.roomType === 'bedroom');

            return matchesSearch && matchesFilter;
        });

        return filtered.sort((a, b) => {
            if (photoSort === 'status') {
                if (a.stagedUrl && !b.stagedUrl) return -1;
                if (!a.stagedUrl && b.stagedUrl) return 1;
            }
            if (photoSort === 'room') {
                return (a.roomType || '').localeCompare(b.roomType || '');
            }

            return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        });
    }, [photos, photoFilter, photoSearch, photoSort]);

    const groupedPhotos = useMemo(() => ({
        staged: filteredPhotos.filter((photo) => Boolean(photo.stagedUrl)),
        unstaged: filteredPhotos.filter((photo) => !photo.stagedUrl)
    }), [filteredPhotos]);

    const selectedPhotoTotalCredits = CREDIT_COSTS.full_staging + (RESOLUTION_OPTIONS.find((option) => option.value === selectedResolution)?.extraCredits || 0);

    useEffect(() => {
        const handleKeyDown = (event) => {
            const targetTag = event.target?.tagName;
            if (targetTag === 'INPUT' || targetTag === 'TEXTAREA' || event.target?.isContentEditable) {
                return;
            }

            if (event.key === '1') {
                setActiveWorkspace('create');
                setCreateMode('staging');
            }
            if (event.key === '2') {
                setActiveWorkspace('create');
                setCreateMode('video');
            }
            if (event.key === '3') {
                setActiveWorkspace('sessions');
                setActiveSession(null);
            }
            if (event.key.toLowerCase() === 'f') setPreviewPhoto(selectedPhoto || null);
            if (event.key.toLowerCase() === 'r' && activeSession && !isGeneratingRefinement) {
                setRefinementInput((prev) => prev || QUICK_REFINEMENTS[0]);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeSession, isGeneratingRefinement, selectedPhoto]);

    // Load photos and credits on mount
    useEffect(() => {
        if (!hasAccess) {
            // If no access, stop loading state to show upgrade prompt
            setIsLoading(false);
            return;
        }

        let isMounted = true;

        const loadData = async () => {
            setIsLoading(true);

            // Add timeout to prevent infinite loading (30 seconds)
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Load timeout')), 30000)
            );

            try {
                const dataPromise = Promise.all([
                    fetchPhotos().catch(() => ({ photos: [] })),
                    fetchCreditBalance().catch(() => ({ credits_balance: subscription?.aiCreditsRemaining ?? 0 })),
                    fetchStagingConfig().catch(() => null), // Fetch inspirations from API
                    fetchSmartBatchConfig().catch(() => null) // Fetch smart batch pricing config
                ]);

                const [photosData, creditsData, stagingConfig, batchConfig] = await Promise.race([dataPromise, timeoutPromise]);

                if (isMounted) {
                    setPhotos(photosData.photos || []);
                    setCredits(creditsData.credits_balance);
                    // Set dynamic inspirations if available from API
                    if (stagingConfig?.inspirations) {
                        setDynamicInspirations(stagingConfig.inspirations);
                    }
                    if (stagingConfig?.inspirationCategories) {
                        setDynamicInspirationCategories(stagingConfig.inspirationCategories);
                    }
                    // Set smart batch pricing config if available
                    if (batchConfig) {
                        setSmartBatchConfig(prev => ({
                            ...prev,
                            batchFee: batchConfig.batchFee ?? prev.batchFee,
                            creditsPerPhoto: batchConfig.creditsPerPhoto ?? prev.creditsPerPhoto,
                            maxPhotos: batchConfig.maxPhotos ?? prev.maxPhotos
                        }));
                    }
                }
            } catch (err) {
                console.error('Failed to load data:', err);
                // On error/timeout, set defaults so page is still usable
                if (isMounted) {
                    setPhotos([]);
                    setCredits(subscription?.aiCreditsRemaining ?? 0);
                    // Keep using static INSPIRATIONS as fallback
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [hasAccess, subscription]);

    // Handle photo upload
    const handleUpload = useCallback(async (file, roomType, customName = null) => {
        if (!file) return;

        // Validate file
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setError('Invalid file type. Please upload JPEG, PNG, or WebP images.');
            return;
        }

        if (file.size > 20 * 1024 * 1024) {
            setError('File too large. Maximum size is 20MB.');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        try {
            // Simulate progress for better UX
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => Math.min(prev + 10, 90));
            }, 200);

            const result = await uploadPhoto(file, roomType);

            clearInterval(progressInterval);
            setUploadProgress(100);

            // Use roomType from backend if provided, otherwise use the selected one
            const finalRoomType = result.roomType || result.room_type || roomType;

            // Add to photos list
            const newPhoto = {
                id: result.photoId || result.photo_id || result.id,
                originalUrl: result.url || result.originalUrl || result.original_url,
                thumbnailUrl: result.thumbnailUrl || result.thumbnail_url || result.url,
                roomType: finalRoomType,
                customRoomName: roomType === 'other' ? customName : null,
                stagedUrl: null,
                createdAt: new Date().toISOString()
            };

            setPhotos((prev) => [newPhoto, ...prev]);
            setSelectedPhoto(newPhoto);
            // Reset style/inspiration for new photo
            setSelectedInspiration(null);
            setSelectedStyle(null);

            setTimeout(() => {
                setIsUploading(false);
                setUploadProgress(0);
            }, 500);
        } catch (err) {
            console.error('Upload failed:', err);
            setError(err.message || 'Failed to upload photo. Please try again.');
            setIsUploading(false);
            setUploadProgress(0);
        }
    }, []);

    // Handle photo delete
    const handleDelete = useCallback(async (photoId) => {
        setIsDeleting(photoId);
        try {
            await deletePhoto(photoId);
            setPhotos((prev) => prev.filter((p) => p.id !== photoId));
            if (selectedPhoto?.id === photoId) {
                setSelectedPhoto(null);
            }
        } catch (err) {
            console.error('Delete failed:', err);
            setError('Failed to delete photo. Please try again.');
        } finally {
            setIsDeleting(null);
        }
    }, [selectedPhoto]);

    // Update selected photo's room type
    const handleUpdatePhotoRoomType = useCallback((newRoomType, newCustomRoomName = null) => {
        if (!selectedPhoto) return;

        const updatedPhoto = {
            ...selectedPhoto,
            roomType: newRoomType,
            customRoomName: newRoomType === 'other' ? newCustomRoomName : null
        };

        // Update in photos list
        setPhotos(prev => prev.map(p =>
            p.id === selectedPhoto.id ? updatedPhoto : p
        ));

        // Update selected photo
        setSelectedPhoto(updatedPhoto);

    }, [selectedPhoto]);

    // Handle staging job
    const handleStage = useCallback(async () => {
        if (!selectedPhoto || isProcessing) return;

        setIsProcessing(true);
        setProcessingProgress(10);
        const isOutdoor = singlePhotoCategory === 'outdoor';
        setProcessingMessage(isOutdoor ? 'Analyzing exterior photo...' : 'Analyzing room layout...');
        setProcessingType('staging');
        setError(null);

        // Simulated progress interval (declared here for cleanup access)
        let progressInterval = null;
        let simulatedProgress = 10;

        try {
            const result = await startStagingJob(
                selectedPhoto.id,
                selectedJobType,
                selectedStyle,
                selectedPhoto.roomType,
                customPrompt || null,
                singlePhotoCategory,
                singleOutdoorMode
            );

            // Get job ID from response (handle different field names)
            const jobId = result.jobId || result.job_id || result.id;

            if (!jobId) {
                throw new Error('No job ID returned from staging service');
            }

            // Start simulated progress (backend may not send real-time progress)
            // Smooth asymptotic progress - never stops, just slows down
            setProcessingProgress(15);
            setProcessingMessage('AI processing started...');

            progressInterval = setInterval(() => {
                // Asymptotic approach: increment decreases as we approach 99%
                const remaining = 99 - simulatedProgress;
                const increment = Math.max(0.1, remaining * 0.025 + Math.random() * 0.4);
                simulatedProgress = Math.min(simulatedProgress + increment, 98.5);
                const roundedProgress = Math.round(simulatedProgress);
                setProcessingProgress(roundedProgress);

                // Different progress messages for indoor staging vs outdoor enhancement
                const newMessage = isOutdoor
                    ? (
                        roundedProgress < 20 ? 'Analyzing exterior photo...' :
                        roundedProgress < 32 ? 'Detecting sky region...' :
                        roundedProgress < 44 ? 'Preparing sky replacement...' :
                        roundedProgress < 56 ? 'Generating dramatic sky...' :
                        roundedProgress < 68 ? 'Blending horizon line...' :
                        roundedProgress < 78 ? 'Adjusting lighting & colors...' :
                        roundedProgress < 86 ? 'Adding window glow effects...' :
                        roundedProgress < 92 ? 'Enhancing details...' :
                        roundedProgress < 96 ? 'Final quality check...' :
                        'Almost there...'
                    )
                    : (
                        roundedProgress < 20 ? 'Analyzing room layout...' :
                        roundedProgress < 32 ? 'Identifying furniture zones...' :
                        roundedProgress < 44 ? 'Selecting style elements...' :
                        roundedProgress < 56 ? 'Placing virtual furniture...' :
                        roundedProgress < 68 ? 'Adjusting lighting & shadows...' :
                        roundedProgress < 78 ? 'Rendering high-res image...' :
                        roundedProgress < 86 ? 'Enhancing textures...' :
                        roundedProgress < 92 ? 'Polishing details...' :
                        roundedProgress < 96 ? 'Final quality check...' :
                        'Almost there...'
                    );
                setProcessingMessage(newMessage);
            }, 120); // Faster updates for smoother feel

            // Poll for completion
            const completed = await pollJobUntilComplete(
                jobId,
                'image',
                (progress, message) => {
                    // Use backend progress if available, otherwise keep simulated
                    if (progress > 0) {
                        clearInterval(progressInterval);
                        setProcessingProgress(progress);
                        setProcessingMessage(message);
                    }
                }
            );
            clearInterval(progressInterval);
            setProcessingProgress(100);
            setProcessingMessage('Complete!');

            // Get staged URL from response (handle different field names)
            const stagedUrl = completed.stagedUrl || completed.staged_url || completed.resultUrl || completed.result_url || completed.url || completed.imageUrl || completed.image_url;
            // Get HD URL for high-quality download (2K upscaled)
            const stagedUrlHd = completed.stagedUrlHd || completed.staged_url_hd || completed.resultUrlHd || completed.result_url_hd || completed.imageUrlHd || completed.image_url_hd;

            // Update photo with staged URL (including HD version)
            setPhotos((prev) => prev.map((p) =>
                p.id === selectedPhoto.id
                    ? { ...p, stagedUrl, stagedUrlHd }
                    : p
            ));

            setSelectedPhoto((prev) => ({
                ...prev,
                stagedUrl,
                stagedUrlHd
            }));

            // Update credits
            const cost = CREDIT_COSTS[selectedJobType];
            setLastCreditDeduction(cost);
            setCredits((prev) => prev - cost);
            setTimeout(() => setLastCreditDeduction(null), 3000);

            // Clear custom prompt after successful staging
            setCustomPrompt('');

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Staging failed:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
            } else if (err.code === 'JOB_FAILED') {
                setError(`Staging failed: ${err.message}${err.creditsRefunded ? ' (credits refunded)' : ''}`);
            } else {
                setError(err.message || 'Staging failed. Please try again.');
            }
        } finally {
            if (progressInterval) clearInterval(progressInterval);
            setIsProcessing(false);
            setProcessingProgress(0);
            setProcessingMessage('');
        }
    }, [selectedPhoto, selectedJobType, selectedStyle, customPrompt, isProcessing, onRefreshCredits, singlePhotoCategory, singleOutdoorMode]);

    // Handle video generation
    const handleGenerateVideo = useCallback(async () => {
        if (!selectedPhoto?.stagedUrl || isProcessing) return;

        setIsProcessing(true);
        setProcessingProgress(0);
        setProcessingMessage('Starting video generation...');
        setProcessingType('video');
        setError(null);

        try {
            const result = await startVideoJob(
                selectedPhoto.id,
                aspectRatio,
                videoStyle,
                musicStyle,
                selectedProperty?.propertyName || null
            );

            // Get video job ID from response (handle different field names)
            const videoJobId = result.videoId || result.video_id || result.jobId || result.job_id || result.id;

            if (!videoJobId) {
                throw new Error('No video job ID returned from service');
            }

            // Poll for completion
            const completed = await pollJobUntilComplete(
                videoJobId,
                'video',
                (progress, message) => {
                    setProcessingProgress(progress);
                    setProcessingMessage(message);
                }
            );

            // Download video - check multiple possible field names
            const videoUrl = completed.videoUrl || completed.resultUrl || completed.video_url || completed.result_url || completed.url || completed.output_url || completed.outputUrl;
            const thumbnailUrl = completed.thumbnailUrl || completed.thumbnail_url || selectedPhoto.thumbnailUrl;
            if (videoUrl) {
                // Trigger download to user's computer
                downloadVideo(videoUrl, `property-reel-${selectedPhoto.id}.mp4`);

                // Save video to backend for persistence
                try {
                    const savedVideo = await saveVideo({
                        videoUrl: videoUrl,
                        thumbnailUrl: thumbnailUrl,
                        name: `Property Reel - ${selectedPhoto.roomType || 'Room'}`,
                        duration: completed.duration || '0:08',
                        sourceType: 'virtual_staging',
                        sourceId: selectedPhoto.id,
                        metadata: {
                            resolution: selectedResolution,
                            aspectRatio: aspectRatio,
                            style: videoStyle,
                            roomType: selectedPhoto.roomType
                        }
                    });

                    // Add saved video to local state (use backend response)
                    const newVideo = {
                        id: savedVideo?.data?.id || savedVideo?.id || completed.id || `video-${Date.now()}`,
                        name: savedVideo?.data?.name || `Property Reel - ${selectedPhoto.roomType || 'Room'}`,
                        videoUrl: savedVideo?.data?.videoUrl || videoUrl,
                        thumbnailUrl: savedVideo?.data?.thumbnailUrl || thumbnailUrl,
                        shortUrl: savedVideo?.data?.shortUrl || null,
                        createdAt: savedVideo?.data?.createdAt || new Date().toISOString(),
                        duration: savedVideo?.data?.duration || completed.duration || null
                    };
                    setMyVideos(prev => [newVideo, ...prev]);
                    // Show success message
                    setSuccessMessage('Video created and saved to your Video Library!');
                    setTimeout(() => setSuccessMessage(''), 5000);
                } catch (saveErr) {
                    console.error('Failed to save video to library:', saveErr);
                    // Still add to local state even if backend save fails
                    const newVideo = {
                        id: completed.id || completed.jobId || `video-${Date.now()}`,
                        name: `Property Reel - ${selectedPhoto.roomType || 'Room'}`,
                        videoUrl: videoUrl,
                        thumbnailUrl: thumbnailUrl,
                        createdAt: new Date().toISOString(),
                        duration: completed.duration || null
                    };
                    setMyVideos(prev => [newVideo, ...prev]);
                    setSuccessMessage('Video created! Added to your Video Library.');
                    setTimeout(() => setSuccessMessage(''), 5000);
                }
            } else {
                // No video URL in response
                console.error('Video completed but no URL found in response:', completed);
                setError('Video generated but download URL not available. Please try again.');
            }

            // Update credits
            setLastCreditDeduction(CREDIT_COSTS.video_reel);
            setCredits((prev) => prev - CREDIT_COSTS.video_reel);
            setTimeout(() => setLastCreditDeduction(null), 3000);

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Video generation failed:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
            } else if (err.code === 'JOB_FAILED') {
                setError(`Video generation failed: ${err.message}${err.creditsRefunded ? ' (credits refunded)' : ''}`);
            } else {
                setError(err.message || 'Video generation failed. Please try again.');
            }
        } finally {
            setIsProcessing(false);
            setProcessingProgress(0);
            setProcessingMessage('');
        }
    }, [selectedPhoto, aspectRatio, videoStyle, musicStyle, isProcessing, onRefreshCredits, selectedResolution, selectedProperty?.propertyName]);

    // Handle Magic Reel - 3 high-end variations at once
    const handleMagicReel = useCallback(async () => {
        if (!selectedPhoto?.stagedUrl || isProcessing) return;

        setIsProcessing(true);
        setProcessingProgress(0);
        setProcessingMessage('Queuing magic marketing package...');
        setProcessingType('video');
        setError(null);

        try {
            // Define the 3 premium styles for the marketing package
            const magicStyles = [
                { style: 'luxury_modern', music: 'luxury_lofi' },
                { style: 'twilight_serenity', music: 'ambient_piano' },
                { style: 'virtual_staging_fly_in', music: 'macro_detail' }
            ];

            // Submit 3 separate batch jobs (processed in background)
            for (const item of magicStyles) {
                await submitBatchVideoJob(
                    [selectedPhoto],
                    aspectRatio,
                    item.style,
                    item.music,
                    `Magic Reel - ${selectedPhoto.roomType || 'Room'}`
                );
            }

            setSuccessMessage("✨ Magic Marketing Package queued! You'll receive an email when all 3 high-end reels are ready.");
            setTimeout(() => setSuccessMessage(''), 8000);

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Magic reel failed:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
            } else {
                setError(err.message || 'Failed to start Magic Reel. Please try again.');
            }
        } finally {
            setIsProcessing(false);
            setProcessingProgress(0);
            setProcessingMessage('');
        }
    }, [selectedPhoto, aspectRatio, isProcessing, onRefreshCredits]);

    // Show video options modal before generating
    const handleGenerateReelFromSession = useCallback((imageUrl, version) => {
        if (!activeSession || isProcessing) return;
        setPendingReelRequest({ imageUrl, version, type: 'session' });
        setShowReelOptionsModal(true);
    }, [activeSession, isProcessing]);

    // Open reel modal for batch/property photo - uses same modal as session
    const handleGenerateReelFromBatchPhoto = useCallback((photo) => {
        if (isProcessing) return;
        const photoId = photo.photoId || photo.photo_id || photo.id;
        const imageUrl = photo.processedUrl || photo.processed_url || photo.stagedUrl || photo.staged_url || photo.url;
        const roomType = photo.roomType || photo.room_type || 'Room';
        setPendingReelRequest({ imageUrl, photoId, roomType, type: 'batch' });
        setShowReelOptionsModal(true);
    }, [isProcessing]);

    // Actually generate the video reel after options are selected
    // Handles both session-based and batch photo videos
    const handleConfirmGenerateReel = useCallback(async () => {
        if (!pendingReelRequest || isProcessing) return;

        // For session type, require active session
        if (pendingReelRequest.type === 'session' && !activeSession) return;

        setShowReelOptionsModal(false);
        setIsProcessing(true);
        setProcessingMessage('Starting video generation...');
        setProcessingType('video');
        setError(null);

        try {
            let result;
            let videoName;
            let sourceId;

            // Call appropriate API based on type
            if (pendingReelRequest.type === 'batch') {
                // Batch photo - use startVideoJob with photoId
                result = await startVideoJob(
                    pendingReelRequest.photoId,
                    aspectRatio,
                    videoStyle,
                    musicStyle,
                    selectedProperty?.propertyName || null
                );
                videoName = `Property Reel - ${pendingReelRequest.roomType}`;
                sourceId = pendingReelRequest.photoId;
            } else {
                // Session - use startVideoJobFromSession
                result = await startVideoJobFromSession(
                    activeSession.session_id,
                    pendingReelRequest.version,
                    aspectRatio,
                    videoStyle,
                    musicStyle,
                    selectedProperty?.propertyName || null
                );
                videoName = `Session Reel - v${pendingReelRequest.version}`;
                sourceId = activeSession?.id;
            }

            // Get video job ID from response
            const videoJobId = result.videoId || result.video_id || result.jobId || result.job_id || result.id;

            if (!videoJobId) {
                throw new Error('No video job ID returned from service');
            }

            // Poll for completion
            const completed = await pollJobUntilComplete(
                videoJobId,
                'video',
                (progress, message) => {
                    setProcessingProgress(progress);
                    setProcessingMessage(message || 'Generating video reel...');
                }
            );

            // Download video - check multiple possible field names
            const videoUrl = completed.videoUrl || completed.resultUrl || completed.video_url || completed.result_url || completed.url || completed.output_url || completed.outputUrl;
            const thumbnailUrl = completed.thumbnailUrl || completed.thumbnail_url || pendingReelRequest.imageUrl;

            if (videoUrl) {
                // Trigger download to user's computer
                downloadVideo(videoUrl, `${videoName.toLowerCase().replace(/\s+/g, '-')}.mp4`);

                // Save video to backend for persistence
                try {
                    const savedVideo = await saveVideo({
                        videoUrl: videoUrl,
                        thumbnailUrl: thumbnailUrl,
                        name: videoName,
                        duration: completed.duration || '0:08',
                        sourceType: 'virtual_staging',
                        sourceId: sourceId,
                        metadata: {
                            resolution: selectedResolution,
                            aspectRatio: aspectRatio,
                            style: videoStyle,
                            roomType: pendingReelRequest.roomType || activeSession?.roomType,
                            version: pendingReelRequest.version
                        }
                    });

                    // Add saved video to local state (use backend response)
                    const newVideo = {
                        id: savedVideo?.data?.id || savedVideo?.id || completed.id || `video-${Date.now()}`,
                        name: savedVideo?.data?.name || videoName,
                        videoUrl: savedVideo?.data?.videoUrl || videoUrl,
                        thumbnailUrl: savedVideo?.data?.thumbnailUrl || thumbnailUrl,
                        shortUrl: savedVideo?.data?.shortUrl || null,
                        createdAt: savedVideo?.data?.createdAt || new Date().toISOString(),
                        duration: savedVideo?.data?.duration || completed.duration || null
                    };
                    setMyVideos(prev => [newVideo, ...prev]);
                    // Show success message
                    setSuccessMessage('Video created and saved to your Video Library!');
                    setTimeout(() => setSuccessMessage(''), 5000);
                } catch (saveErr) {
                    console.error('Failed to save video to library:', saveErr);
                    // Still add to local state even if backend save fails
                    const newVideo = {
                        id: completed.id || completed.jobId || `video-${Date.now()}`,
                        name: videoName,
                        videoUrl: videoUrl,
                        thumbnailUrl: thumbnailUrl,
                        createdAt: new Date().toISOString(),
                        duration: completed.duration || null
                    };
                    setMyVideos(prev => [newVideo, ...prev]);
                    setSuccessMessage('Video created! Added to your Video Library.');
                    setTimeout(() => setSuccessMessage(''), 5000);
                }
            } else {
                // No video URL in response
                console.error('Video completed but no URL found in response:', completed);
                setError('Video generated but download URL not available. Please try again.');
            }

            // Update credits
            setLastCreditDeduction(CREDIT_COSTS.video_reel);
            setCredits((prev) => prev - CREDIT_COSTS.video_reel);
            setTimeout(() => setLastCreditDeduction(null), 3000);

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Video generation failed:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
            } else if (err.code === 'JOB_FAILED') {
                setError(`Video generation failed: ${err.message}${err.creditsRefunded ? ' (credits refunded)' : ''}`);
            } else {
                setError(err.message || 'Video generation failed. Please try again.');
            }
        } finally {
            setIsProcessing(false);
            setProcessingProgress(0);
            setProcessingMessage('');
            setPendingReelRequest(null);
        }
    }, [activeSession, pendingReelRequest, aspectRatio, videoStyle, musicStyle, isProcessing, onRefreshCredits, selectedResolution, selectedProperty?.propertyName]);

    // Refresh photos
    const handleRefresh = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchPhotos();
            setPhotos(data.photos || []);
        } catch (err) {
            console.error('Failed to refresh:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // ============================================================
    // VIDEO MANAGEMENT HANDLERS
    // ============================================================

    // Load user's videos
    const loadMyVideos = useCallback(async () => {
        setIsLoadingVideos(true);
        try {
            const data = await listMyVideos();
            // Handle different response formats
            const apiVideos = data.videos || data.data || data || [];
            const validApiVideos = Array.isArray(apiVideos) ? apiVideos : [];

            // Merge with locally-generated videos (keep local videos that aren't in API response)
            setMyVideos(prev => {
                // Get IDs from API videos
                const apiVideoIds = new Set(validApiVideos.map(v => v.id));
                // Keep local videos that were generated in this session (not yet synced to API)
                const localOnlyVideos = prev.filter(v => !apiVideoIds.has(v.id) && v.id?.startsWith('video-'));
                // Merge: local-only first (most recent), then API videos
                return [...localOnlyVideos, ...validApiVideos];
            });
        } catch (err) {
            console.error('Failed to load videos:', err);
            // On error, keep existing videos rather than clearing
        } finally {
            setIsLoadingVideos(false);
        }
    }, []);

    // Delete a video
    const handleDeleteVideo = useCallback(async (videoId) => {
        if (!videoId || deletingVideoId) return;

        setDeletingVideoId(videoId);
        try {
            await deleteVideo(videoId);
            setMyVideos(prev => prev.filter(v => v.id !== videoId));
        } catch (err) {
            console.error('Failed to delete video:', err);
            setError('Failed to delete video. Please try again.');
        } finally {
            setDeletingVideoId(null);
        }
    }, [deletingVideoId]);

    // Load variation presets
    const loadVariationPresets = useCallback(async () => {
        try {
            const data = await fetchVariationPresets();
            setVariationPresets(data.presets || []);
        } catch (err) {
            console.error('Failed to load variation presets:', err);
        }
    }, []);

    // Open variation modal for a video
    const handleOpenVariationModal = useCallback((video) => {
        setSelectedVideoForVariation(video);
        setSelectedVariations([]);
        setVariationNamePrefix(video.name || '');
        setVariationError(null);
        setShowVariationModal(true);
        // Load presets if not already loaded
        if (variationPresets.length === 0) {
            loadVariationPresets();
        }
    }, [variationPresets.length, loadVariationPresets]);

    // Toggle variation selection
    const handleToggleVariation = useCallback((presetKey) => {
        setSelectedVariations(prev => {
            if (prev.includes(presetKey)) {
                return prev.filter(k => k !== presetKey);
            } else {
                return [...prev, presetKey];
            }
        });
    }, []);

    // Create variations and download
    const handleCreateVariations = useCallback(async () => {
        if (!selectedVideoForVariation || selectedVariations.length === 0) return;

        setIsCreatingVariations(true);
        setVariationError(null);

        try {
            const result = await createVideoVariations(
                selectedVideoForVariation.id,
                selectedVariations,
                variationNamePrefix
            );

            const createdVideos = result.videos || [];

            if (createdVideos.length === 0) {
                throw new Error('No variations were created');
            }

            // Add new variations to the video list
            setMyVideos(prev => [...createdVideos, ...prev]);

            // Download: single file or zip for multiple
            if (createdVideos.length === 1) {
                // Single variation - direct download
                const video = createdVideos[0];
                const link = document.createElement('a');
                link.href = video.videoUrl;
                link.download = `${video.name || 'variation'}.mp4`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                // Multiple variations - download as zip
                await downloadVariationsZip(
                    createdVideos.map(v => ({
                        videoUrl: v.videoUrl,
                        name: v.name || v.variationName || `variation-${v.id}`
                    })),
                    variationNamePrefix || 'video-variations'
                );
            }

            // Close modal and reset
            setShowVariationModal(false);
            setSelectedVideoForVariation(null);
            setSelectedVariations([]);
        } catch (err) {
            console.error('Failed to create variations:', err);
            setVariationError(err.message || 'Failed to create variations. Please try again.');
        } finally {
            setIsCreatingVariations(false);
        }
    }, [selectedVideoForVariation, selectedVariations, variationNamePrefix]);

    // Toggle video selection for pro editing
    const handleToggleVideoForPro = useCallback((video) => {
        setSelectedVideosForPro(prev => {
            const isSelected = prev.some(v => v.id === video.id);
            if (isSelected) {
                return prev.filter(v => v.id !== video.id);
            } else {
                return [...prev, video];
            }
        });
    }, []);

    // Select all videos for pro editing
    const handleSelectAllVideosForPro = useCallback(() => {
        setSelectedVideosForPro([...myVideos]);
    }, [myVideos]);

    // Clear video selection for pro editing
    const handleClearProVideoSelection = useCallback(() => {
        setSelectedVideosForPro([]);
    }, []);

    const closeSendToProModal = useCallback(() => {
        setShowSendToProModal(false);
        setProEditStep('details');
    }, []);

    const openSendToProModal = useCallback(() => {
        setError(null);
        setProEditStep('details');
        setShowSendToProModal(true);
    }, []);

    const proEditPricing = useMemo(() => {
        const selectedPkgs = PRO_EDITING_PACKAGES.filter((pkg) => proEditRequest.selectedPackages.includes(pkg.id));
        const packagesTotal = selectedPkgs.reduce((sum, pkg) => sum + pkg.price, 0);
        const rushOption = RUSH_OPTIONS.find((option) => option.id === proEditRequest.turnaround) || RUSH_OPTIONS[0];
        const rushFee = rushOption.price;
        const total = packagesTotal + rushFee;

        return {
            selectedPkgs,
            packagesTotal,
            rushOption,
            rushFee,
            total,
            totalFormatted: total.toFixed(2)
        };
    }, [PRO_EDITING_PACKAGES, RUSH_OPTIONS, proEditRequest.selectedPackages, proEditRequest.turnaround]);

    const validateProEditDetails = useCallback(() => {
        if (selectedVideosForPro.length === 0) {
            return 'Please select at least one video';
        }
        if (!proEditRequest.instructions.trim()) {
            return 'Please provide editing instructions';
        }
        if (!proEditRequest.contactName.trim()) {
            return 'Please provide your name';
        }
        if (!proEditRequest.contactEmail.trim()) {
            return 'Please provide your email';
        }
        return null;
    }, [selectedVideosForPro.length, proEditRequest.instructions, proEditRequest.contactName, proEditRequest.contactEmail]);

    const validateProEditPayment = useCallback(() => {
        const cardNumberDigits = getDigitsOnly(proEditPayment.cardNumber);
        const expiryDigits = getDigitsOnly(proEditPayment.expiry);
        const cvvDigits = getDigitsOnly(proEditPayment.cvv);
        const zipDigits = getDigitsOnly(proEditPayment.billingZip);

        if (!proEditPayment.cardholderName.trim()) {
            return 'Please provide the cardholder name';
        }
        if (cardNumberDigits.length < 13) {
            return 'Please provide a valid demo card number';
        }
        if (expiryDigits.length !== 4) {
            return 'Please provide a valid expiry date in MM/YY format';
        }

        const expiryMonth = Number(expiryDigits.slice(0, 2));
        if (expiryMonth < 1 || expiryMonth > 12) {
            return 'Please provide a valid expiry month';
        }

        if (cvvDigits.length < 3 || cvvDigits.length > 4) {
            return 'Please provide a valid CVV';
        }
        if (zipDigits.length < 5) {
            return 'Please provide a valid billing ZIP code';
        }
        return null;
    }, [proEditPayment]);

    const handleAdvanceToProPayment = useCallback(() => {
        setError(null);
        setProEditStep('payment');
    }, []);

    // Submit pro editing request
    const handleSubmitProEditingRequest = useCallback(async () => {
        if (selectedVideosForPro.length === 0 || isSubmittingProRequest) return;

        const detailsError = validateProEditDetails();
        if (detailsError) {
            setError(detailsError);
            setProEditStep('details');
            return;
        }

        const paymentError = validateProEditPayment();
        if (paymentError) {
            setError(paymentError);
            setProEditStep('payment');
            return;
        }

        setIsSubmittingProRequest(true);
        setError(null);

        const cardNumberDigits = getDigitsOnly(proEditPayment.cardNumber);
        const expiryDigits = getDigitsOnly(proEditPayment.expiry);

        try {
            await submitProEditingRequest({
                videos: selectedVideosForPro,
                selectedPackages: proEditRequest.selectedPackages,
                packageNames: proEditPricing.selectedPkgs.map((pkg) => pkg.name),
                packagesTotal: proEditPricing.packagesTotal,
                turnaround: proEditRequest.turnaround,
                turnaroundLabel: proEditPricing.rushOption.label,
                rushFee: proEditPricing.rushFee,
                totalPrice: parseFloat(proEditPricing.totalFormatted),
                instructions: proEditRequest.instructions,
                contactName: proEditRequest.contactName,
                contactEmail: proEditRequest.contactEmail,
                contactPhone: proEditRequest.contactPhone,
                additionalNotes: proEditRequest.additionalNotes,
                payment: {
                    demoMode: true,
                    paymentMethod: 'demo_card_form',
                    cardBrand: getCardBrand(cardNumberDigits),
                    last4: cardNumberDigits.slice(-4),
                    maskedCardNumber: maskCardNumber(cardNumberDigits),
                    expiryMonth: expiryDigits.slice(0, 2),
                    expiryYear: expiryDigits.slice(2),
                    cardholderName: proEditPayment.cardholderName.trim(),
                    billingZip: proEditPayment.billingZip.trim(),
                    collectionNote: 'Demo-only payment collection. No live gateway charge or tokenization performed.'
                }
            });

            // Success - close modal, clear selection, show message
            setShowSendToProModal(false);
            setProEditStep('details');
            setSelectedVideosForPro([]);
            setProEditRequest({ ...DEFAULT_PRO_EDIT_REQUEST });
            setProEditPayment({ ...DEFAULT_PRO_EDIT_PAYMENT });
            setSuccessMessage(`Your pro editing request has been submitted! Demo payment captured for $${proEditPricing.totalFormatted}. Our team will contact you shortly.`);
            setTimeout(() => setSuccessMessage(''), 5000);

        } catch (err) {
            console.error('Failed to submit pro editing request:', err);
            setError(err.message || 'Failed to submit request. Please try again.');
        } finally {
            setIsSubmittingProRequest(false);
        }
    }, [
        selectedVideosForPro,
        isSubmittingProRequest,
        proEditRequest,
        proEditPayment,
        proEditPricing,
        validateProEditDetails,
        validateProEditPayment
    ]);

    // Handle video file upload
    const handleVideoUpload = useCallback(async (file) => {
        if (!file) return;

        // Validate file type
        const validTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
        if (!validTypes.includes(file.type)) {
            setVideoUploadError('Invalid file type. Please upload MP4, MOV, WebM, or AVI video files.');
            return;
        }

        // Validate file size (max 500MB)
        const maxSize = 500 * 1024 * 1024;
        if (file.size > maxSize) {
            setVideoUploadError('File too large. Maximum size is 500MB.');
            return;
        }

        setIsUploadingVideo(true);
        setVideoUploadProgress(0);
        setVideoUploadError(null);

        try {
            const result = await uploadVideoFile(file, (progress) => {
                setVideoUploadProgress(progress);
            });

            // Add the uploaded video to the list
            if (result?.data) {
                setMyVideos(prev => [result.data, ...prev]);
            }

            setShowVideoUploadModal(false);
            setVideoUploadProgress(0);
        } catch (err) {
            console.error('Failed to upload video:', err);
            setVideoUploadError(err.message || 'Failed to upload video. Please try again.');
        } finally {
            setIsUploadingVideo(false);
        }
    }, []);

    // ============================================================
    // BATCH STAGING HANDLERS
    // ============================================================

    // Handle batch photo upload
    const handleBatchUpload = useCallback(async (files) => {
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);

        // Validate files
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const invalidFiles = fileArray.filter(f => !validTypes.includes(f.type));
        if (invalidFiles.length > 0) {
            setError(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}. Please upload JPEG, PNG, or WebP images.`);
            return;
        }

        const oversizedFiles = fileArray.filter(f => f.size > 20 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            setError(`Files too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum size is 20MB.`);
            return;
        }

        if (batchPhotos.length + fileArray.length > 20) {
            setError('Maximum 20 photos per batch. Please remove some photos first.');
            return;
        }

        setIsBatchUploading(true);
        setBatchUploadProgress(0);
        setBatchUploadTotal(fileArray.length);
        setError(null);

        try {
            const uploaded = await uploadPhotosForBatch(fileArray, (current, total) => {
                setBatchUploadProgress(current);
            });

            setBatchPhotos(prev => [...prev, ...uploaded]);
        } catch (err) {
            console.error('Batch upload failed:', err);
            setError(err.message || 'Failed to upload photos. Please try again.');
        } finally {
            setIsBatchUploading(false);
            setBatchUploadProgress(0);
            setBatchUploadTotal(0);
        }
    }, [batchPhotos.length]);

    // Handle room type change for batch photo
    const handleBatchRoomTypeChange = useCallback((photoId, roomType) => {
        setBatchPhotos(prev => prev.map(p =>
            p.id === photoId ? { ...p, roomType } : p
        ));
    }, []);

    // Remove photo from batch
    const handleBatchRemovePhoto = useCallback((photoId) => {
        setBatchPhotos(prev => prev.filter(p => p.id !== photoId));
    }, []);

    // Calculate batch cost
    const batchCost = useMemo(() => {
        const costPerPhoto = CREDIT_COSTS[batchJobType] || 250;
        return batchPhotos.length * costPerPhoto;
    }, [batchPhotos.length, batchJobType]);

    // Submit batch job
    const handleBatchSubmit = useCallback(async () => {
        if (batchPhotos.length === 0 || isSubmittingBatch) return;

        // Check credits
        if (credits !== null && credits < batchCost) {
            setShowInsufficientCredits(true);
            return;
        }

        setIsSubmittingBatch(true);
        setError(null);

        try {
            const result = await submitBatchStagingJob(
                batchPhotos,
                batchStyle,
                batchJobType,
                batchPropertyName || null
            );

            // Show confirmation
            setBatchSubmitResult({
                batchId: result.batchId || result.batch_id,
                photoCount: batchPhotos.length,
                totalCredits: result.totalCredits || batchCost,
                estimatedMinutes: result.estimatedMinutes || Math.ceil(batchPhotos.length * 1.5)
            });

            // Update credits
            setLastCreditDeduction(batchCost);
            setCredits(prev => prev - batchCost);
            setTimeout(() => setLastCreditDeduction(null), 3000);

            // Clear batch
            setBatchPhotos([]);
            setBatchPropertyName('');

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Batch submit failed:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
            } else {
                setError(err.message || 'Failed to submit batch job. Please try again.');
            }
        } finally {
            setIsSubmittingBatch(false);
        }
    }, [batchPhotos, batchStyle, batchJobType, batchPropertyName, batchCost, credits, isSubmittingBatch, onRefreshCredits]);

    // Load batch jobs history
    const loadBatchJobs = useCallback(async () => {
        setIsLoadingJobs(true);
        try {
            const data = await fetchBatchJobs();
            setBatchJobs(data.jobs || []);
        } catch (err) {
            console.error('Failed to load batch jobs:', err);
        } finally {
            setIsLoadingJobs(false);
        }
    }, []);

    // View batch job details
    const handleViewBatchDetails = useCallback(async (batchId) => {
        try {
            const details = await fetchBatchJobStatus(batchId);
            setSelectedBatchJob(details);
        } catch (err) {
            console.error('Failed to load batch details:', err);
            setError('Failed to load batch details. Please try again.');
        }
    }, []);

    // Download batch results
    const handleDownloadBatch = useCallback(async (batchId) => {
        try {
            await downloadBatchResults(batchId);
        } catch (err) {
            console.error('Failed to download batch:', err);
            setError('Failed to download batch results. Please try again.');
        }
    }, []);

    // Cancel batch job
    const handleCancelBatch = useCallback((batchId) => {
        setConfirmModal({
            isOpen: true,
            title: 'Cancel Batch Job',
            message: 'Are you sure you want to cancel this batch job? Unprocessed photos will be refunded.',
            confirmLabel: 'Cancel Job',
            cancelLabel: 'Keep Job',
            variant: 'destructive',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    const result = await cancelBatchJob(batchId);
                    // Refresh jobs list
                    loadBatchJobs();
                    // Refresh credits if refunded
                    if (result.creditsRefunded > 0) {
                        setCredits(prev => prev + result.creditsRefunded);
                        if (onRefreshCredits) onRefreshCredits();
                    }
                } catch (err) {
                    console.error('Failed to cancel batch:', err);
                    setError(err.message || 'Failed to cancel batch job. Please try again.');
                }
            },
        });
    }, [loadBatchJobs, onRefreshCredits]);

    // ============================================================
    // SMART BATCH STAGING HANDLERS (AI-powered)
    // ============================================================

    // Handle smart batch photo upload - uses batch analyze endpoint to upload all photos at once
    const handleSmartBatchUpload = useCallback(async (files) => {
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);

        // Validate files
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const invalidFiles = fileArray.filter(f => !validTypes.includes(f.type));
        if (invalidFiles.length > 0) {
            setError(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}. Please upload JPEG, PNG, or WebP images.`);
            return;
        }

        const oversizedFiles = fileArray.filter(f => f.size > 20 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            setError(`Files too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum size is 20MB.`);
            return;
        }

        if (fileArray.length > 50) {
            setError('Maximum 50 photos per batch.');
            return;
        }

        setIsAnalyzingPhotos(true);
        setError(null);
        setBatchUploadTotal(fileArray.length);
        setBatchUploadProgress(0);
        setBatchUploadedCount(0);
        setIsServerProcessing(false);

        try {
            // Upload photos one by one for accurate progress tracking
            const result = await analyzePhotosForSmartBatch(fileArray, (uploadedCount, totalCount, percent) => {
                // Progress callback receives actual per-file progress
                setBatchUploadProgress(percent);
                setBatchUploadedCount(uploadedCount);
            });

            if (!result.photos || result.photos.length === 0) {
                throw new Error('Failed to upload any photos. Please try again.');
            }

            // Store batchId from analyze response
            setSmartBatchId(result.batchId);

            // Map photos with s3Key and create preview URLs
            const uploadedPhotos = result.photos.map((photo, index) => ({
                photoId: photo.photoId || photo.photo_id,
                s3Key: photo.s3Key || photo.s3_key, // Store s3Key for submit
                originalUrl: photo.url || photo.originalUrl || photo.original_url,
                thumbnailUrl: photo.thumbnailUrl || photo.thumbnail_url || photo.url,
                roomType: photo.detectedRoomType || photo.detected_room_type || photo.roomType || 'other',
                category: photo.detectedCategory || photo.detected_category || photo.category || 'indoor',
                confidence: photo.detectionConfidence || photo.detection_confidence || photo.confidence || 0.8,
                previewUrl: fileArray[index] ? URL.createObjectURL(fileArray[index]) : null
            }));

            // Calculate summary
            const indoor = uploadedPhotos.filter(p => p.category === 'indoor').length;
            const outdoor = uploadedPhotos.filter(p => p.category === 'outdoor').length;

            setSmartBatchAnalysis(uploadedPhotos);
            // Note: creditsRequired will be recalculated by useEffect when smartBatchAnalysis changes
            setSmartBatchSummary({
                total: uploadedPhotos.length,
                indoor,
                outdoor,
                creditsRequired: smartBatchConfig.batchFee + (uploadedPhotos.length * smartBatchConfig.creditsPerPhoto),
                batchFee: smartBatchConfig.batchFee,
                creditsPerPhoto: smartBatchConfig.creditsPerPhoto
            });
            setSmartBatchStep(3); // Move to review step
        } catch (err) {
            console.error('Batch upload failed:', err);
            setError(err.message || 'Failed to upload photos. Please try again.');
        } finally {
            setIsAnalyzingPhotos(false);
            setBatchUploadProgress(0);
            setBatchUploadTotal(0);
            setBatchUploadedCount(0);
            setIsServerProcessing(false);
        }
    }, []);

    // Update detected room type for a photo
    const handleSmartPhotoUpdate = useCallback((photoId, updates) => {
        setSmartBatchAnalysis(prev => prev.map(p =>
            p.photoId === photoId ? { ...p, ...updates } : p
        ));
    }, []);

    // Automatically recalculate summary whenever smartBatchAnalysis, config, or video selections change
    useEffect(() => {
        if (smartBatchAnalysis.length > 0) {
            const indoor = smartBatchAnalysis.filter(p => p.category === 'indoor').length;
            const outdoor = smartBatchAnalysis.filter(p => p.category === 'outdoor').length;
            // Calculate: batch fee + (photos * per-photo cost) + (videos * video cost)
            const photoCredits = smartBatchAnalysis.length * smartBatchConfig.creditsPerPhoto;
            const videoCredits = enableVideoGeneration ? selectedVideoPhotos.length * CREDIT_COSTS.video_reel : 0;
            const creditsRequired = smartBatchConfig.batchFee + photoCredits + videoCredits;
            setSmartBatchSummary({
                total: smartBatchAnalysis.length,
                indoor,
                outdoor,
                creditsRequired,
                batchFee: smartBatchConfig.batchFee,
                creditsPerPhoto: smartBatchConfig.creditsPerPhoto,
                videoCredits,
                videoCount: enableVideoGeneration ? selectedVideoPhotos.length : 0
            });
        }
    }, [smartBatchAnalysis, smartBatchConfig, enableVideoGeneration, selectedVideoPhotos]);

    // Auto-select first 3 photos for video generation when video is enabled and no manual selection made
    // This ensures videos are included by default without requiring user to click each photo
    useEffect(() => {
        if (enableVideoGeneration && smartBatchAnalysis.length > 0 && selectedVideoPhotos.length === 0) {
            // Auto-select up to 3 photos with preference: 1 exterior, then indoor rooms
            const autoSelected = [];

            // First, try to add an exterior/outdoor photo
            const exterior = smartBatchAnalysis.find(p => p.category === 'outdoor');
            if (exterior) {
                autoSelected.push({
                    photoId: exterior.photoId,
                    roomType: exterior.roomType,
                    style: 'cinematic',
                    aspectRatio: '9:16'
                });
            }

            // Then add indoor photos (living room, bedroom, kitchen preferred)
            const preferredRooms = ['living_room', 'bedroom', 'kitchen', 'dining_room', 'office'];
            const indoorPhotos = smartBatchAnalysis.filter(p => p.category === 'indoor');

            for (const roomType of preferredRooms) {
                if (autoSelected.length >= 3) break;
                const photo = indoorPhotos.find(p =>
                    p.roomType === roomType && !autoSelected.some(s => s.photoId === p.photoId)
                );
                if (photo) {
                    autoSelected.push({
                        photoId: photo.photoId,
                        roomType: photo.roomType,
                        style: 'cinematic',
                        aspectRatio: '9:16'
                    });
                }
            }

            // Fill remaining slots with any other photos
            for (const photo of smartBatchAnalysis) {
                if (autoSelected.length >= 3) break;
                if (!autoSelected.some(s => s.photoId === photo.photoId)) {
                    autoSelected.push({
                        photoId: photo.photoId,
                        roomType: photo.roomType,
                        style: 'cinematic',
                        aspectRatio: '9:16'
                    });
                }
            }

            if (autoSelected.length > 0) {
                setSelectedVideoPhotos(autoSelected);
            }
        }
    }, [enableVideoGeneration, smartBatchAnalysis]); // Note: intentionally not including selectedVideoPhotos to avoid loop

    // Remove photo from smart batch (summary recalculated by useEffect)
    const handleSmartPhotoRemove = useCallback((photoId) => {
        setSmartBatchAnalysis(prev => prev.filter(p => p.photoId !== photoId));
    }, []);

    // Reorder photos via drag and drop
    const handleSmartPhotoReorder = useCallback((fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex === null || toIndex === null) return;

        setSmartBatchAnalysis(prev => {
            const newPhotos = [...prev];
            const [movedPhoto] = newPhotos.splice(fromIndex, 1);
            newPhotos.splice(toIndex, 0, movedPhoto);
            return newPhotos;
        });
        setDraggedPhotoIndex(null);
    }, []);

    // Submit smart batch for processing
    const handleSmartBatchSubmit = useCallback(async () => {
        if (smartBatchAnalysis.length === 0 || isSubmittingBatch) return;

        // Calculate total: batch fee + (photos * per-photo cost) + (videos * video cost)
        const videoCredits = enableVideoGeneration ? selectedVideoPhotos.length * CREDIT_COSTS.video_reel : 0;
        const totalCredits = smartBatchSummary?.creditsRequired ||
            (smartBatchConfig.batchFee + smartBatchAnalysis.length * smartBatchConfig.creditsPerPhoto + videoCredits);

        // Check credits
        if (credits !== null && credits < totalCredits) {
            setShowInsufficientCredits(true);
            return;
        }

        setIsSubmittingBatch(true);
        setError(null);

        try {
            // Include s3Key from analyze response - backend needs this to retrieve images from S3
            const photosForSubmit = smartBatchAnalysis.map(photo => ({
                photoId: photo.photoId,
                s3Key: photo.s3Key, // Required for backend to locate image in S3
                roomType: photo.roomType,
                category: photo.category,
                confidence: photo.confidence,
                processingType: photo.category === 'indoor' ? 'virtual_staging' : 'sky_replacement'
            }));

            // Build branding settings from userMeta
            const brandingSettings = userMeta ? {
                agentName: userMeta.agentName,
                agentTitle: userMeta.agentTitle,
                agentPhone: userMeta.agentPhone,
                agentEmail: userMeta.agentEmail || userEmail,
                agentPhoto: userMeta.agentPhoto,
                brokerageName: userMeta.brokerageName,
                brokerageLogo: userMeta.brokerageLogo,
                accentColor: userMeta.accentColor,
                template: userMeta.defaultTemplate
            } : null;

            // Debug: Log property info being sent
            console.log('[SmartBatch Submit] Property info being sent:', {
                smartBatchPropertyInfo,
                propertyAddress: smartBatchPropertyInfo.propertyAddress,
                listingPrice: smartBatchPropertyInfo.listingPrice,
                bedrooms: smartBatchPropertyInfo.bedrooms,
                bathrooms: smartBatchPropertyInfo.bathrooms,
                squareFeet: smartBatchPropertyInfo.squareFeet,
                propertyDescription: smartBatchPropertyInfo.propertyDescription
            });

            // Build video settings array with style and aspectRatio for each (max 3)
            const videoSettings = enableVideoGeneration && selectedVideoPhotos.length > 0
                ? selectedVideoPhotos.map(p => ({
                    photoId: p.photoId,
                    style: p.style || 'cinematic',
                    aspectRatio: p.aspectRatio || '9:16'
                }))
                : [];

            const result = await submitSmartBatch(
                photosForSubmit,
                smartInspiration ? null : smartIndoorStyle,
                smartOutdoorMode,
                smartPropertyName || smartBatchPropertyInfo.propertyAddress || null,
                smartInspiration || null,
                smartBatchId, // Pass batchId from analyze response
                smartBatchPropertyInfo, // Property info for gallery
                brandingSettings, // Branding settings from account
                {
                    sendEmail: true,
                    publishGallery: true,
                    includeQrCode: true,
                    recipientEmail: userEmail
                },
                videoSettings // Array of video settings: { photoId, style, aspectRatio }
            );

            // Generate gallery URL - prefer slug from API, fallback to generated slug from address
            const galleryUrl = result.galleryUrl || generateGalleryUrl(
                result.slug,
                result.batchId,
                smartBatchPropertyInfo.propertyAddress
            );

            // Show confirmation - use API response fields when available
            setBatchSubmitResult({
                batchId: result.batchId,
                slug: result.slug || generateSlugFromAddress(smartBatchPropertyInfo.propertyAddress),
                photoCount: result.totalPhotos || smartBatchAnalysis.length,
                indoorCount: result.indoorCount || smartBatchSummary?.indoor || 0,
                outdoorCount: result.outdoorCount || smartBatchSummary?.outdoor || 0,
                totalCredits: result.totalCredits || totalCredits,
                estimatedMinutes: Math.ceil((result.totalPhotos || smartBatchAnalysis.length) * 1.5),
                trackingUrl: result.trackingUrl,  // For optional polling
                message: result.message,
                galleryUrl  // SEO-friendly gallery URL for QR code and sharing
            });

            // Store gallery URL for QR code display
            if (galleryUrl) {
                setSmartBatchResult({
                    batchId: result.batchId,
                    slug: result.slug || generateSlugFromAddress(smartBatchPropertyInfo.propertyAddress),
                    galleryUrl,
                    estimatedCompletion: result.estimatedCompletion
                });
            }

            // Update property info to ensure it's persisted to the database
            // This calls PATCH /api/staging/properties/{batchId}
            try {
                await updatePropertyInfo(result.batchId, smartBatchPropertyInfo);
                console.log('[SmartBatch] Property info updated successfully');
            } catch (propError) {
                console.warn('[SmartBatch] Failed to update property info:', propError);
                // Don't fail the whole submission if this fails
            }

            // Update credits
            setLastCreditDeduction(totalCredits);
            setCredits(prev => prev - totalCredits);
            setTimeout(() => setLastCreditDeduction(null), 3000);

            // Reset smart batch state
            setSmartBatchStep(1);
            setSmartBatchAnalysis([]);
            setSmartBatchSummary(null);
            setSmartBatchId(null);
            setSmartPropertyName('');
            setSmartStyleTab('styles');
            setSmartInspiration(null);
            setSmartInspirationCategory('all');
            setSmartBatchPropertyInfo({
                propertyAddress: '',
                listingPrice: '',
                bedrooms: '',
                bathrooms: '',
                squareFeet: '',
                propertyDescription: ''
            });
            setSmartBatchResult(null);

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Smart batch submit failed:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
            } else {
                setError(err.message || 'Failed to submit batch. Please try again.');
            }
        } finally {
            setIsSubmittingBatch(false);
        }
    }, [smartBatchAnalysis, smartBatchSummary, smartBatchConfig, smartBatchId, smartIndoorStyle, smartOutdoorMode, smartInspiration, smartPropertyName, smartBatchPropertyInfo, credits, isSubmittingBatch, onRefreshCredits, userMeta, userEmail, enableVideoGeneration, selectedVideoPhotos]);

    // Reset smart batch to start over
    const handleSmartBatchReset = useCallback(() => {
        // Clean up preview URLs
        smartBatchAnalysis.forEach(p => {
            if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        });
        setSmartBatchStep(1);
        setSmartBatchAnalysis([]);
        setSmartBatchSummary(null);
        setSmartBatchId(null);
        setSmartPropertyName('');
        setSmartStyleTab('styles');
        setSmartInspiration(null);
        setSmartInspirationCategory('all');
        setSmartBatchPropertyInfo({
            propertyAddress: '',
            listingPrice: '',
            bedrooms: '',
            bathrooms: '',
            squareFeet: '',
            propertyDescription: ''
        });
        setSmartBatchResult(null);
        setSelectedVideoPhotos([]);
        setEnableVideoGeneration(true);
    }, [smartBatchAnalysis]);

    // Toggle photo selection for video generation (max 3 photos)
    const toggleVideoPhotoSelection = useCallback((photoId, roomType) => {
        setSelectedVideoPhotos(prev => {
            const isSelected = prev.some(p => p.photoId === photoId);
            if (isSelected) {
                // Remove from selection
                return prev.filter(p => p.photoId !== photoId);
            } else if (prev.length < 3) {
                // Add to selection with default style and aspect ratio
                return [...prev, {
                    photoId,
                    roomType,
                    style: 'cinematic',      // Default video style
                    aspectRatio: '9:16'      // Default to vertical (Reels)
                }];
            }
            return prev; // Already at max, don't add
        });
    }, []);

    // Update individual video settings (style or aspect ratio)
    const updateVideoPhotoSettings = useCallback((photoId, updates) => {
        setSelectedVideoPhotos(prev =>
            prev.map(p => p.photoId === photoId ? { ...p, ...updates } : p)
        );
    }, []);

    // ============================================================
    // PROPERTY MANAGEMENT HANDLERS
    // ============================================================

    // Load properties list
    const loadProperties = useCallback(async () => {
        setIsLoadingProperties(true);
        try {
            const data = await listProperties({ limit: 50 });
            setProperties(data.properties || []);
        } catch (err) {
            console.error('Failed to load properties:', err);
            setError('Failed to load properties. Please try again.');
        } finally {
            setIsLoadingProperties(false);
        }
    }, []);

    // Load properties when switching to properties tab
    useEffect(() => {
        if (activeWorkspace === 'bulk' && bulkView === 'properties' && hasAccess) {
            loadProperties();
        }
    }, [activeWorkspace, bulkView, hasAccess, loadProperties]);

    // Create new property
    const handleCreateProperty = useCallback(async () => {
        if (!newPropertyName.trim()) {
            setError('Please enter a property name.');
            return;
        }

        setIsCreatingProperty(true);
        setError(null);

        try {
            const property = await createProperty({
                propertyName: newPropertyName.trim(),
                propertyAddress: newPropertyAddress.trim() || null,
                indoorStyle: smartIndoorStyle,
                outdoorMode: smartOutdoorMode
            });

            // Navigate to property detail
            setSelectedProperty(property);
            setPropertyContentTab('photos'); // Reset to photos tab for new property
            setNewPropertyName('');
            setNewPropertyAddress('');
            await loadProperties();
        } catch (err) {
            console.error('Failed to create property:', err);
            setError(err.message || 'Failed to create property. Please try again.');
        } finally {
            setIsCreatingProperty(false);
        }
    }, [newPropertyName, newPropertyAddress, smartIndoorStyle, smartOutdoorMode, loadProperties]);

    // Load property details
    const handleViewProperty = useCallback(async (batchId) => {
        try {
            const property = await getProperty(batchId);
            setSelectedProperty(property);
            setPropertyContentTab('photos'); // Reset to photos tab when viewing new property
        } catch (err) {
            console.error('Failed to load property:', err);
            setError('Failed to load property details.');
        }
    }, []);

    // Add photos to property
    const handleAddPhotosToProperty = useCallback(async (files) => {
        if (!selectedProperty || !files || files.length === 0) return;

        const fileArray = Array.from(files);

        // Validate files
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const invalidFiles = fileArray.filter(f => !validTypes.includes(f.type));
        if (invalidFiles.length > 0) {
            setError(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}`);
            return;
        }

        setIsAddingPhotos(true);
        setError(null);

        try {
            const result = await addPhotosToProperty(selectedProperty.batchId, fileArray);
            setSelectedProperty(prev => ({
                ...prev,
                ...result,
                photos: result.photos || prev.photos
            }));
            await loadProperties();
        } catch (err) {
            console.error('Failed to add photos:', err);
            setError(err.message || 'Failed to add photos.');
        } finally {
            setIsAddingPhotos(false);
        }
    }, [selectedProperty, loadProperties]);

    // Remove photo from property
    const handleRemovePhotoFromProperty = useCallback(async (photoId) => {
        if (!selectedProperty) return;

        try {
            const result = await removePhotoFromProperty(selectedProperty.batchId, photoId);
            setSelectedProperty(prev => ({
                ...prev,
                ...result,
                photos: prev.photos.filter(p => p.photoId !== photoId)
            }));
        } catch (err) {
            console.error('Failed to remove photo:', err);
            setError('Failed to remove photo.');
        }
    }, [selectedProperty]);

    // Open batch photo for editing with full UI (before/after, download, video, chat)
    const handleOpenBatchPhotoForEdit = useCallback((photo) => {
        // Debug: log full photo object to see ALL fields available
        console.log('[Refine] handleOpenBatchPhotoForEdit called with photo:', JSON.stringify(photo, null, 2));

        // Check all possible field names for processed/staged URL (the "after" image)
        const processedUrl = photo.processedUrl || photo.processed_url || photo.stagedUrl || photo.staged_url || photo.outputUrl || photo.output_url || photo.resultUrl || photo.result_url;
        // Check all possible field names for original/source URL (the "before" image - before staging)
        // Note: Do NOT use photo.url as fallback since it's often the processed URL
        const originalUrl = photo.inputUrl || photo.input_url || photo.originalUrl || photo.original_url || photo.sourceUrl || photo.source_url || photo.uploadedUrl || photo.uploaded_url || photo.rawUrl || photo.raw_url;

        console.log('[Refine] Resolved URLs:', { processedUrl, originalUrl });

        if (!processedUrl) {
            // Photo not yet processed, just show preview in lightbox
            console.log('[Refine] No processedUrl found, showing lightbox');
            setEnlargedPropertyPhoto(photo);
            return;
        }

        console.log('[Refine] Opening batch photo edit modal');

        // Close any open lightbox first to prevent z-index conflicts
        setEnlargedPropertyPhoto(null);

        // Set up batch photo session immediately with photo data (no API call needed)
        const sessionData = {
            photo: photo,
            originalUrl: originalUrl,
            processedUrl: processedUrl,
            roomType: photo.roomType || photo.room_type || photo.detectedRoomType || photo.detected_room_type || 'room',
            category: photo.category || photo.detectedCategory || photo.detected_category || 'indoor',
            photoId: photo.photoId || photo.photo_id || photo.id,
            session_id: null // Will be created when user starts chatting
        };
        console.log('[Refine] Setting batchPhotoSession:', sessionData);
        setBatchPhotoSession(sessionData);

        // Initialize with the processed image
        setBatchPhotoMessages([{
            role: 'assistant',
            content: 'Here is your staged photo. You can refine it by describing what changes you\'d like.',
            imageUrl: processedUrl,
            originalUrl: originalUrl,
            version: 1,
            timestamp: new Date().toISOString()
        }]);

        setBatchPhotoVersions([{
            version: 1,
            imageUrl: processedUrl,
            instruction: 'Initial staging',
            createdAt: new Date().toISOString()
        }]);
    }, []);

    // Send refinement for batch photo (accepts optional directInstruction for quick directions)
    const handleBatchPhotoRefinement = useCallback(async (directInstruction = null) => {
        // Use direct instruction (from quick direction click) or input field
        const instruction = directInstruction?.trim() || batchPhotoInput.trim();
        if (!batchPhotoSession || !instruction || isGeneratingBatchRefinement) return;

        setBatchPhotoInput('');
        setIsGeneratingBatchRefinement(true);

        // Add user message
        const userMessage = {
            role: 'user',
            content: instruction,
            timestamp: new Date().toISOString()
        };
        setBatchPhotoMessages(prev => [...prev, userMessage]);

        try {
            let sessionId = batchPhotoSession.session_id;

            // Create session if not exists - use useStaged:true for batch photos that are already staged
            if (!sessionId) {
                const session = await createStagingSession(
                    batchPhotoSession.photoId,
                    batchPhotoSession.roomType || 'other',
                    'modern',
                    null,       // customPrompt
                    '1k',       // resolution - use 1K default for batch refinements
                    null,       // inspirationData
                    true        // useStaged - use already-staged image as starting point
                );
                sessionId = session.session_id || session.sessionId;
                setBatchPhotoSession(prev => ({ ...prev, session_id: sessionId }));
            }

            const result = await sendRefinement(sessionId, instruction);

            // Add assistant response with new image
            const assistantMessage = {
                role: 'assistant',
                content: 'Here\'s your refined image.',
                imageUrl: result.imageUrl || result.image_url,
                originalUrl: batchPhotoSession.originalUrl,
                version: result.version,
                timestamp: new Date().toISOString()
            };
            setBatchPhotoMessages(prev => [...prev, assistantMessage]);

            // Update versions
            setBatchPhotoVersions(prev => [...prev, {
                version: result.version,
                imageUrl: result.imageUrl || result.image_url,
                instruction,
                createdAt: new Date().toISOString()
            }]);

            // Deduct credits
            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Batch photo refinement failed:', err);
            setError(err.message || 'Failed to refine image. Please try again.');
            // Remove the user message on failure
            setBatchPhotoMessages(prev => prev.slice(0, -1));
        } finally {
            setIsGeneratingBatchRefinement(false);
        }
    }, [batchPhotoSession, batchPhotoInput, isGeneratingBatchRefinement, onRefreshCredits]);

    // Close batch photo editing modal
    const handleCloseBatchPhotoEdit = useCallback(() => {
        setBatchPhotoSession(null);
        setBatchPhotoMessages([]);
        setBatchPhotoInput('');
        setBatchPhotoVersions([]);
        setShowBatchVersionHistory(false);
    }, []);

    // Toggle photo selection for batch video
    const handleTogglePhotoForVideo = useCallback((photo) => {
        const photoId = photo.photoId || photo.photo_id || photo.id;
        setSelectedPhotosForVideo(prev => {
            const isSelected = prev.some(p => (p.photoId || p.photo_id || p.id) === photoId);
            if (isSelected) {
                return prev.filter(p => (p.photoId || p.photo_id || p.id) !== photoId);
            } else {
                return [...prev, photo];
            }
        });
    }, []);

    // Select all photos for video
    const handleSelectAllPhotosForVideo = useCallback(() => {
        if (!selectedProperty?.photos) return;
        const processedPhotos = selectedProperty.photos.filter(p =>
            p.processedUrl || p.processed_url || p.stagedUrl || p.staged_url
        );
        setSelectedPhotosForVideo(processedPhotos);
    }, [selectedProperty]);

    // Clear video selection
    const handleClearVideoSelection = useCallback(() => {
        setSelectedPhotosForVideo([]);
    }, []);

    // Open batch video modal
    const handleOpenBatchVideoModal = useCallback(() => {
        if (selectedPhotosForVideo.length === 0) {
            setError('Please select at least one photo for the video');
            return;
        }
        setShowBatchVideoModal(true);
    }, [selectedPhotosForVideo]);

    // Submit batch video job - async processing with email notification
    // User can continue working while videos are generated in background
    const handleSubmitBatchVideoJob = useCallback(async () => {
        if (selectedPhotosForVideo.length === 0) {
            setError('No photos selected for video generation');
            setShowBatchVideoModal(false);
            return;
        }

        if (isGeneratingBatchVideo) {
            return;
        }

        setIsGeneratingBatchVideo(true);
        setError(null);

        try {
            // Submit batch job - backend processes asynchronously
            const result = await submitBatchVideoJob(
                selectedPhotosForVideo,
                batchVideoAspectRatio,
                batchVideoStyle,
                batchVideoMusic,
                selectedProperty?.propertyName || null
            );


            // Close modal and clear selection
            setShowBatchVideoModal(false);
            setSelectedPhotosForVideo([]);

            // Show success message using API response
            const videoCount = result.totalCount || selectedPhotosForVideo.length;
            const message = result.message || `${videoCount} video${videoCount > 1 ? 's' : ''} queued for generation. You'll receive an email when they're ready!`;
            setSuccessMessage(message);

            // Clear success message after 5 seconds
            setTimeout(() => setSuccessMessage(''), 5000);

            // Update credits locally if provided
            if (result.creditsCharged) {
                setLastCreditDeduction(result.creditsCharged);
                setCredits(prev => prev !== null ? prev - result.creditsCharged : prev);
                setTimeout(() => setLastCreditDeduction(null), 3000);
            }

            // Refresh credits from server
            if (onRefreshCredits) {
                onRefreshCredits();
            }

        } catch (err) {
            console.error('Batch video submission failed:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
                setShowBatchVideoModal(false);
            } else {
                setError(err.message || 'Failed to submit video batch. Please try again.');
            }
        } finally {
            setIsGeneratingBatchVideo(false);
        }
    }, [selectedPhotosForVideo, batchVideoAspectRatio, batchVideoStyle, batchVideoMusic, isGeneratingBatchVideo, selectedProperty, onRefreshCredits]);

    // Update property settings
    const handleUpdateProperty = useCallback(async (updates) => {
        if (!selectedProperty) return;

        try {
            const result = await updateProperty(selectedProperty.batchId, updates);
            setSelectedProperty(prev => ({ ...prev, ...result }));
            await loadProperties();
        } catch (err) {
            console.error('Failed to update property:', err);
            setError('Failed to update property.');
        }
    }, [selectedProperty, loadProperties]);

    // Rename property from list view
    const handleRenameProperty = useCallback(async (batchId, newName) => {
        if (!newName.trim()) {
            setEditingPropertyId(null);
            return;
        }
        try {
            await updateProperty(batchId, { propertyName: newName.trim() });
            setProperties(prev => prev.map(p =>
                p.batchId === batchId ? { ...p, propertyName: newName.trim() } : p
            ));
            setEditingPropertyId(null);
            setEditingPropertyName('');
        } catch (err) {
            console.error('Failed to rename property:', err);
            setError('Failed to rename property.');
        }
    }, []);

    // Filtered and sorted properties
    const filteredProperties = useMemo(() => {
        let result = [...properties];

        // Filter by search term
        if (propertySearchTerm.trim()) {
            const search = propertySearchTerm.toLowerCase();
            result = result.filter(p =>
                (p.propertyName || '').toLowerCase().includes(search) ||
                (p.propertyAddress || '').toLowerCase().includes(search)
            );
        }

        // Sort
        result.sort((a, b) => {
            if (propertySortOrder === 'newest') {
                return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            } else if (propertySortOrder === 'oldest') {
                return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
            } else if (propertySortOrder === 'name') {
                return (a.propertyName || '').localeCompare(b.propertyName || '');
            }
            return 0;
        });

        return result;
    }, [properties, propertySearchTerm, propertySortOrder]);

    // Delete property
    const handleDeleteProperty = useCallback((batchId) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Property',
            message: 'Are you sure you want to delete this property and all its photos? This cannot be undone.',
            confirmLabel: 'Delete',
            cancelLabel: 'Keep',
            variant: 'destructive',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await deleteProperty(batchId);
                    setSelectedProperty(null);
                    await loadProperties();
                } catch (err) {
                    console.error('Failed to delete property:', err);
                    setError(err.message || 'Failed to delete property.');
                }
            },
        });
    }, [loadProperties]);

    // Process property batch
    const handleProcessProperty = useCallback(async () => {
        if (!selectedProperty || selectedProperty.totalPhotos === 0) return;

        // Calculate: batch fee + (photos * per-photo cost)
        const totalCredits = smartBatchConfig.batchFee + (selectedProperty.totalPhotos * smartBatchConfig.creditsPerPhoto);

        // Check credits
        if (credits !== null && credits < totalCredits) {
            setShowInsufficientCredits(true);
            return;
        }

        setIsProcessingProperty(true);
        setError(null);

        try {
            const result = await processProperty(selectedProperty.batchId);

            // Show confirmation
            setBatchSubmitResult({
                batchId: selectedProperty.batchId,
                photoCount: selectedProperty.totalPhotos,
                indoorCount: selectedProperty.indoorCount || 0,
                outdoorCount: selectedProperty.outdoorCount || 0,
                totalCredits: result.totalCredits || totalCredits
            });

            // Update credits
            setLastCreditDeduction(totalCredits);
            setCredits(prev => prev - totalCredits);
            setTimeout(() => setLastCreditDeduction(null), 3000);

            // Clear selection and reload
            setSelectedProperty(null);
            await loadProperties();

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Failed to process property:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
            } else {
                setError(err.message || 'Failed to start processing.');
            }
        } finally {
            setIsProcessingProperty(false);
        }
    }, [selectedProperty, credits, loadProperties, onRefreshCredits, smartBatchConfig]);

    // Download property results as ZIP via backend API
    const handleDownloadPropertyResults = useCallback(async () => {
        if (!selectedProperty || isDownloadingZip) return;

        setIsDownloadingZip(true);
        setDownloadProgress(0);
        setDownloadMessage('Preparing download...');
        setError(null);

        try {
            setDownloadMessage('Generating ZIP file on server...');
            setDownloadProgress(30);

            // Use backend API which handles S3 downloads server-side (no CORS issues)
            await downloadSmartBatchResults(selectedProperty.batchId, 'properties');

            setDownloadProgress(100);
            setDownloadMessage('Download complete!');

        } catch (err) {
            console.error('Failed to download:', err);
            setError('Failed to download. Please ensure the backend download endpoint is configured.');
        } finally {
            setTimeout(() => {
                setIsDownloadingZip(false);
                setDownloadProgress(0);
                setDownloadMessage('');
            }, 500);
        }
    }, [selectedProperty, isDownloadingZip]);

    // ============================================================
    // SESSION-BASED STAGING HANDLERS (ChatGPT-like)
    // ============================================================

    // Load sessions list
    const loadSessions = useCallback(async () => {
        setIsLoadingSessions(true);
        try {
            const data = await listStagingSessions();
            setSessions(data.sessions || []);
        } catch (err) {
            console.error('Failed to load sessions:', err);
        } finally {
            setIsLoadingSessions(false);
        }
    }, []);

    // Load sessions when switching to sessions tab
    useEffect(() => {
        if (activeWorkspace === 'sessions' || activeSession) {
            loadSessions();
        }
    }, [activeWorkspace, activeSession, hasAccess, loadSessions]);

    // Create new session from selected photo
    const handleCreateSession = useCallback(async () => {
        if (!selectedPhoto || isProcessing) return;

        setIsProcessing(true);
        setProcessingMessage('Preparing session...');
        setProcessingProgress(5);
        setProcessingType('staging');
        setError(null);

        // Smooth asymptotic progress simulation - never stops, just slows down
        let simulatedProgress = 5;
        const progressInterval = setInterval(() => {
            // Asymptotic approach: increment decreases as we approach 99%
            // This creates smooth, continuous progress that never quite stops
            const remaining = 99 - simulatedProgress;
            const increment = Math.max(0.1, remaining * 0.03 + Math.random() * 0.5);
            simulatedProgress = Math.min(simulatedProgress + increment, 98.5);
            const roundedProgress = Math.round(simulatedProgress);
            setProcessingProgress(roundedProgress);

            // More granular messages for better feedback
            const newMessage =
                roundedProgress < 15 ? 'Preparing session...' :
                roundedProgress < 30 ? 'Analyzing room layout...' :
                roundedProgress < 45 ? 'Identifying furniture zones...' :
                roundedProgress < 60 ? 'Generating staging design...' :
                roundedProgress < 75 ? 'Rendering high-quality preview...' :
                roundedProgress < 85 ? 'Applying style details...' :
                roundedProgress < 92 ? 'Enhancing textures...' :
                roundedProgress < 96 ? 'Final touches...' :
                'Almost there...';
            setProcessingMessage(newMessage);
        }, 150); // Faster updates for smoother feel

        // Build room type display name
        const roomType = selectedPhoto.roomType || 'living_room';
        const roomDisplayName = roomType === 'other' && selectedPhoto.customRoomName
            ? selectedPhoto.customRoomName
            : ROOM_TYPES.find(r => r.id === roomType)?.name || 'Room';

        // Build custom prompt including custom room name if applicable
        let finalCustomPrompt = customPrompt || '';
        if (roomType === 'other' && selectedPhoto.customRoomName && !finalCustomPrompt.includes(selectedPhoto.customRoomName)) {
            finalCustomPrompt = `This is a ${selectedPhoto.customRoomName}. ${finalCustomPrompt}`.trim();
        }

        // Handle inspiration-based styling
        let finalStyle = selectedStyle;
        let inspirationData = null;
        if (selectedInspiration) {
            // When using inspiration, inject the prompt keywords
            finalCustomPrompt = `${selectedInspiration.promptKeywords}. ${finalCustomPrompt}`.trim();
            finalStyle = 'inspiration'; // Special style type for backend
            inspirationData = {
                id: selectedInspiration.id,
                name: selectedInspiration.name,
                category: selectedInspiration.category,
                promptKeywords: selectedInspiration.promptKeywords
            };
        }

        try {
            // Log resolution being sent to backend for debugging

            const session = await createStagingSession(
                selectedPhoto.id,
                roomType,
                finalStyle,
                finalCustomPrompt || null,
                selectedResolution,
                inspirationData // Pass inspiration data to backend
            );

            clearInterval(progressInterval);
            setProcessingProgress(100);
            setProcessingMessage('Complete!');

            // Set as active session with original photo URL for comparison
            setActiveSession({
                ...session,
                original_photo_url: selectedPhoto.originalUrl || selectedPhoto.url
            });
            const styleDescription = selectedInspiration
                ? `${selectedInspiration.name} inspired style`
                : `${formatStyleName(selectedStyle)} style`;
            setSessionMessages(session.messages || [
                {
                    message_id: session.message?.message_id,
                    role: MESSAGE_ROLES.USER,
                    content: `Stage this ${roomDisplayName} in ${styleDescription}${customPrompt ? `: ${customPrompt}` : ''}`,
                    version: 1,
                    created_at: session.created_at
                },
                {
                    message_id: session.message?.message_id + '_response',
                    role: MESSAGE_ROLES.ASSISTANT,
                    content: 'Version 1',
                    image_url: session.message?.image_url || session.message?.result_url,
                    image_url_hd: session.message?.image_url_hd || session.message?.result_url_hd,
                    version: 1,
                    created_at: session.created_at
                }
            ]);

            // Switch to sessions tab with chat view
            setActiveWorkspace('create');
            setCustomPrompt('');

            // Update credits (base + resolution extra)
            const resolutionExtra = RESOLUTION_OPTIONS.find(r => r.value === selectedResolution)?.extraCredits || 0;
            const cost = CREDIT_COSTS.full_staging + resolutionExtra;
            setLastCreditDeduction(cost);
            setCredits(prev => prev - cost);
            setTimeout(() => setLastCreditDeduction(null), 3000);

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            clearInterval(progressInterval);
            console.error('Failed to create session:', err);
            if (err.code === 'SUBSCRIPTION_REQUIRED') {
                setShowInsufficientCredits(true);
            } else {
                setError(err.message || 'Failed to create staging session. Please try again.');
            }
        } finally {
            // Small delay before hiding progress to show completion
            setTimeout(() => {
                setIsProcessing(false);
                setProcessingProgress(0);
                setProcessingMessage('');
            }, 500);
        }
    }, [selectedPhoto, selectedStyle, selectedInspiration, selectedResolution, customPrompt, isProcessing, onRefreshCredits]);

    // Open existing session
    const handleOpenSession = useCallback(async (sessionId) => {
        setIsLoadingSessions(true);
        setError(null);

        try {
            const session = await getStagingSession(sessionId, true);
            setActiveSession(session);
            setSessionMessages(session.messages || []);
            setActiveWorkspace('create');

            // Push state to browser history for back button support
            window.history.pushState({ sessionId: session.session_id }, '', window.location.href);

            // Find and set the original photo for before/after comparison
            if (session.original_photo_id) {
                const originalPhoto = photos.find(p => p.id === session.original_photo_id);
                if (originalPhoto) {
                    setSelectedPhoto(originalPhoto);
                } else if (session.original_photo_url) {
                    // Fallback: create a minimal photo object with the URL from session
                    setSelectedPhoto({
                        id: session.original_photo_id,
                        originalUrl: session.original_photo_url,
                        url: session.original_photo_url,
                        roomType: session.room_type || 'other'
                    });
                }
            } else if (session.original_photo_url) {
                // If no photo ID but we have the URL, use it
                setSelectedPhoto({
                    id: 'session-original',
                    originalUrl: session.original_photo_url,
                    url: session.original_photo_url,
                    roomType: session.room_type || 'other'
                });
            }
        } catch (err) {
            console.error('Failed to open session:', err);
            setError(err.message || 'Failed to open session. Please try again.');
        } finally {
            setIsLoadingSessions(false);
        }
    }, [photos]);

    // Send refinement instruction
    const handleSendRefinement = useCallback(async () => {
        if (!activeSession || !refinementInput.trim() || isGeneratingRefinement) return;

        const instruction = refinementInput.trim();
        setIsGeneratingRefinement(true);
        setError(null);

        // Optimistically add user message
        const tempUserMsg = {
            message_id: `temp_${Date.now()}`,
            role: MESSAGE_ROLES.USER,
            content: instruction,
            created_at: new Date().toISOString(),
            branch_from: branchFromVersion
        };
        setSessionMessages(prev => [...prev, tempUserMsg]);
        setRefinementInput('');

        try {
            const result = await sendRefinement(
                activeSession.session_id,
                instruction,
                branchFromVersion
            );

            // Add assistant response (include HD URL for high-quality download)
            const assistantMsg = {
                message_id: result.message_id,
                role: MESSAGE_ROLES.ASSISTANT,
                content: `Version ${result.version}`,
                image_url: result.image_url || result.result_url,
                image_url_hd: result.image_url_hd || result.result_url_hd,
                version: result.version,
                parent_version: result.parent_version,
                created_at: result.created_at
            };

            // Replace temp user message and add assistant message
            setSessionMessages(prev => [
                ...prev.slice(0, -1),
                { ...tempUserMsg, message_id: `user_${result.message_id}`, version: result.version, branch_from: branchFromVersion },
                assistantMsg
            ]);

            // Update active session version
            setActiveSession(prev => ({
                ...prev,
                current_version: result.version
            }));

            // Reset branch version
            setBranchFromVersion(null);

            // Update credits
            const cost = CREDIT_COSTS.full_staging;
            setLastCreditDeduction(cost);
            setCredits(prev => prev - cost);
            setTimeout(() => setLastCreditDeduction(null), 3000);

            if (onRefreshCredits) onRefreshCredits();

        } catch (err) {
            console.error('Failed to send refinement:', err);
            // Remove optimistic message on error
            setSessionMessages(prev => prev.slice(0, -1));
            setRefinementInput(instruction);
            setError(err.message || 'Failed to generate refinement. Please try again.');
        } finally {
            setIsGeneratingRefinement(false);
        }
    }, [activeSession, refinementInput, branchFromVersion, isGeneratingRefinement, onRefreshCredits]);

    // Load version history for branching
    const handleLoadVersionHistory = useCallback(async () => {
        if (!activeSession) return;

        try {
            const data = await getVersionHistory(activeSession.session_id);
            setSessionVersions(data.versions || []);
            setShowVersionHistory(true);
        } catch (err) {
            console.error('Failed to load versions:', err);
            setError('Failed to load version history.');
        }
    }, [activeSession]);

    // Complete session
    const handleCompleteSession = useCallback(async () => {
        if (!activeSession) return;

        try {
            await completeStagingSession(activeSession.session_id);
            setActiveSession(prev => ({ ...prev, status: SESSION_STATUS.COMPLETED }));
            loadSessions(); // Refresh list
        } catch (err) {
            console.error('Failed to complete session:', err);
            setError('Failed to complete session.');
        }
    }, [activeSession, loadSessions]);

    // Delete session
    const handleDeleteSession = useCallback((sessionId) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Session',
            message: 'Are you sure you want to delete this session? This cannot be undone.',
            confirmLabel: 'Delete',
            cancelLabel: 'Cancel',
            variant: 'destructive',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await deleteStagingSession(sessionId);
                    if (activeSession?.session_id === sessionId) {
                        setActiveSession(null);
                        setSessionMessages([]);
                    }
                    loadSessions(); // Refresh list
                } catch (err) {
                    console.error('Failed to delete session:', err);
                    setError('Failed to delete session.');
                }
            },
        });
    }, [activeSession, loadSessions]);

    // Close active session and go back to list
    const handleCloseSession = useCallback(() => {
        setActiveSession(null);
        setSessionMessages([]);
        setShowVersionHistory(false);
        setBranchFromVersion(null);
        // Go back to Sessions tab
        setActiveWorkspace('sessions');
        loadSessions();
    }, [loadSessions]);

    // Handle browser back button for session navigation
    useEffect(() => {
        const handlePopState = () => {
            // If we're in a session, close it when user clicks back
            if (activeSession) {
                setActiveSession(null);
                setSessionMessages([]);
                setShowVersionHistory(false);
                setBranchFromVersion(null);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [activeSession]);

    // Show upgrade prompt if no access
    if (!hasAccess) {
        return <UpgradePrompt onUpgrade={onUpgradePlan} />;
    }


    return (
        <div className="flex-1 h-full flex flex-col bg-gray-50 dark:bg-slate-950 overflow-hidden" style={{ minHeight: 0 }}>
            <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-800 bg-white/95 px-4 py-3 backdrop-blur dark:bg-slate-900/95">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex rounded-2xl bg-slate-100 p-1 dark:bg-slate-800">
                            <button
                                onClick={() => setActiveWorkspace('create')}
                                className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                                    activeWorkspace === 'create'
                                        ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700'
                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                Create
                            </button>
                            <button
                                onClick={() => {
                                    setActiveWorkspace('library');
                                    setLibraryView('photos');
                                    if (activeSession) handleCloseSession();
                                }}
                                className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                                    activeWorkspace === 'library'
                                        ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700'
                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                Library
                            </button>
                            <button
                                onClick={() => {
                                    setActiveWorkspace('sessions');
                                    if (activeSession) handleCloseSession();
                                    loadSessions();
                                }}
                                className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                                    activeWorkspace === 'sessions'
                                        ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700'
                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                Sessions
                            </button>
                            {(featureFlags.isRpStarter || featureFlags.isRpPro || featureFlags.isRpUltra || featureFlags.isUltra) && (
                                <button
                                    onClick={() => setActiveWorkspace('bulk')}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                                        activeWorkspace === 'bulk'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700'
                                            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                >
                                    Agents
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <CreditsDisplay credits={credits} lastDeduction={lastCreditDeduction} />
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-all disabled:opacity-50"
                            title="Refresh photos"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Flow Hero Banner - Shows on Create workspace when no session active */}
            {activeWorkspace === 'create' && !activeSession && !selectedPhoto && (
                <div className="relative overflow-hidden">
                    <img
                        src={flowCoverPhoto}
                        alt="Flow - AI Virtual Staging"
                        className="w-full h-48 md:h-64 object-cover object-center"
                    />
                    {/* Subtle gradient for seamless blend with content below */}
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950 to-transparent" />
                </div>
            )}

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden" style={{ minHeight: 0 }}>
                {/* ============================================================ */}
                {/* SMART BATCH - QUICK WORKFLOW */}
                {/* ============================================================ */}
                {activeWorkspace === 'bulk' && bulkView === 'quick' && (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Agents Sub-tabs: Photo Agent / Properties / Analytics */}
                        <div className="px-6 pt-6 pb-0">
                            <div className="flex rounded-2xl bg-slate-100 p-1 dark:bg-slate-800 w-fit">
                                <button
                                    onClick={() => { setBulkView('quick'); handleSmartBatchReset(); }}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                        bulkView === 'quick'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <Zap className="h-4 w-4" />
                                    Photo Agent
                                </button>
                                <button
                                    onClick={() => { setBulkView('properties'); setSelectedProperty(null); }}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                        bulkView === 'properties'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <Building2 className="h-4 w-4" />
                                    Properties
                                </button>
                                {hasAnalyticsAccess && (
                                    <button
                                        onClick={() => setBulkView('analytics')}
                                        className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                            bulkView === 'analytics'
                                                ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                        }`}
                                    >
                                        <BarChart3 className="h-4 w-4" />
                                        Analytics
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Error display */}
                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    key="bulk-error-banner"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="m-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3"
                                >
                                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
                                    <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                                        <X className="w-4 h-4" />
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="flex-1 flex min-h-0">
                            {/* Left side - Upload and Photo Grid */}
                            <div className="flex-1 flex flex-col p-6 overflow-hidden">
                                {/* Step indicator */}
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    {[1, 2, 3, 4].map((step) => (
                                        <div key={step} className="flex items-center">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                                                smartBatchStep === step
                                                    ? 'bg-emerald-500 text-white'
                                                    : smartBatchStep > step
                                                        ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                        : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                                            }`}>
                                                {smartBatchStep > step ? <Check className="w-4 h-4" /> : step}
                                            </div>
                                            <span className={`ml-2 text-sm font-medium ${
                                                smartBatchStep >= step ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'
                                            }`}>
                                                {step === 1 ? 'Property' : step === 2 ? 'Upload' : step === 3 ? 'Review' : 'Submit'}
                                            </span>
                                            {step < 4 && <ChevronRight className="w-4 h-4 text-slate-300 mx-2" />}
                                        </div>
                                    ))}
                                </div>

                                {/* Step 1: Property Info */}
                                {smartBatchStep === 1 && (
                                    <div className="flex-1 flex flex-col items-center justify-center">
                                        <div className="w-full max-w-xl">
                                            <div className="text-center mb-6">
                                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mx-auto mb-4">
                                                    <Home className="w-8 h-8 text-emerald-500" />
                                                </div>
                                                <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                                                    Property Information
                                                </h3>
                                                <p className="text-slate-500 dark:text-slate-400">
                                                    Enter details about the property for your gallery
                                                </p>
                                            </div>

                                            <div className="space-y-4 p-6 bg-white/50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700">
                                                {/* Property Address */}
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                                        Property Address <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={smartBatchPropertyInfo.propertyAddress}
                                                        onChange={(e) => setSmartBatchPropertyInfo(prev => ({ ...prev, propertyAddress: e.target.value }))}
                                                        placeholder="123 Main Street, City, ST 12345"
                                                        className="w-full px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                </div>

                                                {/* Price, Beds, Baths, Sqft - Grid */}
                                                <div className="grid grid-cols-4 gap-3">
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                                            Price <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={smartBatchPropertyInfo.listingPrice}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                                const formatted = value ? '$' + parseInt(value).toLocaleString() : '';
                                                                setSmartBatchPropertyInfo(prev => ({ ...prev, listingPrice: formatted }));
                                                            }}
                                                            placeholder="$1,250,000"
                                                            className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                                            Beds <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="20"
                                                            value={smartBatchPropertyInfo.bedrooms}
                                                            onChange={(e) => setSmartBatchPropertyInfo(prev => ({ ...prev, bedrooms: e.target.value }))}
                                                            placeholder="4"
                                                            className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                                            Baths <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="20"
                                                            step="0.5"
                                                            value={smartBatchPropertyInfo.bathrooms}
                                                            onChange={(e) => setSmartBatchPropertyInfo(prev => ({ ...prev, bathrooms: e.target.value }))}
                                                            placeholder="3"
                                                            className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                                            Sq Ft <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={smartBatchPropertyInfo.squareFeet}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                                const formatted = value ? parseInt(value).toLocaleString() : '';
                                                                setSmartBatchPropertyInfo(prev => ({ ...prev, squareFeet: formatted }));
                                                            }}
                                                            placeholder="2,500"
                                                            className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Description */}
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                                        Description <span className="text-slate-400 text-xs font-normal">(optional)</span>
                                                    </label>
                                                    <textarea
                                                        value={smartBatchPropertyInfo.propertyDescription}
                                                        onChange={(e) => setSmartBatchPropertyInfo(prev => ({ ...prev, propertyDescription: e.target.value }))}
                                                        placeholder="Beautiful modern home with stunning views..."
                                                        rows={3}
                                                        className="w-full px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                                                    />
                                                </div>
                                            </div>

                                            {/* Next Button */}
                                            <div className="mt-6 flex justify-end">
                                                <button
                                                    onClick={() => {
                                                        // Also set smartPropertyName from the address for backwards compatibility
                                                        setSmartPropertyName(smartBatchPropertyInfo.propertyAddress);
                                                        setSmartBatchStep(2);
                                                    }}
                                                    disabled={
                                                        !smartBatchPropertyInfo.propertyAddress.trim() ||
                                                        !smartBatchPropertyInfo.listingPrice.trim() ||
                                                        !smartBatchPropertyInfo.bedrooms ||
                                                        !smartBatchPropertyInfo.bathrooms ||
                                                        !smartBatchPropertyInfo.squareFeet.trim()
                                                    }
                                                    className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-medium shadow-lg shadow-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                >
                                                    Next: Upload Photos
                                                    <ArrowRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Upload */}
                                {smartBatchStep === 2 && (
                                    <div className="flex-1 flex flex-col items-center justify-center">
                                        <div
                                            onClick={() => !isAnalyzingPhotos && document.getElementById('smart-batch-upload')?.click()}
                                            className={`w-full max-w-2xl border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                                                isAnalyzingPhotos
                                                    ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20'
                                                    : 'border-slate-300 dark:border-slate-600 hover:border-emerald-400 hover:bg-emerald-50/30 dark:hover:bg-emerald-900/10'
                                            }`}
                                        >
                                            <input
                                                id="smart-batch-upload"
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                multiple
                                                onChange={(e) => e.target.files && handleSmartBatchUpload(e.target.files)}
                                                className="hidden"
                                            />
                                            {isAnalyzingPhotos ? (
                                                <div className="flex flex-col items-center gap-4">
                                                    <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                                                        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-lg font-medium text-slate-800 dark:text-white">
                                                            Uploading {batchUploadedCount} of {batchUploadTotal} photos...
                                                        </p>
                                                    </div>
                                                    {batchUploadTotal > 0 && (
                                                        <div className="w-64">
                                                            <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                <motion.div
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: `${batchUploadProgress}%` }}
                                                                    transition={{ duration: 0.3 }}
                                                                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                                                                />
                                                            </div>
                                                            <p className="text-sm text-slate-400 mt-2 text-center font-medium">
                                                                {batchUploadedCount} of {batchUploadTotal} uploaded
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mx-auto mb-6">
                                                        <Wand2 className="w-10 h-10 text-emerald-500" />
                                                    </div>
                                                    <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                                                        Your AI Social Media Assistant
                                                    </h3>
                                                    <p className="text-slate-500 dark:text-slate-400 mb-4 max-w-md mx-auto">
                                                        Drop in dozens of photos. We batch-stage every image—so your listings are ready for content creation.
                                                    </p>
                                                    <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
                                                        <span className="flex items-center gap-1">
                                                            <Home className="w-4 h-4 text-blue-500" />
                                                            Indoor → Stage
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <TreePine className="w-4 h-4 text-amber-500" />
                                                            Outdoor → Sky
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-slate-400 mt-4">
                                                        Up to 50 photos • JPEG, PNG, WebP • 20MB max each
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Step 3: Review AI Detection */}
                                {smartBatchStep === 3 && (
                                    <div className="flex-1 overflow-y-auto">
                                        <div className="flex items-center justify-between mb-4">
                                            <div>
                                                <h3 className="font-semibold text-slate-800 dark:text-white">
                                                    Review AI Detection
                                                </h3>
                                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                                    Verify room types. Click a photo to override if needed.
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg">
                                                        <Home className="w-3 h-3" />
                                                        {smartBatchSummary?.indoor || 0} Indoor
                                                    </span>
                                                    <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-lg">
                                                        <TreePine className="w-3 h-3" />
                                                        {smartBatchSummary?.outdoor || 0} Outdoor
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={handleSmartBatchReset}
                                                    className="text-sm text-red-500 hover:text-red-600"
                                                >
                                                    Start Over
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">
                                            Drag photos to reorder. Click to expand.
                                        </p>

                                        {/* Video Generation Selection */}
                                        <div className="mb-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border border-violet-200 dark:border-violet-800 rounded-xl">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Film className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                                                    <span className="font-medium text-slate-800 dark:text-white">Auto-Generate Videos</span>
                                                    <span className="text-xs px-2 py-0.5 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded-full">
                                                        Included
                                                    </span>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={enableVideoGeneration}
                                                        onChange={(e) => {
                                                            setEnableVideoGeneration(e.target.checked);
                                                            if (!e.target.checked) setSelectedVideoPhotos([]);
                                                        }}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 dark:peer-focus:ring-violet-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-violet-600"></div>
                                                </label>
                                            </div>
                                            {enableVideoGeneration && (
                                                <>
                                                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                                                        Select up to 3 photos for video reels. We recommend: 1 exterior, 1 living room, 1 bedroom.
                                                    </p>
                                                    {selectedVideoPhotos.length === 0 ? (
                                                        <p className="text-sm text-slate-400 italic">Click photos below to select for video</p>
                                                    ) : (
                                                        <div className="space-y-3">
                                                            {selectedVideoPhotos.map((selection, idx) => (
                                                                <div
                                                                    key={selection.photoId}
                                                                    className="flex items-center gap-3 p-3 bg-violet-50 dark:bg-violet-900/30 rounded-xl border border-violet-200 dark:border-violet-700"
                                                                >
                                                                    <span className="w-6 h-6 flex items-center justify-center bg-violet-600 text-white rounded-full text-xs font-bold flex-shrink-0">
                                                                        {idx + 1}
                                                                    </span>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-violet-800 dark:text-violet-200 truncate">
                                                                            {selection.roomType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                                        </p>
                                                                    </div>
                                                                    {/* Aspect Ratio Selector */}
                                                                    <select
                                                                        value={selection.aspectRatio || '9:16'}
                                                                        onChange={(e) => updateVideoPhotoSettings(selection.photoId, { aspectRatio: e.target.value })}
                                                                        className="px-2 py-1 text-xs bg-white dark:bg-slate-800 border border-violet-300 dark:border-violet-600 rounded-lg text-violet-700 dark:text-violet-300 cursor-pointer"
                                                                    >
                                                                        {ASPECT_RATIOS.map(ar => (
                                                                            <option key={ar.id} value={ar.id}>{ar.name}</option>
                                                                        ))}
                                                                    </select>
                                                                    {/* Style Selector */}
                                                                    <select
                                                                        value={selection.style || 'cinematic'}
                                                                        onChange={(e) => updateVideoPhotoSettings(selection.photoId, { style: e.target.value })}
                                                                        className="px-2 py-1 text-xs bg-white dark:bg-slate-800 border border-violet-300 dark:border-violet-600 rounded-lg text-violet-700 dark:text-violet-300 cursor-pointer"
                                                                    >
                                                                        {VIDEO_STYLES.map(vs => (
                                                                            <option key={vs.id} value={vs.id}>{vs.icon} {vs.name}</option>
                                                                        ))}
                                                                    </select>
                                                                    {/* Remove Button */}
                                                                    <button
                                                                        onClick={() => toggleVideoPhotoSelection(selection.photoId, selection.roomType)}
                                                                        className="p-1 hover:bg-violet-200 dark:hover:bg-violet-800 rounded-full transition-colors flex-shrink-0"
                                                                    >
                                                                        <X className="w-4 h-4 text-violet-500" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {selectedVideoPhotos.length > 0 && selectedVideoPhotos.length < 3 && (
                                                        <p className="text-sm text-slate-400 mt-2">
                                                            ({3 - selectedVideoPhotos.length} more available)
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                            <AnimatePresence>
                                                {smartBatchAnalysis.map((photo, index) => {
                                                    const videoIdx = selectedVideoPhotos.findIndex(p => p.photoId === photo.photoId);
                                                    return (
                                                        <SmartBatchPhotoCard
                                                            key={photo.photoId}
                                                            photo={photo}
                                                            index={index}
                                                            onUpdate={handleSmartPhotoUpdate}
                                                            onRemove={handleSmartPhotoRemove}
                                                            onDragStart={(idx) => setDraggedPhotoIndex(idx)}
                                                            onDragOver={() => {}}
                                                            onDrop={(toIndex) => handleSmartPhotoReorder(draggedPhotoIndex, toIndex)}
                                                            isDragging={draggedPhotoIndex === index}
                                                            enableVideoSelection={enableVideoGeneration}
                                                            isVideoSelected={videoIdx !== -1}
                                                            videoSelectionIndex={videoIdx}
                                                            onToggleVideo={toggleVideoPhotoSelection}
                                                        />
                                                    );
                                                })}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                )}

                                {/* Step 4: Select Styles and Submit */}
                                {smartBatchStep === 4 && (
                                    <div className="flex-1 overflow-y-auto">
                                        <div className="max-w-2xl mx-auto">
                                            <div className="text-center mb-8">
                                                <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                                                    Choose Styles & Submit
                                                </h3>
                                                <p className="text-slate-500 dark:text-slate-400">
                                                    Select styling options for your {smartBatchAnalysis.length} photos
                                                </p>
                                            </div>

                                            {/* Property Name */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                    Property Name (Optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={smartPropertyName}
                                                    onChange={(e) => setSmartPropertyName(e.target.value)}
                                                    placeholder="e.g., 123 Main Street"
                                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                />
                                            </div>

                                            {/* Indoor Style Selection with Styles/Inspiration tabs */}
                                            {(smartBatchSummary?.indoor || 0) > 0 && (
                                                <div className="mb-6">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <Home className="w-4 h-4 text-blue-500" />
                                                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                                Indoor Design ({smartBatchSummary?.indoor} photos)
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {/* Styles / Inspiration Tabs */}
                                                    <div className="flex gap-2 mb-4 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSmartStyleTab('styles');
                                                                setSmartInspiration(null);
                                                            }}
                                                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                                                smartStyleTab === 'styles'
                                                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                                            }`}
                                                        >
                                                            <Palette className="w-4 h-4" />
                                                            Styles
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSmartStyleTab('inspiration');
                                                                setSmartIndoorStyle(null);
                                                            }}
                                                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                                                smartStyleTab === 'inspiration'
                                                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                                            }`}
                                                        >
                                                            <Sparkles className="w-4 h-4" />
                                                            Inspiration
                                                        </button>
                                                    </div>

                                                    {smartStyleTab === 'styles' ? (
                                                        <div className="max-h-80 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl p-2">
                                                            <div className="grid grid-cols-3 gap-2">
                                                                {STAGING_STYLES.map((style) => (
                                                                    <button
                                                                        key={style.id}
                                                                        onClick={() => setSmartIndoorStyle(style.id)}
                                                                        className={`p-2 rounded-xl text-left transition-all ${
                                                                            smartIndoorStyle === style.id && !smartInspiration
                                                                                ? 'ring-2 ring-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                                                                                : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                                                                        }`}
                                                                    >
                                                                        <div className={`w-full h-8 rounded-lg bg-gradient-to-r ${style.color} mb-1`} />
                                                                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{style.name}</p>
                                                                        <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-1">{style.description}</p>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            {/* Inspiration Categories */}
                                                            <div className="flex flex-wrap gap-2 mb-3">
                                                                {(dynamicInspirationCategories || INSPIRATION_CATEGORIES).map((cat) => (
                                                                    <button
                                                                        key={cat.id}
                                                                        type="button"
                                                                        onClick={() => setSmartInspirationCategory(cat.id)}
                                                                        className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 ${
                                                                            smartInspirationCategory === cat.id
                                                                                ? 'bg-purple-600 text-white'
                                                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                                                                        }`}
                                                                    >
                                                                        <span>{cat.icon}</span>
                                                                        {cat.name}
                                                                    </button>
                                                                ))}
                                                            </div>

                                                            {/* Inspiration Grid */}
                                                            <div className="max-h-80 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl p-2">
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    {(dynamicInspirations || INSPIRATIONS).filter(insp =>
                                                                        smartInspirationCategory === 'all' || insp.category === smartInspirationCategory
                                                                    ).map((inspiration) => {
                                                                        const isSelected = smartInspiration?.id === inspiration.id;
                                                                        return (
                                                                            <button
                                                                                key={inspiration.id}
                                                                                onClick={() => setSmartInspiration(inspiration)}
                                                                                className={`relative overflow-hidden rounded-xl border text-left transition-all ${
                                                                                    isSelected
                                                                                        ? 'border-purple-500 ring-2 ring-purple-500/30'
                                                                                        : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                                                                                }`}
                                                                            >
                                                                                <div className={`h-16 w-full bg-gradient-to-br ${inspiration.image}`} />
                                                                                <div className="absolute top-2 left-2">
                                                                                    <span className="rounded-full bg-purple-600/80 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
                                                                                        {inspiration.badge}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="p-2 bg-white dark:bg-slate-800">
                                                                                    <p className="text-xs font-medium text-slate-800 dark:text-white">{inspiration.name}</p>
                                                                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-1">{inspiration.description}</p>
                                                                                </div>
                                                                                {isSelected && (
                                                                                    <div className="absolute top-2 right-2 rounded-full bg-purple-500 p-1 text-white">
                                                                                        <Check className="w-3 h-3" />
                                                                                    </div>
                                                                                )}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Outdoor Mode Selection */}
                                            {(smartBatchSummary?.outdoor || 0) > 0 && (
                                                <div className="mb-6">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <TreePine className="w-4 h-4 text-amber-500" />
                                                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                            Outdoor Enhancement ({smartBatchSummary?.outdoor} photos)
                                                        </label>
                                                    </div>
                                                    <OutdoorModeSelector
                                                        value={smartOutdoorMode}
                                                        onChange={setSmartOutdoorMode}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right side - Summary Panel */}
                            <div className="w-80 flex-shrink-0 border-l border-slate-200/50 dark:border-slate-700/50 bg-white/30 dark:bg-slate-800/30 backdrop-blur-sm p-6 flex flex-col">
                                <h3 className="font-semibold text-slate-800 dark:text-white mb-4">Agent Summary</h3>

                                {/* Photo counts */}
                                <div className="p-4 rounded-xl bg-slate-100 dark:bg-slate-700/50 mb-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm text-slate-600 dark:text-slate-400">Total Photos</span>
                                        <span className="font-semibold text-slate-800 dark:text-white">{smartBatchAnalysis.length}</span>
                                    </div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                                            <Home className="w-3 h-3" />
                                            Indoor (Stage)
                                        </span>
                                        <span className="font-medium text-slate-800 dark:text-white">{smartBatchSummary?.indoor || 0}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400">
                                            <TreePine className="w-3 h-3" />
                                            Outdoor (Sky)
                                        </span>
                                        <span className="font-medium text-slate-800 dark:text-white">{smartBatchSummary?.outdoor || 0}</span>
                                    </div>
                                    {/* Video generation summary */}
                                    {enableVideoGeneration && selectedVideoPhotos.length > 0 && (
                                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                                            <span className="flex items-center gap-1 text-sm text-violet-600 dark:text-violet-400">
                                                <Film className="w-3 h-3" />
                                                Video Reels
                                            </span>
                                            <span className="font-medium text-slate-800 dark:text-white">{selectedVideoPhotos.length} / 3</span>
                                        </div>
                                    )}
                                </div>

                                {/* Video selection summary */}
                                {enableVideoGeneration && selectedVideoPhotos.length > 0 && (
                                    <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 mb-4">
                                        <div className="flex items-center gap-2 text-sm font-medium text-violet-700 dark:text-violet-300 mb-2">
                                            <Film className="w-4 h-4" />
                                            Videos Included
                                        </div>
                                        <div className="space-y-1">
                                            {selectedVideoPhotos.map((v, idx) => (
                                                <div key={v.photoId} className="flex items-center gap-2 text-xs text-violet-600 dark:text-violet-400">
                                                    <span className="w-4 h-4 flex items-center justify-center bg-violet-600 text-white rounded-full text-[10px] font-bold">
                                                        {idx + 1}
                                                    </span>
                                                    {v.roomType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Cost summary */}
                                {smartBatchAnalysis.length > 0 && (
                                    <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 mb-4">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm text-emerald-700 dark:text-emerald-400">Batch fee</span>
                                            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                                                {smartBatchConfig.batchFee} credits
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-emerald-700 dark:text-emerald-400">
                                                {smartBatchConfig.creditsPerPhoto} credits × {smartBatchAnalysis.length} photos
                                            </span>
                                            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                                                {smartBatchConfig.creditsPerPhoto * smartBatchAnalysis.length} credits
                                            </span>
                                        </div>
                                        {enableVideoGeneration && selectedVideoPhotos.length > 0 && (
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-violet-700 dark:text-violet-400">
                                                    {CREDIT_COSTS.video_reel} credits × {selectedVideoPhotos.length} videos
                                                </span>
                                                <span className="text-sm font-medium text-violet-700 dark:text-violet-400">
                                                    {CREDIT_COSTS.video_reel * selectedVideoPhotos.length} credits
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between pt-2 border-t border-emerald-200 dark:border-emerald-700">
                                            <span className="font-semibold text-emerald-800 dark:text-emerald-300">Total Cost</span>
                                            <span className="font-bold text-lg text-emerald-600 dark:text-emerald-400">
                                                {smartBatchSummary?.creditsRequired || (smartBatchConfig.batchFee + smartBatchAnalysis.length * smartBatchConfig.creditsPerPhoto + (enableVideoGeneration ? selectedVideoPhotos.length * CREDIT_COSTS.video_reel : 0))} credits
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Action buttons */}
                                <div className="mt-auto space-y-3">
                                    {smartBatchStep === 3 && (
                                        <button
                                            onClick={() => setSmartBatchStep(4)}
                                            disabled={smartBatchAnalysis.length === 0}
                                            className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-medium shadow-lg shadow-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            Continue to Styles
                                            <ArrowRight className="w-4 h-4" />
                                        </button>
                                    )}

                                    {smartBatchStep === 4 && (
                                        <>
                                            <button
                                                onClick={() => setSmartBatchStep(3)}
                                                className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                                            >
                                                <ArrowLeft className="w-4 h-4" />
                                                Back to Review
                                            </button>
                                            <button
                                                onClick={handleSmartBatchSubmit}
                                                disabled={smartBatchAnalysis.length === 0 || isSubmittingBatch}
                                                className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-medium shadow-lg shadow-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                            >
                                                {isSubmittingBatch ? (
                                                    <>
                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkles className="w-5 h-5" />
                                                        Process {smartBatchAnalysis.length} Photos
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>

                                <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-3">
                                    You'll receive an email when ready
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* SMART BATCH - PROPERTIES WORKFLOW */}
                {/* ============================================================ */}
                {activeWorkspace === 'bulk' && bulkView === 'properties' && !selectedProperty && (
                    <div className="flex-1 flex flex-col overflow-hidden p-6">
                        {/* Agents Sub-tabs: Photo Agent / Properties / Analytics */}
                        <div className="mb-6">
                            <div className="flex rounded-2xl bg-slate-100 p-1 dark:bg-slate-800 w-fit">
                                <button
                                    onClick={() => { setBulkView('quick'); handleSmartBatchReset(); }}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                        bulkView === 'quick'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <Zap className="h-4 w-4" />
                                    Photo Agent
                                </button>
                                <button
                                    onClick={() => { setBulkView('properties'); setSelectedProperty(null); }}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                        bulkView === 'properties'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <Building2 className="h-4 w-4" />
                                    Properties
                                </button>
                                {hasAnalyticsAccess && (
                                    <button
                                        onClick={() => setBulkView('analytics')}
                                        className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                            bulkView === 'analytics'
                                                ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                        }`}
                                    >
                                        <BarChart3 className="h-4 w-4" />
                                        Analytics
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Property Batches</h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'}
                                </p>
                            </div>
                            <button
                                onClick={() => setIsCreatingProperty(true)}
                                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-all"
                            >
                                <Plus className="w-4 h-4" />
                                New Property
                            </button>
                        </div>

                        {/* Search, Sort, and View Controls */}
                        <div className="flex items-center gap-3 mb-4">
                            {/* Search */}
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    value={propertySearchTerm}
                                    onChange={(e) => setPropertySearchTerm(e.target.value)}
                                    placeholder="Search properties..."
                                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                                {propertySearchTerm && (
                                    <button
                                        onClick={() => setPropertySearchTerm('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {/* Sort */}
                            <div className="relative">
                                <select
                                    value={propertySortOrder}
                                    onChange={(e) => setPropertySortOrder(e.target.value)}
                                    className="appearance-none pl-3 pr-8 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="name">Name A-Z</option>
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            </div>

                            {/* View Toggle */}
                            <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                                <button
                                    onClick={() => setPropertiesViewMode('grid')}
                                    className={`p-2 rounded-md transition-all ${
                                        propertiesViewMode === 'grid'
                                            ? 'bg-white dark:bg-slate-700 text-emerald-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    }`}
                                    title="Grid view"
                                >
                                    <LayoutGrid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setPropertiesViewMode('list')}
                                    className={`p-2 rounded-md transition-all ${
                                        propertiesViewMode === 'list'
                                            ? 'bg-white dark:bg-slate-700 text-emerald-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    }`}
                                    title="List view"
                                >
                                    <List className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Create Property Form */}
                        {isCreatingProperty && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 mb-6"
                            >
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Property Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={newPropertyName}
                                            onChange={(e) => setNewPropertyName(e.target.value)}
                                            placeholder="e.g., 123 Main Street"
                                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                                            autoFocus
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Address (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={newPropertyAddress}
                                            onChange={(e) => setNewPropertyAddress(e.target.value)}
                                            placeholder="Full address"
                                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => { setIsCreatingProperty(false); setNewPropertyName(''); setNewPropertyAddress(''); }}
                                        className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleCreateProperty}
                                        disabled={!newPropertyName.trim()}
                                        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                                    >
                                        Create Property
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {/* Properties List */}
                        <div className="flex-1 overflow-y-auto">
                            {isLoadingProperties ? (
                                <div className="flex items-center justify-center h-48">
                                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                                </div>
                            ) : properties.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-48 text-center">
                                    <Building2 className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
                                    <h3 className="text-lg font-medium text-slate-800 dark:text-white mb-2">No properties yet</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                        Create a property to organize your staging batches
                                    </p>
                                    <button
                                        onClick={() => setIsCreatingProperty(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Create Property
                                    </button>
                                </div>
                            ) : filteredProperties.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-48 text-center">
                                    <Search className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
                                    <h3 className="text-lg font-medium text-slate-800 dark:text-white mb-2">No matches found</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Try a different search term
                                    </p>
                                </div>
                            ) : propertiesViewMode === 'grid' ? (
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    {filteredProperties.map((property) => (
                                        <PropertyCard
                                            key={property.batchId}
                                            property={property}
                                            onView={handleViewProperty}
                                            onDownload={handleDownloadPropertyResults}
                                            onDelete={handleDeleteProperty}
                                            isEditing={editingPropertyId === property.batchId}
                                            editingName={editingPropertyName}
                                            onStartEdit={() => {
                                                setEditingPropertyId(property.batchId);
                                                setEditingPropertyName(property.propertyName || '');
                                            }}
                                            onEditNameChange={setEditingPropertyName}
                                            onSaveEdit={() => handleRenameProperty(property.batchId, editingPropertyName)}
                                            onCancelEdit={() => {
                                                setEditingPropertyId(null);
                                                setEditingPropertyName('');
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                /* List View */
                                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-slate-50 dark:bg-slate-900/50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Property</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Photos</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Created</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                            {filteredProperties.map((property) => (
                                                <tr key={property.batchId} onClick={() => handleViewProperty(property.batchId)} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                                                                <Building2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                {editingPropertyId === property.batchId ? (
                                                                    <input
                                                                        type="text"
                                                                        value={editingPropertyName}
                                                                        onChange={(e) => setEditingPropertyName(e.target.value)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') handleRenameProperty(property.batchId, editingPropertyName);
                                                                            if (e.key === 'Escape') { setEditingPropertyId(null); setEditingPropertyName(''); }
                                                                        }}
                                                                        onBlur={() => handleRenameProperty(property.batchId, editingPropertyName)}
                                                                        className="w-full px-2 py-1 text-sm bg-white dark:bg-slate-700 border border-emerald-500 rounded focus:outline-none"
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setEditingPropertyId(property.batchId);
                                                                            setEditingPropertyName(property.propertyName || '');
                                                                        }}
                                                                        className="text-sm font-medium text-slate-800 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors text-left flex items-center gap-1 group"
                                                                    >
                                                                        {property.propertyName || 'Untitled Property'}
                                                                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                                                                    </button>
                                                                )}
                                                                {property.propertyAddress && (
                                                                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                                                        {property.propertyAddress}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                                            property.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                                                            property.status === 'processing' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                            property.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                                            'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                                                        }`}>
                                                            {property.status === 'completed' && <Check className="w-3 h-3" />}
                                                            {property.status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
                                                            {property.status || 'draft'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                                                        {property.totalPhotos || 0} photos
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                                                        {property.createdAt ? new Date(property.createdAt).toLocaleDateString() : '-'}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleViewProperty(property.batchId); }}
                                                                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                                                                title="View property"
                                                            >
                                                                <Eye className="w-4 h-4 text-slate-500" />
                                                            </button>
                                                            {property.status === 'completed' && (
                                                                <>
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); handleDownloadPropertyResults(property); }}
                                                                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                                                                        title="Download"
                                                                    >
                                                                        <Download className="w-4 h-4 text-slate-500" />
                                                                    </button>
                                                                    <a
                                                                        href={generateGalleryUrl(property.slug, property.batchId, property.propertyAddress)}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-lg transition-colors"
                                                                        title="View gallery"
                                                                    >
                                                                        <ExternalLink className="w-4 h-4 text-emerald-500" />
                                                                    </a>
                                                                </>
                                                            )}
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteProperty(property.batchId); }}
                                                                className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                                title="Delete"
                                                            >
                                                                <Trash2 className="w-4 h-4 text-red-500" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Property Detail View */}
                {activeWorkspace === 'bulk' && bulkView === 'properties' && selectedProperty && (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Property Header */}
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => setSelectedProperty(null)}
                                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                        <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                    </button>
                                    <div>
                                        <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                                            {selectedProperty.propertyName}
                                        </h2>
                                        {selectedProperty.propertyAddress && (
                                            <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {selectedProperty.propertyAddress}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {selectedProperty.status === 'completed' && (
                                        <>
                                            <button
                                                onClick={() => {
                                                    // Property info may be nested under 'property' key from API
                                                    const propInfo = selectedProperty.property || selectedProperty.propertyInfo || {};

                                                    // Populate all property fields from database for flyer (check nested object first)
                                                    const price = propInfo.price || propInfo.listingPrice ||
                                                                 selectedProperty.listingPrice || selectedProperty.listing_price || selectedProperty.price || '';
                                                    const sqft = propInfo.squareFeet || propInfo.sqft ||
                                                                selectedProperty.squareFeet || selectedProperty.square_feet || selectedProperty.sqft || '';
                                                    const beds = propInfo.bedrooms || propInfo.beds ||
                                                                selectedProperty.bedrooms || selectedProperty.beds || '';
                                                    const baths = propInfo.bathrooms || propInfo.baths ||
                                                                 selectedProperty.bathrooms || selectedProperty.baths || '';
                                                    const desc = propInfo.description || propInfo.propertyDescription ||
                                                                selectedProperty.propertyDescription || selectedProperty.property_description || selectedProperty.description || '';
                                                    let address = propInfo.address || propInfo.propertyAddress ||
                                                                 selectedProperty.propertyAddress || selectedProperty.property_address || selectedProperty.address || '';
                                                    let name = selectedProperty.propertyName || selectedProperty.property_name || selectedProperty.name || selectedProperty.title || '';

                                                    // If address is empty but name looks like an address, use it for both
                                                    if (!address && name && /\d+.*,/.test(name)) {
                                                        address = name;
                                                    }
                                                    if (!name && address) {
                                                        name = address;
                                                    }

                                                    console.log('[Create Flyer] Property info:', { price, beds, baths, sqft, desc, address, name });

                                                    setGallerySettings(prev => ({
                                                        ...prev,
                                                        propertyName: name,
                                                        propertyAddress: address,
                                                        listingPrice: typeof price === 'number' ? price.toLocaleString() : (price || ''),
                                                        squareFeet: typeof sqft === 'number' ? sqft.toLocaleString() : (sqft || ''),
                                                        bedrooms: beds,
                                                        bathrooms: baths,
                                                        propertyDescription: desc
                                                    }));
                                                    setShowFlyerBuilder(true);
                                                }}
                                                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-violet-500 text-slate-700 dark:text-slate-200 rounded-xl font-medium"
                                            >
                                                <ImagePlus className="w-4 h-4" />
                                                Create Flyer
                                            </button>
                                            <button
                                                onClick={() => {
                                                    // Property info may be nested under 'property' key from API
                                                    const propInfo = selectedProperty.property || selectedProperty.propertyInfo || {};

                                                    // Populate all property fields from database (check nested object first)
                                                    const price = propInfo.price || propInfo.listingPrice ||
                                                                 selectedProperty.listingPrice || selectedProperty.listing_price || selectedProperty.price || '';
                                                    const sqft = propInfo.squareFeet || propInfo.sqft ||
                                                                selectedProperty.squareFeet || selectedProperty.square_feet || selectedProperty.sqft || '';
                                                    const beds = propInfo.bedrooms || propInfo.beds ||
                                                                selectedProperty.bedrooms || selectedProperty.beds || '';
                                                    const baths = propInfo.bathrooms || propInfo.baths ||
                                                                 selectedProperty.bathrooms || selectedProperty.baths || '';
                                                    const desc = propInfo.description || propInfo.propertyDescription ||
                                                                selectedProperty.propertyDescription || selectedProperty.property_description || selectedProperty.description || '';
                                                    // Try multiple field names for address
                                                    let address = propInfo.address || propInfo.propertyAddress ||
                                                                 selectedProperty.propertyAddress || selectedProperty.property_address || selectedProperty.address || '';
                                                    // Try multiple field names for property name/title
                                                    let name = selectedProperty.propertyName || selectedProperty.property_name || selectedProperty.name || selectedProperty.title || '';

                                                    // If address is empty but name looks like an address (contains numbers and commas), use it for both
                                                    if (!address && name && /\d+.*,/.test(name)) {
                                                        address = name;
                                                    }
                                                    // If name is empty, use address
                                                    if (!name && address) {
                                                        name = address;
                                                    }

                                                    console.log('[Create Postcard] Property info:', { price, beds, baths, sqft, desc, address, name });

                                                    setGallerySettings(prev => ({
                                                        ...prev,
                                                        propertyName: name,
                                                        propertyAddress: address,
                                                        listingPrice: typeof price === 'number' ? price.toLocaleString() : (price || ''),
                                                        squareFeet: typeof sqft === 'number' ? sqft.toLocaleString() : (sqft || ''),
                                                        bedrooms: beds,
                                                        bathrooms: baths,
                                                        propertyDescription: desc
                                                    }));
                                                    setShowPostcardBuilder(true);
                                                }}
                                                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-500 text-slate-700 dark:text-slate-200 rounded-xl font-medium"
                                            >
                                                <Mail className="w-4 h-4" />
                                                Create Postcard
                                            </button>
                                            <button
                                                onClick={() => setShowSocialPost(true)}
                                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl font-medium shadow-md"
                                            >
                                                <Share2 className="w-4 h-4" />
                                                Post to Social
                                            </button>
                                            <button
                                                onClick={() => {
                                                    // Property info may be nested under 'property' key from API
                                                    const propInfo = selectedProperty.property || selectedProperty.propertyInfo || {};

                                                    // Populate all property fields from database (check nested object first)
                                                    const price = propInfo.price || propInfo.listingPrice ||
                                                                 selectedProperty.listingPrice || selectedProperty.listing_price || selectedProperty.price || '';
                                                    const sqft = propInfo.squareFeet || propInfo.sqft ||
                                                                selectedProperty.squareFeet || selectedProperty.square_feet || selectedProperty.sqft || '';
                                                    const beds = propInfo.bedrooms || propInfo.beds ||
                                                                selectedProperty.bedrooms || selectedProperty.beds || '';
                                                    const baths = propInfo.bathrooms || propInfo.baths ||
                                                                 selectedProperty.bathrooms || selectedProperty.baths || '';
                                                    const desc = propInfo.description || propInfo.propertyDescription ||
                                                                selectedProperty.propertyDescription || selectedProperty.property_description || selectedProperty.description || '';
                                                    // Try multiple field names for address
                                                    let address = propInfo.address || propInfo.propertyAddress ||
                                                                 selectedProperty.propertyAddress || selectedProperty.property_address || selectedProperty.address || '';
                                                    // Try multiple field names for property name/title
                                                    let name = selectedProperty.propertyName || selectedProperty.property_name || selectedProperty.name || selectedProperty.title || '';

                                                    // If address is empty but name looks like an address (contains numbers and commas), use it for both
                                                    if (!address && name && /\d+.*,/.test(name)) {
                                                        address = name;
                                                    }
                                                    // If name is empty, use address
                                                    if (!name && address) {
                                                        name = address;
                                                    }

                                                    console.log('[Publish Gallery] Property data:', {
                                                        raw: selectedProperty,
                                                        propInfo,
                                                        mapped: { name, address, price, sqft, beds, baths, desc }
                                                    });

                                                    setGallerySettings(prev => ({
                                                        ...prev,
                                                        propertyName: name,
                                                        propertyAddress: address,
                                                        listingPrice: typeof price === 'number' ? price.toLocaleString() : (price || ''),
                                                        squareFeet: typeof sqft === 'number' ? sqft.toLocaleString() : (sqft || ''),
                                                        bedrooms: beds,
                                                        bathrooms: baths,
                                                        propertyDescription: desc
                                                    }));
                                                    setGalleryEditorStep(1);
                                                    setShowGalleryEditor(true);
                                                }}
                                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white rounded-xl font-medium"
                                            >
                                                <Globe className="w-4 h-4" />
                                                Publish Gallery
                                            </button>
                                            <button
                                                onClick={handleDownloadPropertyResults}
                                                disabled={isDownloadingZip}
                                                className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-400 text-white rounded-xl font-medium min-w-[140px]"
                                            >
                                                {isDownloadingZip ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        <span>{downloadProgress}%</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Download className="w-4 h-4" />
                                                        Download All
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                    {selectedProperty.status === 'draft' && selectedProperty.totalPhotos > 0 && (
                                        <button
                                            onClick={handleProcessProperty}
                                            disabled={isProcessingProperty}
                                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-medium"
                                        >
                                            {isProcessingProperty ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Sparkles className="w-4 h-4" />
                                            )}
                                            Process ({smartBatchConfig.batchFee + selectedProperty.totalPhotos * smartBatchConfig.creditsPerPhoto} credits)
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 flex min-h-0">
                            {/* Photos/Videos Grid */}
                            <div className="flex-1 p-6 overflow-y-auto">
                                {/* Photos/Videos Tab Switcher - show when property is completed */}
                                {selectedProperty.status === 'completed' && (
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="inline-flex p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                            <button
                                                onClick={() => setPropertyContentTab('photos')}
                                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                                                    propertyContentTab === 'photos'
                                                        ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-sm'
                                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                                }`}
                                            >
                                                <Camera className="w-4 h-4" />
                                                Photos ({selectedProperty.totalPhotos || 0})
                                            </button>
                                            <button
                                                onClick={() => setPropertyContentTab('videos')}
                                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                                                    propertyContentTab === 'videos'
                                                        ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-400 shadow-sm'
                                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                                }`}
                                            >
                                                <Video className="w-4 h-4" />
                                                Videos ({selectedProperty.photos?.filter(p => p.videoUrl || p.video_url).length || 0})
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* PHOTOS TAB */}
                                {propertyContentTab === 'photos' && (
                                    <>
                                        {/* Upload zone for draft properties */}
                                        {selectedProperty.status === 'draft' && (
                                            <div
                                                onClick={() => !isAddingPhotos && document.getElementById('property-photo-upload')?.click()}
                                                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all mb-6 ${
                                                    isAddingPhotos
                                                        ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20'
                                                        : 'border-slate-300 dark:border-slate-600 hover:border-emerald-400'
                                                }`}
                                            >
                                                <input
                                                    id="property-photo-upload"
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/webp"
                                                    multiple
                                                    onChange={(e) => e.target.files && handleAddPhotosToProperty(e.target.files)}
                                                    className="hidden"
                                                />
                                                {isAddingPhotos ? (
                                                    <Loader2 className="w-6 h-6 text-emerald-500 animate-spin mx-auto" />
                                                ) : (
                                                    <>
                                                        <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                                                        <p className="text-sm text-slate-600 dark:text-slate-400">
                                                            Click or drag photos to add (AI will detect room types)
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Photos */}
                                {selectedProperty.photos?.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-48 text-center">
                                        <Camera className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
                                        <p className="text-slate-500 dark:text-slate-400">No photos added yet</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {selectedProperty.photos?.map((photo, idx) => {
                                            const roomType = getPhotoRoomType(photo);
                                            const category = getPhotoCategory(photo);
                                            const isProcessed = photo.processedUrl || photo.processed_url || photo.stagedUrl || photo.staged_url;
                                            const photoId = photo.photoId || photo.photo_id || photo.id;
                                            const isSelectedForVideo = selectedPhotosForVideo.some(p => (p.photoId || p.photo_id || p.id) === photoId);

                                            return (
                                            <div key={photoId || idx} className={`relative bg-white dark:bg-slate-800 rounded-xl overflow-hidden border-2 transition-all group ${
                                                isSelectedForVideo
                                                    ? 'border-purple-500 ring-2 ring-purple-500/30'
                                                    : 'border-slate-200 dark:border-slate-700'
                                            }`}>
                                                {/* Selection checkbox for processed photos */}
                                                {isProcessed && selectedProperty.status === 'completed' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleTogglePhotoForVideo(photo);
                                                        }}
                                                        className={`absolute top-2 left-2 z-20 w-6 h-6 rounded-md flex items-center justify-center transition-all ${
                                                            isSelectedForVideo
                                                                ? 'bg-purple-500 text-white'
                                                                : 'bg-white/80 dark:bg-slate-800/80 text-slate-400 hover:text-purple-500 border border-slate-300 dark:border-slate-600'
                                                        }`}
                                                        title={isSelectedForVideo ? 'Remove from video batch' : 'Add to video batch'}
                                                    >
                                                        {isSelectedForVideo ? <Check className="w-4 h-4" /> : <Video className="w-3 h-3" />}
                                                    </button>
                                                )}
                                                <div
                                                    className="aspect-[4/3] bg-slate-100 dark:bg-slate-900 cursor-pointer relative"
                                                    onClick={() => handleOpenBatchPhotoForEdit(photo)}
                                                >
                                                    <img
                                                        src={photo.processedUrl || photo.processed_url || photo.url || photo.previewUrl || photo.preview_url || photo.thumbnailUrl || photo.thumbnail_url || photo.originalUrl || photo.original_url}
                                                        alt={roomType}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    {/* Zoom overlay on hover */}
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <ZoomIn className="w-8 h-8 text-white" />
                                                    </div>
                                                    {/* Selected overlay */}
                                                    {isSelectedForVideo && (
                                                        <div className="absolute inset-0 bg-purple-500/20 pointer-events-none" />
                                                    )}
                                                    <div className={`absolute top-2 right-2 px-2 py-1 rounded-lg text-xs font-medium ${
                                                        category === 'indoor'
                                                            ? 'bg-blue-500/90 text-white'
                                                            : 'bg-amber-500/90 text-white'
                                                    }`}>
                                                        {category === 'indoor' ? 'Stage' : 'Sky'}
                                                    </div>
                                                </div>
                                                <div className="p-2">
                                                    <p className="text-xs font-medium text-slate-700 dark:text-slate-300 capitalize">
                                                        {roomType}
                                                    </p>
                                                </div>
                                                {/* Action buttons on hover */}
                                                {(photo.processedUrl || photo.processed_url || photo.stagedUrl || photo.staged_url) && (
                                                    <div className="absolute bottom-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all z-10">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenBatchPhotoForEdit(photo);
                                                            }}
                                                            className="p-1.5 bg-emerald-500/90 hover:bg-emerald-600 text-white rounded-lg flex items-center gap-1"
                                                            title="Refine Image"
                                                        >
                                                            <Wand2 className="w-3 h-3" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleGenerateReelFromBatchPhoto(photo);
                                                            }}
                                                            className="p-1.5 bg-purple-500/90 hover:bg-purple-600 text-white rounded-lg flex items-center gap-1"
                                                            title="Generate Video"
                                                        >
                                                            <Video className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemovePhotoFromProperty(photo.photoId || photo.photo_id || photo.id);
                                                    }}
                                                    className="absolute bottom-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all z-10"
                                                    title="Remove photo"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                                    </>
                                )}

                                {/* VIDEOS TAB */}
                                {propertyContentTab === 'videos' && (
                                    <>
                                        {(() => {
                                            const photosWithVideos = selectedProperty.photos?.filter(p => p.videoUrl || p.video_url) || [];
                                            if (photosWithVideos.length === 0) {
                                                return (
                                                    <div className="flex flex-col items-center justify-center h-64 text-center">
                                                        <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mb-4">
                                                            <Video className="w-8 h-8 text-purple-500" />
                                                        </div>
                                                        <p className="text-lg font-medium text-slate-700 dark:text-slate-300">No videos generated yet</p>
                                                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-xs">
                                                            Generate stunning video reels from your staged photos. Hover over any photo and click the video icon.
                                                        </p>
                                                        <button
                                                            onClick={() => setPropertyContentTab('photos')}
                                                            className="mt-4 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                                                        >
                                                            <Camera className="w-4 h-4" />
                                                            Go to Photos
                                                        </button>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                    {photosWithVideos.map((photo, idx) => {
                                                        const videoUrl = photo.videoUrl || photo.video_url;
                                                        const photoId = photo.photoId || photo.photo_id || photo.id;
                                                        const roomType = getPhotoRoomType(photo) || `Room ${idx + 1}`;
                                                        const videoStyleId = photo.videoStyle || photo.video_style || 'cinematic';
                                                        const aspectRatio = photo.videoAspectRatio || photo.video_aspect_ratio || '9:16';
                                                        const displayName = roomType === 'Unknown' ? `Video ${idx + 1}` : roomType;
                                                        // Look up the display name from VIDEO_STYLES
                                                        const videoStyleConfig = VIDEO_STYLES.find(s => s.id === videoStyleId);
                                                        const videoStyleName = videoStyleConfig?.name || videoStyleId.replace(/_/g, ' ');

                                                        return (
                                                            <div
                                                                key={`video-${photoId || idx}`}
                                                                className="relative bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 group"
                                                            >
                                                                <div className="aspect-[9/16] bg-slate-900 relative">
                                                                    <video
                                                                        src={videoUrl}
                                                                        className="w-full h-full object-cover"
                                                                        muted
                                                                        loop
                                                                        playsInline
                                                                        onMouseEnter={(e) => {
                                                                            e.target.play().catch(() => {});
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.target.pause();
                                                                            e.target.currentTime = 0;
                                                                        }}
                                                                    />
                                                                    {/* Play icon overlay */}
                                                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
                                                                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                                                            <Play className="w-6 h-6 text-white ml-1" />
                                                                        </div>
                                                                    </div>
                                                                    {/* Style badge */}
                                                                    <div className="absolute top-2 right-2 px-2 py-1 rounded-lg text-xs font-medium bg-purple-500/90 text-white">
                                                                        {videoStyleName}
                                                                    </div>
                                                                    {/* Aspect ratio badge */}
                                                                    <div className="absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-medium bg-slate-900/70 text-white">
                                                                        {aspectRatio}
                                                                    </div>
                                                                </div>
                                                                <div className="p-3">
                                                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 capitalize truncate">
                                                                        {displayName}
                                                                    </p>
                                                                    <div className="flex items-center gap-2 mt-2">
                                                                        <a
                                                                            href={videoUrl}
                                                                            download={`${selectedProperty.propertyName?.replace(/[^a-zA-Z0-9]/g, '-') || 'property'}-${videoStyleName.replace(/\s+/g, '-')}-${idx + 1}.mp4`}
                                                                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs font-medium transition-colors"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <Download className="w-3 h-3" />
                                                                            Download
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </>
                                )}

                            </div>

                            {/* Property Settings Panel */}
                            <div className="w-72 flex-shrink-0 border-l border-slate-200/50 dark:border-slate-700/50 bg-white/30 dark:bg-slate-800/30 p-4 overflow-y-auto">
                                <h4 className="font-medium text-slate-800 dark:text-white mb-4">Settings</h4>

                                {/* Stats */}
                                <div className="space-y-2 mb-6">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Total Photos</span>
                                        <span className="font-medium">{selectedProperty.totalPhotos}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-blue-500">Indoor</span>
                                        <span className="font-medium">{selectedProperty.indoorCount}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-amber-500">Outdoor</span>
                                        <span className="font-medium">{selectedProperty.outdoorCount}</span>
                                    </div>
                                    {selectedProperty.status !== 'draft' && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-emerald-500">Completed</span>
                                            <span className="font-medium">{selectedProperty.completedCount}</span>
                                        </div>
                                    )}
                                    {/* Video stats */}
                                    {selectedProperty.status === 'completed' && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-purple-500">Videos</span>
                                            <span className="font-medium">
                                                {selectedProperty.photos?.filter(p => p.videoUrl || p.video_url).length || 0}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Style Settings (only for draft) */}
                                {selectedProperty.status === 'draft' && (
                                    <>
                                        {/* Indoor Design with Styles/Inspiration tabs */}
                                        <div className="mb-4">
                                            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                                                Indoor Design ({selectedProperty.indoorCount || 0} photos)
                                            </label>

                                            {/* Styles / Inspiration Tabs */}
                                            <div className="flex gap-1 mb-3 p-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setPropertyStyleTab('styles');
                                                        setPropertyInspiration(null);
                                                    }}
                                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${
                                                        propertyStyleTab === 'styles'
                                                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                                            : 'text-slate-500 dark:text-slate-400'
                                                    }`}
                                                >
                                                    <Palette className="w-3 h-3" />
                                                    Styles
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setPropertyStyleTab('inspiration');
                                                    }}
                                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${
                                                        propertyStyleTab === 'inspiration'
                                                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                                            : 'text-slate-500 dark:text-slate-400'
                                                    }`}
                                                >
                                                    <Sparkles className="w-3 h-3" />
                                                    Inspiration
                                                </button>
                                            </div>

                                            {propertyStyleTab === 'styles' ? (
                                                <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
                                                    {STAGING_STYLES.map((style) => {
                                                        const isSelected = (selectedProperty.indoorStyle || 'modern') === style.id && !propertyInspiration;
                                                        return (
                                                            <button
                                                                key={style.id}
                                                                onClick={() => {
                                                                    handleUpdateProperty({ indoorStyle: style.id, inspiration: null });
                                                                    setPropertyInspiration(null);
                                                                }}
                                                                className={`w-full text-left rounded-lg overflow-hidden border transition-all ${
                                                                    isSelected
                                                                        ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                                                                        : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'
                                                                }`}
                                                            >
                                                                <div className={`h-6 w-full bg-gradient-to-r ${style.color}`} />
                                                                <div className="px-2 py-1.5 bg-white dark:bg-slate-800">
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-xs font-medium text-slate-800 dark:text-white">{style.name}</span>
                                                                        {isSelected && <Check className="w-3 h-3 text-emerald-500" />}
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div>
                                                    {/* Inspiration Categories */}
                                                    <div className="flex flex-wrap gap-1 mb-2">
                                                        {(dynamicInspirationCategories || INSPIRATION_CATEGORIES).map((cat) => (
                                                            <button
                                                                key={cat.id}
                                                                type="button"
                                                                onClick={() => setPropertyInspirationCategory(cat.id)}
                                                                className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition-all ${
                                                                    propertyInspirationCategory === cat.id
                                                                        ? 'bg-purple-600 text-white'
                                                                        : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                                                                }`}
                                                            >
                                                                {cat.icon} {cat.name}
                                                            </button>
                                                        ))}
                                                    </div>

                                                    {/* Inspiration Grid */}
                                                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
                                                        {(dynamicInspirations || INSPIRATIONS).filter(insp =>
                                                            propertyInspirationCategory === 'all' || insp.category === propertyInspirationCategory
                                                        ).map((inspiration) => {
                                                            const isSelected = propertyInspiration?.id === inspiration.id;
                                                            return (
                                                                <button
                                                                    key={inspiration.id}
                                                                    onClick={() => {
                                                                        setPropertyInspiration(inspiration);
                                                                        handleUpdateProperty({ inspiration: inspiration.id, indoorStyle: null });
                                                                    }}
                                                                    className={`w-full text-left rounded-lg overflow-hidden border transition-all ${
                                                                        isSelected
                                                                            ? 'border-purple-500 ring-2 ring-purple-500/30'
                                                                            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                                                                    }`}
                                                                >
                                                                    <div className={`h-8 w-full bg-gradient-to-br ${inspiration.image} relative`}>
                                                                        <span className="absolute top-1 left-1 rounded-full bg-purple-600/80 px-1.5 py-0.5 text-[8px] font-semibold text-white backdrop-blur-sm">
                                                                            {inspiration.badge}
                                                                        </span>
                                                                    </div>
                                                                    <div className="px-2 py-1.5 bg-white dark:bg-slate-800">
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-xs font-medium text-slate-800 dark:text-white">{inspiration.name}</span>
                                                                            {isSelected && <Check className="w-3 h-3 text-purple-500" />}
                                                                        </div>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Outdoor Mode Selector */}
                                        <div className="mb-4">
                                            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                                                Outdoor Mode ({selectedProperty.outdoorCount || 0} photos)
                                            </label>
                                            <div className="grid grid-cols-2 gap-1.5">
                                                {OUTDOOR_MODES.map((mode) => {
                                                    const isSelected = (selectedProperty.outdoorMode || 'twilight') === mode.value;
                                                    const ModeIcon = mode.value === 'twilight' ? Moon :
                                                                    mode.value === 'sunset' ? Sunset :
                                                                    mode.value === 'sunrise' ? Sunrise :
                                                                    mode.value === 'blue_hour' ? Moon :
                                                                    mode.value === 'dramatic' ? CloudSun : Sun;
                                                    return (
                                                        <button
                                                            key={mode.value}
                                                            onClick={() => handleUpdateProperty({ outdoorMode: mode.value })}
                                                            className={`p-2 rounded-lg text-left transition-all ${
                                                                isSelected
                                                                    ? 'ring-2 ring-amber-500 bg-amber-50 dark:bg-amber-900/20'
                                                                    : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                                                            }`}
                                                        >
                                                            <ModeIcon className={`w-4 h-4 mb-1 ${isSelected ? 'text-amber-500' : 'text-slate-500'}`} />
                                                            <p className={`text-xs font-medium ${isSelected ? 'text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'}`}>
                                                                {mode.label}
                                                            </p>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Cost */}
                                {selectedProperty.status === 'draft' && selectedProperty.totalPhotos > 0 && (
                                    <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                                        <p className="text-xs text-emerald-600 dark:text-emerald-500 mb-1">
                                            Batch fee: {smartBatchConfig.batchFee} + {smartBatchConfig.creditsPerPhoto} × {selectedProperty.totalPhotos} photos
                                        </p>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-emerald-700 dark:text-emerald-400">Total Cost</span>
                                            <span className="font-bold text-emerald-600">
                                                {smartBatchConfig.batchFee + selectedProperty.totalPhotos * smartBatchConfig.creditsPerPhoto} credits
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* SESSIONS TAB (ChatGPT-like) */}
                {/* ============================================================ */}
                {activeWorkspace === 'library' && libraryView === 'photos' && (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    key="error-banner"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="m-4 mb-0 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3"
                                >
                                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
                                    <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                                        <X className="w-4 h-4" />
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Library Sub-tabs: Photo Library / Video Library */}
                            <div className="mb-6 flex items-center gap-4">
                                <div className="flex rounded-2xl bg-slate-100 p-1 dark:bg-slate-800">
                                    <button
                                        onClick={() => setLibraryView('photos')}
                                        className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                            libraryView === 'photos'
                                                ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                        }`}
                                    >
                                        <ImageIcon className="h-4 w-4" />
                                        Photo Library
                                    </button>
                                    <button
                                        onClick={() => {
                                            setLibraryView('videos');
                                            loadMyVideos();
                                        }}
                                        className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                            libraryView === 'videos'
                                                ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                        }`}
                                    >
                                        <Video className="h-4 w-4" />
                                        Video Library
                                    </button>
                                </div>
                            </div>

                            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Photo Library</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Keep creation focused here, then jump back into staging when you select a photo.
                                    </p>
                                </div>
                                <div
                                    onClick={() => document.getElementById('library-photo-upload-input')?.click()}
                                    className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer transition-colors hover:border-emerald-400 hover:text-emerald-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                >
                                    <input
                                        id="library-photo-upload-input"
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], uploadRoomType, customRoomName)}
                                        className="hidden"
                                    />
                                    <Upload className="mr-2 inline h-4 w-4" />
                                    Upload photo
                                </div>
                            </div>

                            <div className="mb-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-800">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Total Photos</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-800 dark:text-white">{filteredPhotos.length}</p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-800">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Ready For Sessions</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-800 dark:text-white">{groupedPhotos.staged.length}</p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-800">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Needs Staging</p>
                                    <p className="mt-2 text-2xl font-semibold text-slate-800 dark:text-white">{groupedPhotos.unstaged.length}</p>
                                </div>
                            </div>

                            <div className="mb-6 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_180px]">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        value={photoSearch}
                                        onChange={(e) => setPhotoSearch(e.target.value)}
                                        placeholder="Search by room or photo name"
                                        className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                    />
                                </div>
                                <select
                                    value={photoFilter}
                                    onChange={(e) => setPhotoFilter(e.target.value)}
                                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                >
                                    <option value="all">All photos</option>
                                    <option value="staged">Staged</option>
                                    <option value="unstaged">Needs staging</option>
                                    <option value="living">Living rooms</option>
                                    <option value="bedroom">Bedrooms</option>
                                </select>
                                <select
                                    value={photoSort}
                                    onChange={(e) => setPhotoSort(e.target.value)}
                                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                >
                                    <option value="recent">Most recent</option>
                                    <option value="status">Staging status</option>
                                    <option value="room">Room type</option>
                                </select>
                            </div>

                            {filteredPhotos.length === 0 ? (
                                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center dark:border-slate-700 dark:bg-slate-800/40">
                                    <ImageIcon className="mb-4 h-10 w-10 text-slate-400" />
                                    <h4 className="text-lg font-semibold text-slate-800 dark:text-white">No matching photos</h4>
                                    <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
                                        Adjust the filters or upload another room to keep building your staging library.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    {groupedPhotos.unstaged.length > 0 && (
                                        <div>
                                            <div className="mb-4 flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Needs staging</h4>
                                                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Rooms that still need a first staged concept.</p>
                                                </div>
                                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                                    {groupedPhotos.unstaged.length}
                                                </span>
                                            </div>
                                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                                <AnimatePresence>
                                                    {groupedPhotos.unstaged.map((photo) => (
                                                        <PhotoCard
                                                            key={photo.id}
                                                            photo={photo}
                                                            onSelect={(nextPhoto) => {
                                                                setSelectedPhoto(nextPhoto);
                                                                // Reset style/inspiration when selecting a new photo
                                                                setSelectedInspiration(null);
                                                                setSelectedStyle(null);
                                                                setActiveWorkspace('create');
                                                            }}
                                                            onDelete={handleDelete}
                                                            isSelected={selectedPhoto?.id === photo.id}
                                                            isDeleting={isDeleting === photo.id}
                                                        />
                                                    ))}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                    )}

                                    {groupedPhotos.staged.length > 0 && (
                                        <div>
                                            <div className="mb-4 flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Staged and ready</h4>
                                                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Open these in Create to compare, download, or continue in Sessions.</p>
                                                </div>
                                                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                                    {groupedPhotos.staged.length}
                                                </span>
                                            </div>
                                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                                <AnimatePresence>
                                                    {groupedPhotos.staged.map((photo) => (
                                                        <PhotoCard
                                                            key={photo.id}
                                                            photo={photo}
                                                            onSelect={(nextPhoto) => {
                                                                setSelectedPhoto(nextPhoto);
                                                                // Reset style/inspiration when selecting a new photo
                                                                setSelectedInspiration(null);
                                                                setSelectedStyle(null);
                                                                setActiveWorkspace('create');
                                                            }}
                                                            onDelete={handleDelete}
                                                            isSelected={selectedPhoto?.id === photo.id}
                                                            isDeleting={isDeleting === photo.id}
                                                        />
                                                    ))}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Analytics View */}
                {activeWorkspace === 'bulk' && bulkView === 'analytics' && hasAnalyticsAccess && (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Agents Sub-tabs: Photo Agent / Properties / Analytics */}
                        <div className="px-6 pt-6 pb-0">
                            <div className="flex rounded-2xl bg-slate-100 p-1 dark:bg-slate-800 w-fit">
                                <button
                                    onClick={() => { setBulkView('quick'); handleSmartBatchReset(); }}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                        bulkView === 'quick'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <Zap className="h-4 w-4" />
                                    Photo Agent
                                </button>
                                <button
                                    onClick={() => { setBulkView('properties'); setSelectedProperty(null); }}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                        bulkView === 'properties'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <Building2 className="h-4 w-4" />
                                    Properties
                                </button>
                                {hasAnalyticsAccess && (
                                    <button
                                        onClick={() => setBulkView('analytics')}
                                        className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                            bulkView === 'analytics'
                                                ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                        }`}
                                    >
                                        <BarChart3 className="h-4 w-4" />
                                        Analytics
                                    </button>
                                )}
                            </div>
                        </div>
                        <GalleryAnalytics />
                    </div>
                )}

                {((activeWorkspace === 'create' && activeSession) || activeWorkspace === 'sessions') && (
                    <div className="flex-1 flex flex-col min-h-0" style={{ height: 'calc(100vh - 70px)' }}>
                        {activeSession ? (
                            /* Active Session Chat View */
                            <div className="h-full flex flex-col">
                                {/* Session Header */}
                                <div className="flex-shrink-0 px-6 py-4 bg-white/50 dark:bg-slate-800/50 border-b border-slate-200/50 dark:border-slate-700/50">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                        <button
                                            onClick={handleCloseSession}
                                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                        >
                                            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                        </button>
                                            <div>
                                                <h3 className="font-semibold text-slate-800 dark:text-white">
                                                    {activeSession.title || 'Staging Session'}
                                                </h3>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    {formatStyleName(activeSession.style)} · v{activeSession.current_version || 1}
                                                    {activeSession.status === 'completed' && ' · Completed'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={handleLoadVersionHistory}
                                                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                            >
                                                <History className="w-4 h-4" />
                                                Versions
                                            </button>
                                            {activeSession.status === 'active' && (
                                                <button
                                                    onClick={handleCompleteSession}
                                                    className="flex items-center gap-2 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                                                >
                                                    <Check className="w-4 h-4" />
                                                    Complete
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-4 grid gap-3 sm:grid-cols-4">
                                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Current Version</p>
                                            <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">v{activeSession.current_version || 1}</p>
                                        </div>
                                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Style</p>
                                            <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">{formatStyleName(activeSession.style)}</p>
                                        </div>
                                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Messages</p>
                                            <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">{sessionMessages.length}</p>
                                        </div>
                                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Status</p>
                                            <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">{activeSession.status === 'completed' ? 'Completed' : 'Active session'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Chat Messages */}
                                <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
                                    <AnimatePresence>
                                        {sessionMessages.map((msg, idx) => (
                                            <ChatMessage
                                                key={msg.message_id || idx}
                                                message={msg}
                                                onBranchFrom={activeSession.status === 'active' ? setBranchFromVersion : null}
                                                originalPhotoUrl={selectedPhoto?.originalUrl || activeSession?.original_photo_url}
                                                onGenerateReel={canGenerateVideo ? handleGenerateReelFromSession : null}
                                                selectedResolution={selectedResolution}
                                            />
                                        ))}
                                    </AnimatePresence>

                                    {isGeneratingRefinement && (
                                        <ThinkingAnimation message="Generating your staged image — hang tight." />
                                    )}
                                </div>

                                {/* Input Area */}
                                {activeSession.status === 'active' && (
                                    <div className="flex-shrink-0 px-6 py-4 bg-white/50 dark:bg-slate-800/50 border-t border-slate-200/50 dark:border-slate-700/50">
                                        {branchFromVersion && (
                                            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                                <GitBranch className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                                <span className="text-sm text-amber-700 dark:text-amber-300">
                                                    Branching from version {branchFromVersion}
                                                </span>
                                                <button
                                                    onClick={() => setBranchFromVersion(null)}
                                                    className="ml-auto text-amber-600 dark:text-amber-400 hover:text-amber-800"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                        <div className="mb-2">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-2">Quick directions</p>
                                            <div className="flex flex-wrap gap-2">
                                            {QUICK_REFINEMENTS.map((prompt) => (
                                                <button
                                                    key={prompt}
                                                    type="button"
                                                    onClick={() => setRefinementInput(prompt)}
                                                    className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-all hover:text-emerald-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:text-emerald-300"
                                                >
                                                    {prompt}
                                                </button>
                                            ))}
                                            </div>
                                        </div>
                                        <div className="flex gap-3">
                                            <input
                                                type="text"
                                                value={refinementInput}
                                                onChange={(e) => setRefinementInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendRefinement()}
                                                placeholder="Describe changes... (e.g., 'Add more plants', 'Make it brighter')"
                                                maxLength={500}
                                                disabled={isGeneratingRefinement}
                                                className="flex-1 px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                                            />
                                            <button
                                                onClick={handleSendRefinement}
                                                disabled={!refinementInput.trim() || isGeneratingRefinement}
                                                className="px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                            >
                                                {isGeneratingRefinement ? (
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                ) : (
                                                    <Send className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between gap-3">
                                            <p className="text-xs text-slate-400">
                                                Give one clear creative direction at a time for better versions.
                                            </p>
                                            <p className="text-xs text-slate-400 text-right">
                                                {refinementInput.length}/500
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Sessions List */
                            <div className="flex-1 flex flex-col p-6 overflow-hidden">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                                            Staging Sessions
                                        </h3>
                                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                            Re-open prior work, compare iterations, and continue refining without restarting from zero.
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {/* View Mode Toggle */}
                                        <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
                                            <button
                                                onClick={() => setSessionsViewMode('list')}
                                                className={`p-2 rounded-md transition-all ${
                                                    sessionsViewMode === 'list'
                                                        ? 'bg-white dark:bg-slate-600 text-emerald-600 dark:text-emerald-400 shadow-sm'
                                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                                }`}
                                                title="List view"
                                            >
                                                <List className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setSessionsViewMode('grid')}
                                                className={`p-2 rounded-md transition-all ${
                                                    sessionsViewMode === 'grid'
                                                        ? 'bg-white dark:bg-slate-600 text-emerald-600 dark:text-emerald-400 shadow-sm'
                                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                                }`}
                                                title="Grid view"
                                            >
                                                <LayoutGrid className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <button
                                            onClick={loadSessions}
                                            disabled={isLoadingSessions}
                                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${isLoadingSessions ? 'animate-spin' : ''}`} />
                                            Refresh
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-6 grid gap-3 sm:grid-cols-3">
                                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-800">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">All Sessions</p>
                                        <p className="mt-2 text-2xl font-semibold text-slate-800 dark:text-white">{sessions.length}</p>
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-800">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Active</p>
                                        <p className="mt-2 text-2xl font-semibold text-slate-800 dark:text-white">{sessions.filter((session) => session.status === 'active').length}</p>
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-800">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Completed</p>
                                        <p className="mt-2 text-2xl font-semibold text-slate-800 dark:text-white">{sessions.filter((session) => session.status === 'completed').length}</p>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto relative">
                                    {/* Loading overlay when opening a session */}
                                    {isLoadingSessions && sessions.length > 0 && (
                                        <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
                                            <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mb-3" />
                                            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Loading session...</p>
                                        </div>
                                    )}
                                    {isLoadingSessions && sessions.length === 0 ? (
                                        <div className="flex justify-center py-12">
                                            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                                        </div>
                                    ) : sessions.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full text-center py-12">
                                            <div className="w-20 h-20 rounded-3xl bg-emerald-500/10 flex items-center justify-center mb-4">
                                                <MessageSquare className="w-10 h-10 text-emerald-500" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                                                No Sessions Yet
                                            </h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-6">
                                                Start a new staging session to refine your images with natural language instructions - like chatting with an AI designer.
                                            </p>
                                            <button
                                                onClick={() => {
                                                    setActiveWorkspace('create');
                                                    setCreateMode('staging');
                                                }}
                                                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-all"
                                            >
                                                Start New Staging
                                            </button>
                                        </div>
                                    ) : sessionsViewMode === 'list' ? (
                                        /* List View */
                                        <div className="space-y-1">
                                            <AnimatePresence>
                                                {sessions.map((session) => (
                                                    <SessionListItem
                                                        key={session.session_id}
                                                        session={session}
                                                        onOpen={handleOpenSession}
                                                        onDelete={handleDeleteSession}
                                                    />
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    ) : (
                                        /* Grid View */
                                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                            <AnimatePresence>
                                                {sessions.map((session) => (
                                                    <SessionCard
                                                        key={session.session_id}
                                                        session={session}
                                                        onOpen={handleOpenSession}
                                                        onDelete={handleDeleteSession}
                                                    />
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ============================================================ */}
                {/* MY VIDEOS (Library Sub-Tab) */}
                {/* ============================================================ */}
                {activeWorkspace === 'library' && libraryView === 'videos' && (
                    <div className="flex-1 flex flex-col p-6 overflow-hidden">
                        {/* Library Sub-tabs: Photo Library / Video Library */}
                        <div className="mb-6 flex items-center gap-4">
                            <div className="flex rounded-2xl bg-slate-100 p-1 dark:bg-slate-800">
                                <button
                                    onClick={() => setLibraryView('photos')}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                        libraryView === 'photos'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <ImageIcon className="h-4 w-4" />
                                    Photo Library
                                </button>
                                <button
                                    onClick={() => {
                                        setLibraryView('videos');
                                        loadMyVideos();
                                    }}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
                                        libraryView === 'videos'
                                            ? 'bg-white text-emerald-600 shadow-sm dark:bg-slate-700 dark:text-emerald-400'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    <Video className="h-4 w-4" />
                                    Video Library
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                                    Video Library
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Manage and download your generated video reels
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowVideoUploadModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                                >
                                    <Upload className="w-4 h-4" />
                                    Upload Video
                                </button>
                                <button
                                    onClick={loadMyVideos}
                                    disabled={isLoadingVideos}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    <RefreshCw className={`w-4 h-4 ${isLoadingVideos ? 'animate-spin' : ''}`} />
                                    Refresh
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {isLoadingVideos && myVideos.length === 0 ? (
                                <div className="flex justify-center py-12">
                                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                                </div>
                            ) : myVideos.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                                    <div className="w-20 h-20 rounded-3xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                                        <Video className="w-10 h-10 text-slate-400" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                                        No Videos Yet
                                    </h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-6">
                                        Create a video reel from your staged photos or upload your own videos.
                                    </p>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => {
                                                setActiveWorkspace('create');
                                                setCreateMode('video');
                                            }}
                                            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-all"
                                        >
                                            Create Video
                                        </button>
                                        <button
                                            onClick={() => setShowVideoUploadModal(true)}
                                            className="px-6 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl font-medium transition-all flex items-center gap-2"
                                        >
                                            <Upload className="w-4 h-4" />
                                            Upload Video
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                    <AnimatePresence>
                                        {myVideos.map((video) => {
                                            const isSelectedForPro = selectedVideosForPro.some(v => v.id === video.id);
                                            return (
                                            <motion.div
                                                key={video.id}
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.95 }}
                                                className={`group relative rounded-2xl border-2 bg-white dark:bg-slate-800 overflow-hidden hover:shadow-lg transition-all ${
                                                    isSelectedForPro
                                                        ? 'border-orange-500 ring-2 ring-orange-500/30'
                                                        : 'border-slate-200 dark:border-slate-700'
                                                }`}
                                            >
                                                {/* Selection checkbox for pro editing */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleVideoForPro(video);
                                                    }}
                                                    className={`absolute top-2 left-2 z-20 w-6 h-6 rounded-md flex items-center justify-center transition-all ${
                                                        isSelectedForPro
                                                            ? 'bg-orange-500 text-white'
                                                            : 'bg-white/80 dark:bg-slate-800/80 text-slate-400 hover:text-orange-500 border border-slate-300 dark:border-slate-600'
                                                    }`}
                                                    title={isSelectedForPro ? 'Remove from selection' : 'Select for pro editing'}
                                                >
                                                    {isSelectedForPro ? <Check className="w-4 h-4" /> : <Star className="w-3 h-3" />}
                                                </button>

                                                {/* Video Thumbnail/Preview */}
                                                <div className="relative aspect-video bg-slate-100 dark:bg-slate-900">
                                                    {video.thumbnailUrl || video.thumbnail_url ? (
                                                        <img
                                                            src={video.thumbnailUrl || video.thumbnail_url}
                                                            alt={video.name || 'Video thumbnail'}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <Video className="w-12 h-12 text-slate-300 dark:text-slate-600" />
                                                        </div>
                                                    )}

                                                    {/* Play overlay */}
                                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <a
                                                            href={video.videoUrl || video.video_url || video.resultUrl || video.result_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 transition-transform"
                                                        >
                                                            <Play className="w-6 h-6 text-emerald-600 ml-1" />
                                                        </a>
                                                    </div>

                                                    {/* Duration badge */}
                                                    {video.duration && (
                                                        <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 rounded text-xs text-white font-medium">
                                                            {video.duration}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Video Info */}
                                                <div className="p-4">
                                                    <h4 className="font-medium text-slate-800 dark:text-white truncate">
                                                        {video.name || video.title || `Video ${video.id?.slice(-6) || ''}`}
                                                    </h4>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                        {video.createdAt || video.created_at
                                                            ? new Date(video.createdAt || video.created_at).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })
                                                            : 'Recently created'
                                                        }
                                                    </p>

                                                    {/* Short URL with copy button */}
                                                    {(video.shortUrl || video.short_url) && (
                                                        <div className="flex items-center gap-2 mt-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                                            <Link2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                                            <span className="text-xs text-slate-600 dark:text-slate-300 truncate flex-1 font-mono">
                                                                {video.shortUrl || video.short_url}
                                                            </span>
                                                            <button
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(video.shortUrl || video.short_url);
                                                                    // Could add a toast notification here
                                                                }}
                                                                className="p-1 text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded transition-colors"
                                                                title="Copy link"
                                                            >
                                                                <Clipboard className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* Action buttons */}
                                                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                                                        <a
                                                            href={video.videoUrl || video.video_url || video.resultUrl || video.result_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                                        >
                                                            <Eye className="w-3.5 h-3.5" />
                                                            View
                                                        </a>
                                                        <a
                                                            href={video.videoUrl || video.video_url || video.resultUrl || video.result_url}
                                                            download={video.name || 'video.mp4'}
                                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                                                        >
                                                            <Download className="w-3.5 h-3.5" />
                                                            Download
                                                        </a>
                                                        <button
                                                            onClick={() => handleOpenVariationModal(video)}
                                                            className="p-2 text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                                                            title="Create Variations"
                                                        >
                                                            <Layers className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteVideo(video.id)}
                                                            disabled={deletingVideoId === video.id}
                                                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                                                            title="Delete video"
                                                        >
                                                            {deletingVideoId === video.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Trash2 className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );})}
                                    </AnimatePresence>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Floating Pro Editing Selection Bar */}
                {selectedVideosForPro.length > 0 && activeWorkspace === 'library' && libraryView === 'videos' && (
                    <motion.div
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 100 }}
                        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] px-6 py-4 bg-slate-900 dark:bg-slate-800 text-white rounded-2xl shadow-2xl border border-slate-700 flex items-center gap-6"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                                <Star className="w-5 h-5 text-orange-400" />
                            </div>
                            <div>
                                <p className="font-semibold">{selectedVideosForPro.length} video{selectedVideosForPro.length > 1 ? 's' : ''} selected</p>
                                <p className="text-xs text-slate-400">for professional editing</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleSelectAllVideosForPro}
                                className="px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            >
                                Select All
                            </button>
                            <button
                                onClick={handleClearProVideoSelection}
                                className="px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            >
                                Clear
                            </button>
                            <button
                                onClick={openSendToProModal}
                                className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-medium flex items-center gap-2 transition-all"
                            >
                                <Sparkles className="w-4 h-4" />
                                Send to Pro
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Send to Pro Modal */}
                {showSendToProModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
                        onClick={closeSendToProModal}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto"
                        >
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-orange-500/10 to-amber-500/10">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                                            <Star className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Send to Pro Editor</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                {selectedVideosForPro.length} video{selectedVideosForPro.length > 1 ? 's' : ''} selected
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeSendToProModal}
                                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5 text-slate-500" />
                                    </button>
                                </div>
                                <div className="mt-4 flex items-center gap-3">
                                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                        <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                                            proEditStep === 'details'
                                                ? 'border-orange-500 bg-orange-500 text-white'
                                                : 'border-emerald-500 bg-emerald-500 text-white'
                                        }`}>
                                            {proEditStep === 'payment' ? <Check className="h-3.5 w-3.5" /> : '1'}
                                        </span>
                                        Details
                                    </div>
                                    <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                        <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                                            proEditStep === 'payment'
                                                ? 'border-orange-500 bg-orange-500 text-white'
                                                : 'border-slate-300 dark:border-slate-600'
                                        }`}>
                                            2
                                        </span>
                                        Payment
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 space-y-5">
                                {/* Selected Videos Preview */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                        Selected Videos ({selectedVideosForPro.length})
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedVideosForPro.map((video) => (
                                            <div key={video.id} className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-lg">
                                                <Video className="w-3.5 h-3.5 text-slate-500" />
                                                <span className="text-sm text-slate-700 dark:text-slate-300 truncate max-w-[150px]">
                                                    {video.name || video.title || `Video ${video.id?.slice(-6)}`}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {proEditStep === 'details' ? (
                                    <>
                                        {/* Editing Packages - Multi-select */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                                                Select Editing Services <span className="text-red-500">*</span>
                                            </label>
                                            <div className="space-y-3">
                                                {PRO_EDITING_PACKAGES.map((pkg) => {
                                                    const isSelected = proEditRequest.selectedPackages.includes(pkg.id);
                                                    const isRequired = pkg.required;
                                                    return (
                                                        <button
                                                            key={pkg.id}
                                                            onClick={() => {
                                                                if (isRequired) return;
                                                                setProEditRequest((prev) => ({
                                                                    ...prev,
                                                                    selectedPackages: isSelected
                                                                        ? prev.selectedPackages.filter((id) => id !== pkg.id)
                                                                        : [...prev.selectedPackages, pkg.id]
                                                                }));
                                                            }}
                                                            className={`w-full p-4 rounded-xl border-2 text-left transition-all relative ${
                                                                isSelected
                                                                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                                                    : 'border-slate-200 dark:border-slate-600 hover:border-orange-300'
                                                            } ${isRequired ? 'cursor-default' : ''}`}
                                                        >
                                                            {pkg.popular && (
                                                                <span className="absolute -top-2 right-3 px-2 py-0.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-medium rounded-full">
                                                                    Popular
                                                                </span>
                                                            )}
                                                            {isRequired && (
                                                                <span className="absolute -top-2 left-3 px-2 py-0.5 bg-emerald-500 text-white text-xs font-medium rounded-full">
                                                                    Base Package
                                                                </span>
                                                            )}
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                                                                            isSelected
                                                                                ? 'border-orange-500 bg-orange-500'
                                                                                : 'border-slate-300 dark:border-slate-500'
                                                                        }`}>
                                                                            {isSelected && <Check className="w-3 h-3 text-white" />}
                                                                        </div>
                                                                        <h4 className="font-semibold text-slate-800 dark:text-white">{pkg.name}</h4>
                                                                    </div>
                                                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 ml-7">{pkg.description}</p>
                                                                    <div className="flex flex-wrap gap-1.5 mt-2 ml-7">
                                                                        {pkg.features.map((feature, idx) => (
                                                                            <span key={idx} className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full">
                                                                                {feature}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div className="text-right ml-4">
                                                                    <p className="text-2xl font-bold text-slate-800 dark:text-white">${pkg.price}</p>
                                                                    {isRequired && <p className="text-xs text-emerald-600 dark:text-emerald-400">Included</p>}
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Editing Instructions <span className="text-red-500">*</span>
                                            </label>
                                            <textarea
                                                value={proEditRequest.instructions}
                                                onChange={(e) => setProEditRequest((prev) => ({ ...prev, instructions: e.target.value }))}
                                                placeholder="Describe what you want our editors to do with these videos (e.g., add music, transitions, color grading, text overlays, combine into one video...)"
                                                rows={4}
                                                className="w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Turnaround Time
                                            </label>
                                            <div className="grid grid-cols-3 gap-2">
                                                {RUSH_OPTIONS.map((option) => (
                                                    <button
                                                        key={option.id}
                                                        onClick={() => setProEditRequest((prev) => ({ ...prev, turnaround: option.id }))}
                                                        className={`p-3 rounded-xl border-2 text-center transition-all relative ${
                                                            proEditRequest.turnaround === option.id
                                                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                                                : 'border-slate-200 dark:border-slate-600 hover:border-orange-300'
                                                        }`}
                                                    >
                                                        {option.price > 0 && (
                                                            <span className="absolute -top-2 right-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-medium rounded-full">
                                                                {option.badge}
                                                            </span>
                                                        )}
                                                        <p className="text-sm font-semibold text-slate-800 dark:text-white">{option.label}</p>
                                                        <p className={`text-xs mt-0.5 ${option.price > 0 ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                                            {option.price > 0 ? `+$${option.price.toFixed(2)}` : 'Included'}
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl border border-orange-200 dark:border-orange-800 space-y-2">
                                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Order Summary</p>
                                            {proEditPricing.selectedPkgs.map((pkg) => (
                                                <div key={pkg.id} className="flex items-center justify-between text-sm">
                                                    <span className="text-slate-600 dark:text-slate-400">{pkg.name}</span>
                                                    <span className="text-slate-700 dark:text-slate-300">${pkg.price.toFixed(2)}</span>
                                                </div>
                                            ))}
                                            {proEditPricing.rushFee > 0 && (
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-red-600 dark:text-red-400">Rush Delivery ({proEditPricing.rushOption.label})</span>
                                                    <span className="text-red-600 dark:text-red-400">+${proEditPricing.rushFee.toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between pt-2 border-t border-orange-200 dark:border-orange-700">
                                                <span className="font-semibold text-slate-800 dark:text-white">Total</span>
                                                <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">${proEditPricing.totalFormatted}</span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 text-center pt-1">
                                                {selectedVideosForPro.length} raw video{selectedVideosForPro.length > 1 ? 's' : ''} will be edited into one professional video
                                            </p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                    Your Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={proEditRequest.contactName}
                                                    onChange={(e) => setProEditRequest((prev) => ({ ...prev, contactName: e.target.value }))}
                                                    placeholder="John Doe"
                                                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                    Phone (optional)
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={proEditRequest.contactPhone}
                                                    onChange={(e) => setProEditRequest((prev) => ({ ...prev, contactPhone: e.target.value }))}
                                                    placeholder="(555) 123-4567"
                                                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                value={proEditRequest.contactEmail}
                                                onChange={(e) => setProEditRequest((prev) => ({ ...prev, contactEmail: e.target.value }))}
                                                placeholder="john@example.com"
                                                className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Additional Notes (optional)
                                            </label>
                                            <textarea
                                                value={proEditRequest.additionalNotes}
                                                onChange={(e) => setProEditRequest((prev) => ({ ...prev, additionalNotes: e.target.value }))}
                                                placeholder="Any other details or preferences..."
                                                rows={2}
                                                className="w-full px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                                            />
                                        </div>

                                        <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                            <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                                    What happens next?
                                                </p>
                                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                    Continue to demo payment details, then we will send the sanitized order summary to our editing team.
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
                                            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                                Demo payment form
                                            </p>
                                            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                                                This step is for UI demos only. Raw card numbers and CVV are not sent to the backend or charged.
                                            </p>
                                        </div>

                                        <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-slate-500 dark:text-slate-400">Amount due</span>
                                                <span className="text-xl font-bold text-orange-600 dark:text-orange-400">${proEditPricing.totalFormatted}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-slate-500 dark:text-slate-400">Videos</span>
                                                <span className="text-slate-700 dark:text-slate-300">{selectedVideosForPro.length}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-slate-500 dark:text-slate-400">Services</span>
                                                <span className="text-slate-700 dark:text-slate-300">{proEditPricing.selectedPkgs.map((pkg) => pkg.name).join(', ')}</span>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Cardholder Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={proEditPayment.cardholderName}
                                                onChange={(e) => setProEditPayment((prev) => ({ ...prev, cardholderName: e.target.value }))}
                                                placeholder="John Doe"
                                                className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Card Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete="cc-number"
                                                value={proEditPayment.cardNumber}
                                                onChange={(e) => setProEditPayment((prev) => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))}
                                                placeholder="4242 4242 4242 4242"
                                                className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                            />
                                        </div>

                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                    Expiry <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete="cc-exp"
                                                    value={proEditPayment.expiry}
                                                    onChange={(e) => setProEditPayment((prev) => ({ ...prev, expiry: formatExpiry(e.target.value) }))}
                                                    placeholder="MM/YY"
                                                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                    CVV <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="password"
                                                    inputMode="numeric"
                                                    autoComplete="cc-csc"
                                                    value={proEditPayment.cvv}
                                                    onChange={(e) => setProEditPayment((prev) => ({ ...prev, cvv: getDigitsOnly(e.target.value).slice(0, 4) }))}
                                                    placeholder="123"
                                                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                    Billing ZIP <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete="postal-code"
                                                    value={proEditPayment.billingZip}
                                                    onChange={(e) => setProEditPayment((prev) => ({ ...prev, billingZip: getDigitsOnly(e.target.value).slice(0, 10) }))}
                                                    placeholder="78701"
                                                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>

                                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800 dark:bg-emerald-900/20">
                                            <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                                                Backend payload preview
                                            </p>
                                            <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">
                                                We will submit only <span className="font-mono">{maskCardNumber(proEditPayment.cardNumber) || '**** **** **** 4242'}</span>, billing ZIP, cardholder name, and order totals.
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-end gap-3">
                                <button
                                    onClick={proEditStep === 'payment' ? () => setProEditStep('details') : closeSendToProModal}
                                    className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    {proEditStep === 'payment' ? 'Back' : 'Cancel'}
                                </button>
                                {proEditStep === 'details' ? (
                                    <button
                                        onClick={handleAdvanceToProPayment}
                                        className="px-6 py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-medium flex items-center gap-2 transition-all"
                                    >
                                        Next
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleSubmitProEditingRequest}
                                        disabled={isSubmittingProRequest}
                                        className="px-6 py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-medium flex items-center gap-2 transition-all"
                                    >
                                        {isSubmittingProRequest ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4" />
                                                Submit Demo Payment
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {/* ============================================================ */}
                {/* VIDEO ASSISTANT TAB */}
                {/* ============================================================ */}
                {activeWorkspace === 'assistant' && (
                    <VideoAgent
                        subscription={subscription}
                        onUpgrade={onUpgradePlan}
                        myVideos={myVideos}
                        onLoadVideos={loadMyVideos}
                        isLoadingVideos={isLoadingVideos}
                    />
                )}

                {/* ============================================================ */}
                {/* SINGLE PHOTO STAGING & VIDEO TABS */}
                {/* ============================================================ */}
                {activeWorkspace === 'create' && !activeSession && (
                    <div className="flex flex-1 h-full overflow-hidden" style={{ minHeight: 0 }}>
                        {/* Photo Tray */}
                        <div className={`${showPhotoTray ? 'w-72' : 'w-0 border-r-0'} flex-shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col overflow-hidden transition-all duration-200`} style={{ minHeight: 0 }}>
                            <div className="p-3 border-b border-slate-200 dark:border-slate-800">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-slate-800 dark:text-white text-sm">Photos</h3>
                                        <p className="mt-0.5 text-[11px] uppercase tracking-[0.14em] text-slate-400">Choose one room</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-500">{photos.length}</span>
                                        <button
                                            onClick={() => setShowPhotoTray(false)}
                                            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-3" style={{ minHeight: 0 }}>
                                {/* Simple Upload Zone */}
                                <div
                                    onClick={() => document.getElementById('photo-upload-input')?.click()}
                                    className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-4 text-center cursor-pointer hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors mb-3"
                                >
                                    <input
                                        id="photo-upload-input"
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], uploadRoomType, customRoomName)}
                                        className="hidden"
                                    />
                                    {isUploading ? (
                                        <Loader2 className="w-6 h-6 mx-auto text-emerald-500 animate-spin" />
                                    ) : (
                                        <>
                                            <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                                            <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Upload a room</p>
                                        </>
                                    )}
                                </div>

                                {/* Photo Grid */}
                                {isLoading && photos.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-8">
                                        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-2" />
                                        <p className="text-xs text-slate-400">Loading photos...</p>
                                    </div>
                                ) : photos.length > 0 ? (
                                    <div className="grid grid-cols-2 gap-2">
                                        <AnimatePresence>
                                            {filteredPhotos.map((photo) => (
                                                <PhotoCard
                                                    key={photo.id}
                                                    photo={photo}
                                                    onSelect={(photo) => {
                                                        setSelectedPhoto(photo);
                                                        // Reset style/inspiration when selecting a new photo
                                                        setSelectedInspiration(null);
                                                        setSelectedStyle(null);
                                                        setShowPhotoTray(false);
                                                    }}
                                                    onDelete={handleDelete}
                                                    isSelected={selectedPhoto?.id === photo.id}
                                                    isDeleting={isDeleting === photo.id}
                                                />
                                            ))}
                                        </AnimatePresence>
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-400 text-center py-4">No photos yet</p>
                                )}
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">

                    {/* Error display */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                key="create-error-banner"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="m-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3"
                            >
                                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
                                <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                                    <X className="w-4 h-4" />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {selectedPhoto ? (
                        <div className="flex-1 flex min-h-0 relative h-full" style={{ minHeight: 0 }}>
                            {/* Preview */}
                            <div className="flex-1 min-w-0 overflow-auto bg-gradient-to-br from-slate-100 via-white to-emerald-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900">
                                <div className="min-h-full w-full p-6 flex items-start justify-center">
                                {selectedPhoto.stagedUrl ? (
                                    <div className="w-full max-w-5xl">
                                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                                            <div>
                                                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Preview Studio</div>
                                                <h3 className="mt-1 text-lg font-semibold text-slate-800 dark:text-white">
                                                    {selectedPhoto.customRoomName || ROOM_TYPES.find((room) => room.id === selectedPhoto.roomType)?.name || 'Staged room'}
                                                </h3>
                                            </div>
                                            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/80 p-1 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewMode('slider')}
                                                    className={`rounded-xl px-3 py-2 text-sm font-medium transition-all ${previewMode === 'slider' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 dark:text-slate-300'}`}
                                                >
                                                    <LayoutPanelTop className="mr-2 inline h-4 w-4" />
                                                    Slider
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewMode('split')}
                                                    className={`rounded-xl px-3 py-2 text-sm font-medium transition-all ${previewMode === 'split' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 dark:text-slate-300'}`}
                                                >
                                                    <LayoutGrid className="mr-2 inline h-4 w-4" />
                                                    Side by side
                                                </button>
                                            </div>
                                        </div>

                                        <div className="relative max-w-full" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                                            {previewMode === 'split' ? (
                                                <div className="grid gap-4 lg:grid-cols-2">
                                                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
                                                        <div className="border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 dark:border-slate-800">Before</div>
                                                        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                                                            <img
                                                                src={selectedPhoto.originalUrl}
                                                                alt="Original room"
                                                                className="w-full object-contain bg-slate-950/5"
                                                                style={{ maxHeight: 'calc(100vh - 300px)' }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-xl dark:border-emerald-900 dark:bg-slate-900">
                                                        <div className="border-b border-emerald-100 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-500 dark:border-emerald-950">After</div>
                                                        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                                                            <img
                                                                src={selectedPhoto.stagedUrl}
                                                                alt="Staged room"
                                                                className="w-full object-contain bg-slate-950/5"
                                                                style={{ maxHeight: 'calc(100vh - 300px)' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ height: 'min(70vh, calc(100vh - 260px))' }}>
                                                    <BeforeAfterSlider
                                                        beforeUrl={selectedPhoto.originalUrl}
                                                        afterUrl={selectedPhoto.stagedUrl}
                                                        className="h-full w-full"
                                                    />
                                                </div>
                                            )}
                                        {/* Fullscreen button */}
                                        <button
                                            onClick={() => setPreviewPhoto(selectedPhoto)}
                                            className="absolute bottom-4 right-4 p-2 bg-black/60 hover:bg-black/80 text-white rounded-lg backdrop-blur-sm transition-all"
                                            title="View fullscreen"
                                        >
                                            <Maximize2 className="w-5 h-5" />
                                        </button>
                                        </div>
                                        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500 dark:text-slate-400">
                                            <div>
                                                <p>{previewMode === 'slider' ? 'Drag to compare before and after' : 'Review both versions side by side'}</p>
                                                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                                                    {selectedPhoto.stagedUrl ? 'Keep the preview open while you decide whether to download or continue in Sessions.' : ''}
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <span className="rounded-full bg-white/80 px-3 py-1 text-xs shadow-sm dark:bg-slate-800">Best for MLS thumbnails</span>
                                                <span className="rounded-full bg-white/80 px-3 py-1 text-xs shadow-sm dark:bg-slate-800">Ready for Sessions</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Original Photo Preview */
                                    <div
                                        className="relative max-w-full max-h-full cursor-pointer group"
                                        onClick={() => setPreviewPhoto(selectedPhoto)}
                                    >
                                        <img
                                            src={selectedPhoto.originalUrl}
                                            alt="Selected photo"
                                            className="max-w-full rounded-2xl shadow-2xl object-contain"
                                            style={{ maxHeight: 'calc(100vh - 260px)' }}
                                        />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity flex items-center justify-center">
                                            <Maximize2 className="w-8 h-8 text-white" />
                                        </div>
                                    </div>
                                )}
                                </div>
                            </div>

                            {/* Options Panel - Scrollable with fixed height */}
                            <div className="w-80 flex-shrink-0 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col" style={{ maxHeight: 'calc(100vh - 80px)' }}>
                                <div className="flex-1 overflow-y-auto p-6 pb-28">
                                        <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-5 dark:border-slate-700 dark:bg-slate-800/60">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Workflow</p>
                                            <h3 className="mt-2 text-base font-semibold text-slate-800 dark:text-white">
                                                {createMode === 'staging'
                                                    ? (singlePhotoCategory === 'outdoor' ? 'Enhance exterior photo' : 'Stage this room')
                                                    : 'Create a video reel'}
                                            </h3>
                                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                                                {createMode === 'staging'
                                                    ? (singlePhotoCategory === 'outdoor'
                                                        ? 'Choose a sky enhancement, generate, then refine.'
                                                        : 'Pick a style, generate, then refine in Sessions.')
                                                    : 'Select reel settings and generate your video.'}
                                            </p>
                                        </div>
                                        {createMode === 'staging' ? (
                                            <StagingWizard
                                                selectedPhoto={selectedPhoto}
                                                selectedStyle={selectedStyle}
                                                setSelectedStyle={setSelectedStyle}
                                                selectedInspiration={selectedInspiration}
                                                setSelectedInspiration={setSelectedInspiration}
                                                selectedResolution={selectedResolution}
                                                setSelectedResolution={setSelectedResolution}
                                                customPrompt={customPrompt}
                                                setCustomPrompt={setCustomPrompt}
                                                credits={credits}
                                                isProcessing={isProcessing}
                                                onRoomTypeChange={(newType, customName) => handleUpdatePhotoRoomType(newType, customName)}
                                                onStartStaging={handleCreateSession}
                                                recommendedStyleIds={recommendedStyleIds}
                                                inspirations={dynamicInspirations}
                                                inspirationCategories={dynamicInspirationCategories}
                                                // Indoor/Outdoor support
                                                photoCategory={singlePhotoCategory}
                                                setPhotoCategory={setSinglePhotoCategory}
                                                outdoorMode={singleOutdoorMode}
                                                setOutdoorMode={setSingleOutdoorMode}
                                            />
                                        ) : (
                                            <>
                                                <VideoOptionsPanel
                                                    aspectRatio={aspectRatio}
                                                    setAspectRatio={setAspectRatio}
                                                    videoStyle={videoStyle}
                                                    setVideoStyle={setVideoStyle}
                                                    musicStyle={musicStyle}
                                                    setMusicStyle={setMusicStyle}
                                                    disabled={isProcessing}
                                                />

                                                <button
                                                    onClick={handleGenerateVideo}
                                                    disabled={isProcessing || !selectedPhoto?.stagedUrl}
                                                    className="w-full mt-6 px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-semibold shadow-lg shadow-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                                >
                                                    {isProcessing ? (
                                                        <>
                                                            <Loader2 className="w-5 h-5 animate-spin" />
                                                            Generating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Play className="w-5 h-5" />
                                                            Generate Video Reel
                                                        </>
                                                    )}
                                                </button>

                                                {!isProcessing && selectedPhoto?.stagedUrl && (
                                                    <button
                                                        onClick={handleMagicReel}
                                                        className="w-full mt-3 px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-semibold shadow-lg shadow-purple-500/30 transition-all flex items-center justify-center gap-2 group"
                                                    >
                                                        <Sparkles className="w-5 h-5 group-hover:animate-pulse" />
                                                        Magic Marketing Package (3 Reels)
                                                    </button>
                                                )}
                                            </>
                                        )}

                                        {/* Download buttons for staged photo */}
                                        {selectedPhoto.stagedUrl && createMode === 'staging' && (
                                            <div className="mt-4 space-y-3">
                                                <button
                                                    onClick={handleCreateSession}
                                                    disabled={isProcessing}
                                                    className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                                                >
                                                    <MessageSquare className="mr-2 inline h-4 w-4" />
                                                    Open Session
                                                </button>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => downloadImage(selectedPhoto.stagedUrlHd || selectedPhoto.stagedUrl, `staged-${selectedPhoto.id}${selectedPhoto.stagedUrlHd ? '-HD' : ''}.${selectedPhoto.stagedUrlHd ? 'png' : 'jpg'}`)}
                                                        className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                        Download
                                                        {selectedPhoto.stagedUrlHd && <span className="px-1 py-0.5 bg-amber-400 text-amber-900 text-[10px] font-bold rounded">HD</span>}
                                                    </button>
                                                    <button
                                                        onClick={() => setPreviewPhoto(selectedPhoto)}
                                                        className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        Compare
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                    ) : (
                        /* Centered Empty State */
                        <div className="flex-1 flex items-center justify-center p-8 bg-gray-50 dark:bg-slate-950">
                            <div className="w-full max-w-lg text-center">
                                {/* Quick Upload Zone */}
                                <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 p-8 hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors cursor-pointer"
                                    onClick={() => document.querySelector('input[type="file"]')?.click()}
                                >
                                    <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                                    <p className="font-medium text-slate-700 dark:text-slate-200">
                                        Click to upload a photo
                                    </p>
                                    <p className="text-sm text-slate-400 mt-1">
                                        or drag and drop • JPEG, PNG, WebP
                                    </p>
                                </div>

                                <div className="mt-4 flex justify-center gap-2">
                                    {photos.length > 0 && (
                                        <button
                                            onClick={() => setShowPhotoTray(true)}
                                            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-emerald-300 hover:text-emerald-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                        >
                                            Browse library ({photos.length})
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setActiveWorkspace('sessions')}
                                        className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-emerald-300 hover:text-emerald-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                    >
                                        View Sessions
                                    </button>
                                </div>

                            </div>
                        </div>
                    )}
                </div>
                    </div>
                )}
            </div>

            {/* Processing Progress Indicator */}
            <AnimatePresence>
                {isProcessing && (
                    <GenerationProgress
                        progress={processingProgress}
                        message={processingMessage}
                        type={processingType}
                        thumbnail={selectedPhoto?.url || selectedPhoto?.originalUrl}
                    />
                )}
            </AnimatePresence>

            {/* Preview Modal */}
            <AnimatePresence>
                {previewPhoto && (
                    <ResultPreviewModal
                        photo={previewPhoto}
                        onClose={() => setPreviewPhoto(null)}
                        onDownload={downloadImage}
                    />
                )}
            </AnimatePresence>

            {/* Insufficient Credits Modal */}
            <AnimatePresence>
                {showInsufficientCredits && (
                    <InsufficientCreditsModal
                        onClose={() => setShowInsufficientCredits(false)}
                        onBuyCredits={onUpgradePlan}
                    />
                )}
            </AnimatePresence>

            {/* Batch Submit Confirmation Modal */}
            <AnimatePresence>
                {batchSubmitResult && (
                    <BatchSubmitConfirmation
                        batchInfo={batchSubmitResult}
                        onClose={() => {
                            setBatchSubmitResult(null);
                            setBulkView('properties');
                            loadProperties();
                        }}
                        onViewJobs={() => {
                            setBatchSubmitResult(null);
                            setActiveWorkspace('bulk');
                            setBulkView('properties');
                            loadProperties();
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Batch Job Details Modal */}
            <AnimatePresence>
                {selectedBatchJob && (
                    <BatchJobDetails
                        job={selectedBatchJob}
                        onClose={() => setSelectedBatchJob(null)}
                        onDownload={handleDownloadBatch}
                    />
                )}
            </AnimatePresence>

            {/* Version History Modal */}
            <AnimatePresence>
                {showVersionHistory && sessionVersions.length > 0 && (
                    <VersionTimeline
                        versions={sessionVersions}
                        currentVersion={activeSession?.current_version}
                        onSelectVersion={(version) => {
                            setBranchFromVersion(version);
                        }}
                        onClose={() => setShowVersionHistory(false)}
                    />
                )}
            </AnimatePresence>

            {/* Reel Options Modal */}
            <AnimatePresence>
                {showReelOptionsModal && (
                    <motion.div
                        key="reel-options-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowReelOptionsModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                        >
                            <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Create Video Reel</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Configure your video settings</p>
                            </div>
                            <div className="p-6">
                               <VideoOptionsPanel
                                   aspectRatio={aspectRatio}
                                   setAspectRatio={setAspectRatio}
                                   videoStyle={videoStyle}
                                   setVideoStyle={setVideoStyle}
                                   musicStyle={musicStyle}
                                   setMusicStyle={setMusicStyle}
                                   disabled={isProcessing}
                               />
                            </div>                            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-700/30 flex gap-3">
                                <button
                                    onClick={() => setShowReelOptionsModal(false)}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmGenerateReel}
                                    disabled={credits < CREDIT_COSTS.video_reel}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    <Video className="w-4 h-4" />
                                    Generate Reel
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Video Upload Modal */}
            <AnimatePresence>
                {showVideoUploadModal && (
                    <motion.div
                        key="video-upload-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        onClick={() => !isUploadingVideo && setShowVideoUploadModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full overflow-hidden"
                        >
                            {/* Header */}
                            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                                        Upload Video
                                    </h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                        Upload your own video to your library
                                    </p>
                                </div>
                                <button
                                    onClick={() => !isUploadingVideo && setShowVideoUploadModal(false)}
                                    disabled={isUploadingVideo}
                                    className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Upload Area */}
                            <div className="p-6">
                                {videoUploadError && (
                                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-red-600 dark:text-red-400">{videoUploadError}</p>
                                    </div>
                                )}

                                {isUploadingVideo ? (
                                    <div className="text-center py-8">
                                        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
                                        <p className="text-slate-600 dark:text-slate-400 mb-2">Uploading video...</p>
                                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 mb-2">
                                            <div
                                                className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${videoUploadProgress}%` }}
                                            />
                                        </div>
                                        <p className="text-sm text-slate-500">{videoUploadProgress}%</p>
                                    </div>
                                ) : (
                                    <label className="block">
                                        <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-all">
                                            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                                            <p className="text-slate-700 dark:text-slate-300 font-medium mb-1">
                                                Drop your video here or click to browse
                                            </p>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                MP4, MOV, WebM, or AVI • Max 500MB
                                            </p>
                                        </div>
                                        <input
                                            type="file"
                                            accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    handleVideoUpload(file);
                                                }
                                                e.target.value = ''; // Reset input
                                            }}
                                            className="hidden"
                                        />
                                    </label>
                                )}
                            </div>

                            {/* Footer */}
                            {!isUploadingVideo && (
                                <div className="px-6 pb-6">
                                    <button
                                        onClick={() => setShowVideoUploadModal(false)}
                                        className="w-full py-2.5 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Confirm Modal */}
            <AnimatePresence>
                {confirmModal.isOpen && (
                    <ConfirmModal
                        key="confirm-modal"
                        isOpen={confirmModal.isOpen}
                        title={confirmModal.title}
                        message={confirmModal.message}
                        confirmLabel={confirmModal.confirmLabel}
                        cancelLabel={confirmModal.cancelLabel}
                        variant={confirmModal.variant}
                        onConfirm={confirmModal.onConfirm}
                        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                    />
                )}

                {/* Success Toast */}
                {successMessage && (
                    <motion.div
                        key="success-toast"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[150] px-6 py-4 bg-emerald-500 text-white rounded-xl shadow-2xl flex items-center gap-3"
                    >
                        <CheckCircle className="w-6 h-6" />
                        <p className="font-medium">{successMessage}</p>
                        <button
                            onClick={() => setSuccessMessage('')}
                            className="ml-2 p-1 hover:bg-white/20 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}

                {/* Floating Video Selection Bar */}
                {selectedPhotosForVideo.length > 0 && activeWorkspace === 'bulk' && selectedProperty && (
                    <motion.div
                        key="floating-video-bar"
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 100 }}
                        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] px-6 py-4 bg-slate-900 dark:bg-slate-800 text-white rounded-2xl shadow-2xl border border-slate-700 flex items-center gap-6"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                                <Video className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                                <p className="font-semibold">{selectedPhotosForVideo.length} photo{selectedPhotosForVideo.length > 1 ? 's' : ''} selected</p>
                                <p className="text-xs text-slate-400">Video Agent</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleSelectAllPhotosForVideo}
                                className="px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            >
                                Select All
                            </button>
                            <button
                                onClick={handleClearVideoSelection}
                                className="px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            >
                                Clear
                            </button>
                            <button
                                onClick={() => setShowBatchVideoModal(true)}
                                className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-medium flex items-center gap-2 transition-all"
                            >
                                <Sparkles className="w-4 h-4" />
                                Generate {selectedPhotosForVideo.length} Video{selectedPhotosForVideo.length > 1 ? 's' : ''}
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Batch Video Options Modal */}
                {showBatchVideoModal && (
                    <motion.div
                        key="batch-video-options-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
                        onClick={() => setShowBatchVideoModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
                        >
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-purple-500/10 to-pink-500/10">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                            <Video className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Video Agent</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                {selectedPhotosForVideo.length} photo{selectedPhotosForVideo.length > 1 ? 's' : ''} selected
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowBatchVideoModal(false)}
                                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5 text-slate-500" />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 space-y-6">
                                {/* Aspect Ratio */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                                        Aspect Ratio
                                    </label>
                                    <div className="grid grid-cols-3 gap-3">
                                        {ASPECT_RATIOS.map((ratio) => (
                                            <button
                                                key={ratio.id}
                                                onClick={() => setBatchVideoAspectRatio(ratio.id)}
                                                className={`p-3 rounded-xl border-2 transition-all ${
                                                    batchVideoAspectRatio === ratio.id
                                                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                                                }`}
                                            >
                                                <div className={`mx-auto mb-2 bg-slate-300 dark:bg-slate-600 ${
                                                    ratio.id === '9:16' ? 'w-5 h-8' :
                                                    ratio.id === '16:9' ? 'w-8 h-5' : 'w-6 h-6'
                                                } rounded`} />
                                                <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{ratio.name}</p>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Video Style */}
                                <div>
                                   <div className="flex items-center justify-between mb-3">
                                       <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                           Video Style
                                       </label>
                                       <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                                           <button
                                               onClick={() => setBatchStyleTab('cinematic')}
                                               className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                                   batchStyleTab === 'cinematic'
                                                       ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                                       : 'text-slate-500 hover:text-slate-700'
                                               }`}
                                           >
                                               Cinematic
                                           </button>
                                           <button
                                               onClick={() => setBatchStyleTab('3d')}
                                               className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                                                   batchStyleTab === '3d'
                                                       ? 'bg-purple-500 text-white shadow-sm'
                                                       : 'text-slate-500 hover:text-slate-700'
                                               }`}
                                           >
                                               3D Effects
                                               <span className={`px-1 py-0.5 rounded-md text-[8px] ${batchStyleTab === '3d' ? 'bg-white/20 text-white' : 'bg-purple-100 text-purple-600'}`}>NEW</span>
                                           </button>
                                       </div>
                                   </div>
                                   <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
                                       {VIDEO_STYLES.filter(style => batchStyleTab === '3d' ? style.is3D : !style.is3D).map((style) => (
                                           <button
                                               key={style.id}
                                               onClick={() => setBatchVideoStyle(style.id)}
                                               className={`p-3 rounded-xl border-2 text-left transition-all relative ${
                                                   batchVideoStyle === style.id
                                                       ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                                                       : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                                               }`}
                                           >
                                               <div className="absolute -top-1.5 -right-1.5 flex flex-col gap-1 items-end">
                                                   {style.isNew && (
                                                       <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full">
                                                           NEW
                                                       </span>
                                                   )}
                                                   {style.is3D && (
                                                       <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full">
                                                           3D
                                                       </span>
                                                   )}
                                               </div>
                                               <div className="flex items-center gap-1.5 mb-1">
                                                   <span className="text-sm">{style.icon}</span>
                                                   <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{style.name}</span>
                                               </div>
                                               <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">{style.description}</p>
                                           </button>
                                       ))}
                                   </div>
                                </div>
                                {/* Music Style */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                                        Background Music
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {MUSIC_STYLES.map((music) => (
                                            <button
                                                key={music.id}
                                                onClick={() => setBatchVideoMusic(music.id)}
                                                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                                                    batchVideoMusic === music.id
                                                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                                                }`}
                                            >
                                                <div className={`p-2 rounded-lg ${batchVideoMusic === music.id ? 'bg-purple-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
                                                    <Music className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <p className={`text-xs font-semibold ${batchVideoMusic === music.id ? 'text-purple-700 dark:text-purple-300' : 'text-slate-700 dark:text-slate-300'}`}>
                                                        {music.name}
                                                    </p>
                                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate w-24">
                                                        {music.description}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Info */}
                                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                    <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                            Processing in background
                                        </p>
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                            You can continue working while videos are generated. You'll receive an email when all videos are ready.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between">
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Cost: <span className="font-semibold text-slate-700 dark:text-slate-300">{selectedPhotosForVideo.length * 50} credits</span>
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setShowBatchVideoModal(false)}
                                        className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSubmitBatchVideoJob}
                                        disabled={isGeneratingBatchVideo}
                                        className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 text-white rounded-xl font-medium flex items-center gap-2 transition-all"
                                    >
                                        {isGeneratingBatchVideo ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-4 h-4" />
                                                Generate Videos
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {/* Property Photo Lightbox */}
                {enlargedPropertyPhoto && (
                    <motion.div
                        key="property-photo-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/90 z-[100] flex flex-col items-center justify-center p-4"
                        onClick={() => setEnlargedPropertyPhoto(null)}
                    >
                        {/* Close button */}
                        <button
                            onClick={() => setEnlargedPropertyPhoto(null)}
                            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Photo info badge */}
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-2">
                            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                                (enlargedPropertyPhoto.category || enlargedPropertyPhoto.detectedCategory || enlargedPropertyPhoto.detected_category) === 'indoor'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-amber-500 text-white'
                            }`}>
                                {(enlargedPropertyPhoto.category || enlargedPropertyPhoto.detectedCategory || enlargedPropertyPhoto.detected_category) === 'indoor' ? 'Indoor - Staging' : 'Outdoor - Sky Enhancement'}
                            </span>
                        </div>

                        {/* Main image */}
                        <div
                            className="max-w-[90vw] max-h-[80vh] relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={enlargedPropertyPhoto.processedUrl || enlargedPropertyPhoto.url || enlargedPropertyPhoto.originalUrl || enlargedPropertyPhoto.previewUrl}
                                alt={enlargedPropertyPhoto.roomType || enlargedPropertyPhoto.room_type || 'Property photo'}
                                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                            />
                        </div>

                        {/* Room type caption */}
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
                            <p className="text-white text-lg font-medium capitalize">
                                {(enlargedPropertyPhoto.roomType || enlargedPropertyPhoto.room_type || enlargedPropertyPhoto.detectedRoomType || enlargedPropertyPhoto.detected_room_type || 'Unknown Room Type')?.replace(/_/g, ' ')}
                            </p>
                            <p className="text-white/60 text-sm mt-1">
                                Click outside or press ESC to close
                            </p>
                        </div>
                    </motion.div>
                )}

                {/* Batch Photo Editing Modal - Full Experience */}
                {batchPhotoSession && (
                    <motion.div
                        key="batch-photo-edit-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-white dark:bg-slate-900 z-[100] flex"
                    >
                        {/* Left side - Image with Before/After - matching session UI */}
                        <div className="flex-1 flex flex-col min-h-0">
                            {/* Header - matching session UI */}
                            <div className="flex-shrink-0 px-6 py-4 bg-white/50 dark:bg-slate-800/50 border-b border-slate-200/50 dark:border-slate-700/50">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={handleCloseBatchPhotoEdit}
                                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                        >
                                            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                        </button>
                                        <div>
                                            <h3 className="font-semibold text-slate-800 dark:text-white">
                                                {(batchPhotoSession.roomType || 'Room').replace(/_/g, ' ')}
                                            </h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                {batchPhotoSession.category === 'indoor' ? 'Indoor Staging' : 'Outdoor Enhancement'} · v{batchPhotoVersions.length}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setShowBatchVersionHistory(!showBatchVersionHistory)}
                                            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                        >
                                            <History className="w-4 h-4" />
                                            Versions
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleTogglePhotoForVideo(batchPhotoSession.photo);
                                                handleCloseBatchPhotoEdit();
                                                handleOpenBatchVideoModal();
                                            }}
                                            className="flex items-center gap-2 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
                                        >
                                            <Video className="w-4 h-4" />
                                            <span>Generate Video</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                const imageUrl = batchPhotoMessages.filter(m => m.imageUrl || m.image_url).pop()?.imageUrl || batchPhotoMessages.filter(m => m.imageUrl || m.image_url).pop()?.image_url || batchPhotoSession.processedUrl;
                                                if (imageUrl) {
                                                    downloadImage(imageUrl, `staged-photo-v${batchPhotoVersions.length}.jpg`);
                                                }
                                            }}
                                            className="flex items-center gap-2 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                                        >
                                            <Download className="w-4 h-4" />
                                            <span>Download</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Stats row - matching session UI */}
                                <div className="mt-4 grid gap-3 sm:grid-cols-4">
                                    <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Current Version</p>
                                        <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">v{batchPhotoVersions.length}</p>
                                    </div>
                                    <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Category</p>
                                        <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200 capitalize">{batchPhotoSession.category || 'Indoor'}</p>
                                    </div>
                                    <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Messages</p>
                                        <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">{batchPhotoMessages.length}</p>
                                    </div>
                                    <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Status</p>
                                        <p className="mt-1 text-sm font-medium text-slate-700 dark:text-slate-200">Active session</p>
                                    </div>
                                </div>
                            </div>

                            {/* Before/After Slider - Same as single session */}
                            <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gradient-to-br from-slate-100 via-white to-emerald-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 overflow-auto">
                                <div className="relative max-w-5xl w-full">
                                    {/* Check if we have the original URL for before/after comparison */}
                                    {(() => {
                                        const originalUrl = batchPhotoSession.originalUrl || batchPhotoSession.photo?.inputUrl || batchPhotoSession.photo?.input_url || batchPhotoSession.photo?.originalUrl || batchPhotoSession.photo?.original_url || batchPhotoSession.photo?.sourceUrl || batchPhotoSession.photo?.source_url;
                                        const afterUrl = batchPhotoMessages.filter(m => m.imageUrl || m.image_url).pop()?.imageUrl || batchPhotoMessages.filter(m => m.imageUrl || m.image_url).pop()?.image_url || batchPhotoSession.processedUrl;

                                        if (originalUrl) {
                                            return (
                                                <BeforeAfterSlider
                                                    beforeUrl={originalUrl}
                                                    afterUrl={afterUrl}
                                                    className="rounded-2xl shadow-2xl"
                                                />
                                            );
                                        } else {
                                            // Original not available - show just the processed image with a note
                                            return (
                                                <div className="relative">
                                                    <img
                                                        src={afterUrl}
                                                        alt="Staged"
                                                        className="w-full rounded-2xl shadow-2xl"
                                                    />
                                                    <div className="absolute bottom-4 left-4 right-4 bg-amber-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm">
                                                        <strong>Note:</strong> Original photo not available for comparison. This feature requires a backend update.
                                                    </div>
                                                </div>
                                            );
                                        }
                                    })()}
                                    {/* Version badge */}
                                    <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm font-medium">
                                        v{batchPhotoVersions.length}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right side - Chat panel - matching session UI */}
                        <div className="w-96 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/50">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                                    <MessageSquare className="w-5 h-5 text-emerald-500" />
                                    Refine Image
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Chat to make changes to this image</p>
                            </div>

                            {/* Messages - Using ChatMessage component like session UI */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 dark:bg-slate-900/50">
                                <AnimatePresence>
                                    {batchPhotoMessages.map((msg, idx) => (
                                        <ChatMessage
                                            key={msg.message_id || idx}
                                            message={{
                                                ...msg,
                                                image_url: msg.imageUrl || msg.image_url,
                                                version: msg.version || idx + 1
                                            }}
                                            onBranchFrom={null}
                                            originalPhotoUrl={batchPhotoSession?.originalUrl}
                                            onGenerateReel={(imageUrl, version) => {
                                                handleTogglePhotoForVideo(batchPhotoSession.photo);
                                                handleCloseBatchPhotoEdit();
                                                handleOpenBatchVideoModal();
                                            }}
                                            selectedResolution={selectedResolution}
                                        />
                                    ))}
                                </AnimatePresence>
                                {isGeneratingBatchRefinement && (
                                    <ThinkingAnimation message="Generating your refined image — hang tight." />
                                )}
                            </div>

                            {/* Quick directions - matching session UI */}
                            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-2">Quick directions</p>
                                <div className="flex flex-wrap gap-2">
                                    {QUICK_REFINEMENTS.map(prompt => (
                                        <button
                                            key={prompt}
                                            onClick={() => handleBatchPhotoRefinement(prompt)}
                                            disabled={isGeneratingBatchRefinement}
                                            className="rounded-full bg-slate-100 dark:bg-slate-700 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 transition-all hover:text-emerald-600 dark:hover:text-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {prompt}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Input - matching session UI */}
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                                <div className="flex gap-3">
                                    <input
                                        type="text"
                                        value={batchPhotoInput}
                                        onChange={(e) => setBatchPhotoInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleBatchPhotoRefinement()}
                                        placeholder="Describe changes... (e.g., 'Add more plants', 'Make it brighter')"
                                        maxLength={500}
                                        className="flex-1 px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                                        disabled={isGeneratingBatchRefinement}
                                    />
                                    <button
                                        onClick={handleBatchPhotoRefinement}
                                        disabled={!batchPhotoInput.trim() || isGeneratingBatchRefinement}
                                        className="px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {isGeneratingBatchRefinement ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <Send className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                                <p className="mt-2 text-xs text-slate-400">
                                    Give one clear creative direction at a time for better versions.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}

            </AnimatePresence>

            {/* Download Progress Overlay */}
            <AnimatePresence>
                {isDownloadingZip && (
                    <motion.div
                        key="download-progress-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-6"
                        >
                            <div className="flex flex-col items-center text-center">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                                    <FileArchive className="w-8 h-8 text-emerald-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                                    Creating ZIP File
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                    {downloadMessage}
                                </p>
                                {/* Progress bar */}
                                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mb-2">
                                    <div
                                        className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full transition-all duration-300"
                                        style={{ width: `${downloadProgress}%` }}
                                    />
                                </div>
                                <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                                    {downloadProgress}% complete
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {/* Video Variations Modal */}
                {showVariationModal && selectedVideoForVariation && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                        onClick={() => !isCreatingVariations && setShowVariationModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                            <Layers className="w-5 h-5 text-purple-500" />
                                            Create Video Variations
                                        </h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                            Select variations to generate from your base video
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowVariationModal(false)}
                                        disabled={isCreatingVariations}
                                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Base video info */}
                                <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl flex items-center gap-3">
                                    {(selectedVideoForVariation.thumbnailUrl || selectedVideoForVariation.thumbnail_url) ? (
                                        <img
                                            src={selectedVideoForVariation.thumbnailUrl || selectedVideoForVariation.thumbnail_url}
                                            alt="Base video"
                                            className="w-16 h-10 object-cover rounded-lg"
                                        />
                                    ) : (
                                        <div className="w-16 h-10 bg-slate-200 dark:bg-slate-600 rounded-lg flex items-center justify-center">
                                            <Video className="w-5 h-5 text-slate-400" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                                            {selectedVideoForVariation.name || 'Base Video'}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {selectedVideoForVariation.duration || 'Video'} • AI Generated
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Name Prefix Input */}
                            <div className="px-6 pt-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Name Prefix (optional)
                                </label>
                                <input
                                    type="text"
                                    value={variationNamePrefix}
                                    onChange={(e) => setVariationNamePrefix(e.target.value)}
                                    placeholder="e.g., 123 Main Street"
                                    className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    disabled={isCreatingVariations}
                                />
                            </div>

                            {/* Variation Presets Grid */}
                            <div className="p-6 max-h-[40vh] overflow-y-auto">
                                {variationPresets.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-3" />
                                        <p className="text-sm text-slate-500">Loading variation presets...</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Group presets by category */}
                                        {['standard', 'vertical', 'luxury', 'marketing'].map(group => {
                                            const groupPresets = variationPresets.filter(p => p.group === group);
                                            if (groupPresets.length === 0) return null;

                                            return (
                                                <div key={group} className="mb-6 last:mb-0">
                                                    <h4 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">
                                                        {group}
                                                    </h4>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                        {groupPresets.map(preset => {
                                                            const isSelected = selectedVariations.includes(preset.key);
                                                            return (
                                                                <button
                                                                    key={preset.key}
                                                                    onClick={() => handleToggleVariation(preset.key)}
                                                                    disabled={isCreatingVariations}
                                                                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                                                                        isSelected
                                                                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                                                                            : 'border-slate-200 dark:border-slate-600 hover:border-purple-300 dark:hover:border-purple-700'
                                                                    } disabled:opacity-50`}
                                                                >
                                                                    <div className="flex items-start justify-between">
                                                                        <span className={`text-sm font-medium ${
                                                                            isSelected
                                                                                ? 'text-purple-700 dark:text-purple-300'
                                                                                : 'text-slate-700 dark:text-slate-200'
                                                                        }`}>
                                                                            {preset.label}
                                                                        </span>
                                                                        {isSelected && (
                                                                            <Check className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                                                        )}
                                                                    </div>
                                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                                                                        {preset.description}
                                                                    </p>
                                                                    {preset.aspectRatio && (
                                                                        <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">
                                                                            {preset.aspectRatio}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>

                            {/* Error Message */}
                            {variationError && (
                                <div className="mx-6 mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-600 dark:text-red-400">{variationError}</p>
                                </div>
                            )}

                            {/* Footer */}
                            <div className="p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-slate-600 dark:text-slate-400">
                                        {selectedVariations.length > 0 ? (
                                            <span>
                                                <span className="font-medium text-purple-600 dark:text-purple-400">{selectedVariations.length}</span>
                                                {' '}variation{selectedVariations.length !== 1 ? 's' : ''} selected
                                                {selectedVariations.length > 1 && (
                                                    <span className="text-slate-400 dark:text-slate-500"> • Will download as ZIP</span>
                                                )}
                                            </span>
                                        ) : (
                                            <span>Select at least one variation</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => setShowVariationModal(false)}
                                            disabled={isCreatingVariations}
                                            className="px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleCreateVariations}
                                            disabled={selectedVariations.length === 0 || isCreatingVariations}
                                            className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            {isCreatingVariations ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Creating...
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles className="w-4 h-4" />
                                                    Create & Download
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {/* Gallery Editor Modal */}
                {showGalleryEditor && selectedProperty && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                        onClick={() => setShowGalleryEditor(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="w-full max-w-6xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-violet-500/10 to-purple-500/10">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center">
                                        <Globe className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Publish Property Gallery</h2>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Create a shareable landing page for your listing</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowGalleryEditor(false)}
                                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>

                            {/* Progress Steps - Simplified: Property → Preview (Template & Branding from Account Settings) */}
                            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center justify-center gap-2">
                                    {[
                                        { step: 1, label: 'Property', icon: Home },
                                        { step: 2, label: 'Preview', icon: Eye }
                                    ].map(({ step, label, icon: Icon }, idx) => (
                                        <React.Fragment key={step}>
                                            <button
                                                onClick={() => setGalleryEditorStep(step)}
                                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                                                    galleryEditorStep === step
                                                        ? 'bg-violet-500 text-white'
                                                        : galleryEditorStep > step
                                                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                                                        : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                                }`}
                                            >
                                                {galleryEditorStep > step ? (
                                                    <Check className="w-4 h-4" />
                                                ) : (
                                                    <Icon className="w-4 h-4" />
                                                )}
                                                <span className="hidden sm:inline">{label}</span>
                                            </button>
                                            {idx < 1 && (
                                                <ChevronRight className={`w-4 h-4 ${galleryEditorStep > step ? 'text-emerald-500' : 'text-slate-300 dark:text-slate-600'}`} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {/* Step 1: Property Details (pre-populated from database) */}
                                {galleryEditorStep === 1 && (
                                    <div className="max-w-2xl mx-auto">
                                        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">Property Information</h3>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Property Name / Title</label>
                                                <input
                                                    type="text"
                                                    value={gallerySettings.propertyName}
                                                    onChange={e => setGallerySettings(prev => ({ ...prev, propertyName: e.target.value }))}
                                                    placeholder="e.g., Stunning Modern Estate"
                                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Address</label>
                                                <input
                                                    type="text"
                                                    value={gallerySettings.propertyAddress}
                                                    onChange={e => setGallerySettings(prev => ({ ...prev, propertyAddress: e.target.value }))}
                                                    placeholder="123 Main Street, Austin, TX 78701"
                                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Listing Price</label>
                                                    <div className="relative">
                                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                                                        <input
                                                            type="text"
                                                            value={gallerySettings.listingPrice}
                                                            onChange={e => {
                                                                const raw = e.target.value.replace(/[^\d]/g, '');
                                                                const formatted = raw ? Number(raw).toLocaleString() : '';
                                                                setGallerySettings(prev => ({ ...prev, listingPrice: formatted }));
                                                            }}
                                                            placeholder="1,250,000"
                                                            className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Square Feet</label>
                                                    <input
                                                        type="text"
                                                        value={gallerySettings.squareFeet}
                                                        onChange={e => {
                                                            const raw = e.target.value.replace(/[^\d]/g, '');
                                                            const formatted = raw ? Number(raw).toLocaleString() : '';
                                                            setGallerySettings(prev => ({ ...prev, squareFeet: formatted }));
                                                        }}
                                                        placeholder="3,500"
                                                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Bedrooms</label>
                                                    <input
                                                        type="text"
                                                        value={gallerySettings.bedrooms}
                                                        onChange={e => setGallerySettings(prev => ({ ...prev, bedrooms: e.target.value }))}
                                                        placeholder="4"
                                                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Bathrooms</label>
                                                    <input
                                                        type="text"
                                                        value={gallerySettings.bathrooms}
                                                        onChange={e => setGallerySettings(prev => ({ ...prev, bathrooms: e.target.value }))}
                                                        placeholder="3.5"
                                                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
                                                <textarea
                                                    value={gallerySettings.propertyDescription}
                                                    onChange={e => setGallerySettings(prev => ({ ...prev, propertyDescription: e.target.value }))}
                                                    placeholder="Describe the property's key features and highlights..."
                                                    rows={4}
                                                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                                                />
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={gallerySettings.showPrice}
                                                        onChange={e => setGallerySettings(prev => ({ ...prev, showPrice: e.target.checked }))}
                                                        className="w-4 h-4 rounded border-slate-300 text-violet-500 focus:ring-violet-500"
                                                    />
                                                    <span className="text-sm text-slate-600 dark:text-slate-400">Show listing price</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Preview */}
                                {galleryEditorStep === 2 && (() => {
                                    const previewTheme = getPropertyGalleryTemplate(gallerySettings.template);
                                    const previewIsDark = previewTheme.isDark;

                                    return (
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Preview Your Gallery</h3>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-slate-500">Template:</span>
                                                <span className="text-sm font-medium text-violet-600 dark:text-violet-400 capitalize">
                                                    {gallerySettings.template.replace('-', ' ')}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Preview Frame */}
                                        <div className={`rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden ${previewTheme.bg}`}>
                                            {/* Header Preview */}
                                            <div className={`p-6 border-b ${
                                                previewIsDark
                                                    ? 'border-slate-700'
                                                    : 'border-slate-200'
                                            }`}>
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h1 className={`text-2xl font-bold ${
                                                            previewIsDark
                                                                ? 'text-white'
                                                                : 'text-slate-900'
                                                        }`}>
                                                            {gallerySettings.propertyName || 'Property Name'}
                                                        </h1>
                                                        {gallerySettings.propertyAddress && (
                                                            <p className={`mt-1 flex items-center gap-1 ${
                                                                previewIsDark
                                                                    ? 'text-slate-400'
                                                                    : 'text-slate-500'
                                                            }`}>
                                                                <MapPin className="w-4 h-4" />
                                                                {gallerySettings.propertyAddress}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {gallerySettings.showPrice && gallerySettings.listingPrice && (
                                                        <div
                                                            className="text-2xl font-bold"
                                                            style={{ color: gallerySettings.accentColor }}
                                                        >
                                                            ${gallerySettings.listingPrice}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Stats */}
                                                {(gallerySettings.bedrooms || gallerySettings.bathrooms || gallerySettings.squareFeet) && (
                                                    <div className="flex items-center gap-6 mt-4">
                                                        {gallerySettings.bedrooms && (
                                                            <div className={`flex items-center gap-2 ${
                                                                previewIsDark
                                                                    ? 'text-slate-300'
                                                                    : 'text-slate-600'
                                                            }`}>
                                                                <Bed className="w-5 h-5" />
                                                                <span>{gallerySettings.bedrooms} Beds</span>
                                                            </div>
                                                        )}
                                                        {gallerySettings.bathrooms && (
                                                            <div className={`flex items-center gap-2 ${
                                                                previewIsDark
                                                                    ? 'text-slate-300'
                                                                    : 'text-slate-600'
                                                            }`}>
                                                                <Bath className="w-5 h-5" />
                                                                <span>{gallerySettings.bathrooms} Baths</span>
                                                            </div>
                                                        )}
                                                        {gallerySettings.squareFeet && (
                                                            <div className={`flex items-center gap-2 ${
                                                                previewIsDark
                                                                    ? 'text-slate-300'
                                                                    : 'text-slate-600'
                                                            }`}>
                                                                <Home className="w-5 h-5" />
                                                                <span>{gallerySettings.squareFeet} sqft</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {/* Property Description */}
                                                {gallerySettings.propertyDescription && (
                                                    <p className={`mt-4 text-sm leading-relaxed ${
                                                        previewIsDark
                                                            ? 'text-slate-400'
                                                            : 'text-slate-600'
                                                    }`}>
                                                        {gallerySettings.propertyDescription}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Photos Grid Preview */}
                                            <div className="p-6">
                                                <div className="grid grid-cols-3 gap-3">
                                                    {selectedProperty.photos?.slice(0, 6).map((photo, idx) => {
                                                        const photoUrl = photo.processedUrl || photo.processed_url || photo.stagedUrl || photo.staged_url || photo.originalUrl || photo.original_url;
                                                        return (
                                                            <div key={idx} className="aspect-[4/3] rounded-lg overflow-hidden bg-slate-200">
                                                                <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {selectedProperty.photos?.length > 6 && (
                                                    <p className={`text-center mt-4 text-sm ${
                                                        previewIsDark
                                                            ? 'text-slate-400'
                                                            : 'text-slate-500'
                                                    }`}>
                                                        +{selectedProperty.photos.length - 6} more photos
                                                    </p>
                                                )}
                                            </div>

                                            {/* Agent Card Preview */}
                                            {(gallerySettings.agentName || gallerySettings.brokerageName) && (() => {
                                                // Decode agent photo URL (may be over-encoded S3 URL)
                                                const decodedAgentPhoto = decodeS3Url(gallerySettings.agentPhoto);
                                                const decodedBrokerageLogo = decodeS3Url(gallerySettings.brokerageLogo);

                                                return (
                                                <div className={`p-6 border-t ${
                                                    previewIsDark
                                                        ? 'border-slate-700 bg-slate-800/50'
                                                        : 'border-slate-200 bg-slate-50'
                                                }`}>
                                                    <div className="flex items-center gap-4">
                                                        {decodedAgentPhoto ? (
                                                            <img
                                                                src={decodedAgentPhoto}
                                                                alt=""
                                                                className="w-16 h-16 rounded-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling?.classList.remove('hidden');
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div className={`w-16 h-16 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center ${decodedAgentPhoto ? 'hidden' : ''}`}>
                                                            <User className="w-8 h-8 text-slate-400" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className={`font-semibold ${
                                                                previewIsDark
                                                                    ? 'text-white'
                                                                    : 'text-slate-900'
                                                            }`}>
                                                                {gallerySettings.agentName || 'Agent Name'}
                                                            </p>
                                                            <p className={`text-sm ${
                                                                previewIsDark
                                                                    ? 'text-slate-400'
                                                                    : 'text-slate-500'
                                                            }`}>
                                                                {gallerySettings.agentTitle}
                                                            </p>
                                                            {gallerySettings.brokerageName && (
                                                                <p className="text-sm" style={{ color: gallerySettings.accentColor }}>
                                                                    {gallerySettings.brokerageName}
                                                                </p>
                                                            )}
                                                        </div>
                                                        {decodedBrokerageLogo && (
                                                            <img
                                                                src={decodedBrokerageLogo}
                                                                alt=""
                                                                className="h-12 object-contain"
                                                                onError={(e) => e.target.style.display = 'none'}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                );
                                            })()}
                                        </div>

                                        {/* Published URL (if already published) */}
                                        {publishedGalleryUrl && (
                                            <div className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Gallery Published!</p>
                                                        <p className="text-sm text-emerald-600 dark:text-emerald-500">{publishedGalleryUrl}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => navigator.clipboard.writeText(publishedGalleryUrl)}
                                                            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-800 rounded-lg transition-colors"
                                                        >
                                                            <Copy className="w-4 h-4 text-emerald-600" />
                                                        </button>
                                                        <button
                                                            onClick={() => window.open(publishedGalleryUrl, '_blank')}
                                                            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-800 rounded-lg transition-colors"
                                                        >
                                                            <ExternalLink className="w-4 h-4 text-emerald-600" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })()}
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                                <button
                                    onClick={() => {
                                        if (galleryEditorStep > 1) {
                                            setGalleryEditorStep(galleryEditorStep - 1);
                                        } else {
                                            setShowGalleryEditor(false);
                                        }
                                    }}
                                    className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    {galleryEditorStep === 1 ? 'Cancel' : 'Back'}
                                </button>
                                <div className="flex items-center gap-3">
                                    {galleryEditorStep < 2 ? (
                                        <button
                                            onClick={() => setGalleryEditorStep(galleryEditorStep + 1)}
                                            className="flex items-center gap-2 px-6 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-xl font-medium transition-colors"
                                        >
                                            Next
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={async () => {
                                                setIsPublishingGallery(true);
                                                try {
                                                    const batchId = selectedProperty.batchId;
                                                    const slug = gallerySettings.customSlug || batchId;
                                                    const baseUrl = window.location.origin;

                                                    // Save property info to backend API
                                                    const propertyData = {
                                                        propertyName: gallerySettings.propertyName,
                                                        propertyAddress: gallerySettings.propertyAddress,
                                                        price: gallerySettings.listingPrice || null,
                                                        beds: gallerySettings.bedrooms ? parseInt(gallerySettings.bedrooms) : null,
                                                        baths: gallerySettings.bathrooms ? parseFloat(gallerySettings.bathrooms) : null,
                                                        sqft: gallerySettings.squareFeet ? parseInt(String(gallerySettings.squareFeet).replace(/[^0-9]/g, '')) : null,
                                                        description: gallerySettings.propertyDescription || null,
                                                    };
                                                    console.log('[Publish Gallery] Saving property info to backend:', propertyData);
                                                    console.log('[Publish Gallery] Gallery settings state:', {
                                                        listingPrice: gallerySettings.listingPrice,
                                                        bedrooms: gallerySettings.bedrooms,
                                                        bathrooms: gallerySettings.bathrooms,
                                                        squareFeet: gallerySettings.squareFeet,
                                                        propertyDescription: gallerySettings.propertyDescription
                                                    });
                                                    await updateProperty(batchId, propertyData);
                                                    console.log('[Publish Gallery] Property info saved to backend successfully');

                                                    // Also save gallery data to localStorage for immediate local access
                                                    const galleryData = {
                                                        id: slug,
                                                        template: gallerySettings.template,
                                                        property: {
                                                            name: gallerySettings.propertyName,
                                                            address: gallerySettings.propertyAddress,
                                                            price: gallerySettings.listingPrice ? parseFloat(gallerySettings.listingPrice.replace(/[^0-9.]/g, '')) : null,
                                                            bedrooms: gallerySettings.bedrooms,
                                                            bathrooms: gallerySettings.bathrooms,
                                                            squareFeet: gallerySettings.squareFeet,
                                                            description: gallerySettings.propertyDescription,
                                                            features: gallerySettings.features || []
                                                        },
                                                        agent: {
                                                            name: gallerySettings.agentName,
                                                            title: gallerySettings.agentTitle,
                                                            phone: gallerySettings.agentPhone,
                                                            email: gallerySettings.agentEmail,
                                                            photo: gallerySettings.agentPhoto,
                                                            brokerage: gallerySettings.brokerageName,
                                                            brokerageLogo: gallerySettings.brokerageLogo
                                                        },
                                                        settings: {
                                                            accentColor: gallerySettings.accentColor,
                                                            showPrice: gallerySettings.showPrice,
                                                            showContactForm: gallerySettings.showContactForm,
                                                            showDownloadButton: gallerySettings.showDownloadButton
                                                        },
                                                        photos: selectedProperty.photos?.filter(p => p.processedUrl).map((photo, idx) => ({
                                                            id: idx + 1,
                                                            original: photo.originalUrl,
                                                            processed: photo.processedUrl,
                                                            room: photo.roomType || 'Room',
                                                            style: photo.style || 'Modern'
                                                        })) || []
                                                    };

                                                    // Store in localStorage
                                                    const galleries = JSON.parse(localStorage.getItem('publishedGalleries') || '{}');
                                                    galleries[slug] = galleryData;
                                                    localStorage.setItem('publishedGalleries', JSON.stringify(galleries));

                                                    setPublishedGalleryUrl(`${baseUrl}/flow/${slug}`);
                                                } catch (error) {
                                                    console.error('[Publish Gallery] Failed to save property info:', error);
                                                    // Show error to user - don't silently fail
                                                    alert(`Failed to save property info: ${error.message}`);
                                                    // Still set the URL so they can see the gallery, but warn them
                                                    const slug = gallerySettings.customSlug || selectedProperty.batchId;
                                                    const baseUrl = window.location.origin;
                                                    setPublishedGalleryUrl(`${baseUrl}/flow/${slug}`);
                                                } finally {
                                                    setIsPublishingGallery(false);
                                                }
                                            }}
                                            disabled={isPublishingGallery}
                                            className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 disabled:opacity-50 text-white rounded-xl font-medium transition-colors"
                                        >
                                            {isPublishingGallery ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Publishing...
                                                </>
                                            ) : publishedGalleryUrl ? (
                                                <>
                                                    <Check className="w-4 h-4" />
                                                    Update Gallery
                                                </>
                                            ) : (
                                                <>
                                                    <Globe className="w-4 h-4" />
                                                    Publish Gallery
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                <PropertyFlyerBuilderModal
                    isOpen={showFlyerBuilder && !!selectedProperty}
                    onClose={() => setShowFlyerBuilder(false)}
                    property={selectedProperty}
                    initialBranding={{
                        listingPrice: gallerySettings.listingPrice,
                        bedrooms: gallerySettings.bedrooms,
                        bathrooms: gallerySettings.bathrooms,
                        squareFeet: gallerySettings.squareFeet,
                        propertyDescription: gallerySettings.propertyDescription,
                        agentName: gallerySettings.agentName,
                        agentTitle: gallerySettings.agentTitle,
                        agentPhone: gallerySettings.agentPhone,
                        agentEmail: gallerySettings.agentEmail,
                        agentPhoto: gallerySettings.agentPhoto,
                        brokerageName: gallerySettings.brokerageName,
                        brokerageLogo: gallerySettings.brokerageLogo,
                        accentColor: gallerySettings.accentColor
                    }}
                />

                <PropertyPostcardBuilderModal
                    isOpen={showPostcardBuilder && !!selectedProperty}
                    onClose={() => setShowPostcardBuilder(false)}
                    property={selectedProperty}
                    initialBranding={{
                        listingPrice: gallerySettings.listingPrice,
                        bedrooms: gallerySettings.bedrooms,
                        bathrooms: gallerySettings.bathrooms,
                        squareFeet: gallerySettings.squareFeet,
                        propertyDescription: gallerySettings.propertyDescription,
                        agentName: gallerySettings.agentName,
                        agentTitle: gallerySettings.agentTitle,
                        agentPhone: gallerySettings.agentPhone,
                        agentEmail: gallerySettings.agentEmail,
                        agentPhoto: gallerySettings.agentPhoto,
                        brokerageName: gallerySettings.brokerageName,
                        accentColor: gallerySettings.accentColor
                    }}
                    onSendToMailingList={(postcardDesign) => {
                        // Store the postcard design and open mailing list picker
                        setPendingPostcardDesign(postcardDesign);
                        setShowMailingListPicker(true);
                    }}
                />

                {/* Social Post Modal - Post staged photos to social media */}
                <SocialPostModal
                    isOpen={showSocialPost && !!selectedProperty}
                    onClose={() => setShowSocialPost(false)}
                    batchId={selectedProperty?.batch_id || selectedProperty?.batchId}
                    propertyName={selectedProperty?.propertyName || selectedProperty?.property_name || selectedProperty?.name || ''}
                    propertyAddress={selectedProperty?.propertyAddress || selectedProperty?.property_address || selectedProperty?.address || ''}
                    onSuccess={() => {
                        setShowSocialPost(false);
                        // Show success notification
                        console.log('[Social Post] Posted successfully');
                    }}
                />

                {/* Mailing List Picker - Select where to send postcards */}
                <MailingListPickerModal
                    isOpen={showMailingListPicker}
                    onClose={() => {
                        setShowMailingListPicker(false);
                        setPendingPostcardDesign(null);
                    }}
                    onSelect={(list) => {
                        // Store selected list and open campaign modal
                        setSelectedMailingList(list);
                        setShowMailingListPicker(false);
                        setShowPostcardBuilder(false);
                        setShowCampaignModal(true);
                    }}
                />

                {/* Campaign Modal - Configure and send to contacts */}
                {showCampaignModal && selectedMailingList && (
                    <CreateCampaignModal
                        listId={selectedMailingList.id || selectedMailingList.list_id}
                        list={selectedMailingList}
                        postcardDesign={pendingPostcardDesign}
                        onClose={() => {
                            setShowCampaignModal(false);
                            setSelectedMailingList(null);
                            setPendingPostcardDesign(null);
                        }}
                        onSent={() => {
                            setShowCampaignModal(false);
                            setSelectedMailingList(null);
                            setPendingPostcardDesign(null);
                            // TODO: Show success toast
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

VirtualStaging.propTypes = {
    subscription: PropTypes.object,
    userEmail: PropTypes.string,
    userMeta: PropTypes.object,
    onUpgradePlan: PropTypes.func,
    onRefreshCredits: PropTypes.func
};

export default VirtualStaging;
